import React, { DragEvent, ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { protectedResources } from "../../../../authConfig";
import useAuthToken from "../../../../hooks/useAuthToken";
import { createApiClient } from "../../../../utils/apiClient";
import { getPdfServerUrl } from "../../../../utils/pdfServer";
import { useUser } from "../../../../utils/userContext";
import { useTier } from "../../../../utils/tierContext";
import { isAdmin } from "../../../../utils/access";
import { isReportAccessDenied, reportFailureMessage } from "../../reportAccess";
import Toast from "../../../../components/Toast";
import ReportDefinitionBrowser, { type DefinitionInspection } from "./ReportDefinitionBrowser";
import type { FormattedInvoicePayload } from "../../../formattedInvoice/formattedInvoiceData";
import ReportsV3Preview from "../../ReportsV3Preview";
import NativeReportImage from "../../NativeReportImage";
import { INVOICE_FIELDS, INVOICE_SOURCES } from "../../nativeBusinessExecution";
import { buildRenderContext, type RenderFilters } from "../../renderOperation";
import NativeBusinessDataControls, { NativeInvoiceSourceEditor } from "./NativeBusinessDataControls";
import { isNativeReportsV31Payload, nativeWorkbookSupport, type NativeResolvedBand, type NativeResolvedElement } from "../../nativeLayout.mjs";
import { resolveDesignerLayout as resolveNativeLayoutPlan, inheritInternalSession, internalSamplePayload, type InternalDesignerSession } from "../internalConditionalSession";
import { createInternalFormulaSession, createInternalTemplateSession, formulaColumn, formulaEditingContext, formulaTextTarget, isFormulaTarget, isInternalFormulaSession } from "../internalFormulaSession";
import ReportTemplateGallery from "../../templates/ReportTemplateGallery";
import type { TemplateInstance } from "../../templates/templateCatalog";
import InternalTypedPreview from "./InternalTypedPreview";
import InternalParameterPanel from "./InternalParameterPanel";
import InternalFilterPanel from "./InternalFilterPanel";
import { reportParameters } from "../parameterAuthoring";
import FormulaBuilder from "./FormulaBuilder";
import InternalFormulaTools, { InternalFormulaValueControls } from "./InternalFormulaTools";
import type { ConditionalRuleAction } from "../conditionalAuthoring";
import ConditionalRulesInspector from "./ConditionalRulesInspector";
import InternalConditionalTools from "./InternalConditionalTools";
import InternalConditionalPreview from "./InternalConditionalPreview";
import { exportNativeExcel } from "../../nativeExcelExport";
import { assertReportAdmission, NativeConditionError } from "../../nativeConditions.mjs";
import * as commands from "../designerCommands";
import { columnWidthToCss } from "../documentGeometry";
import { designerDocumentToLegacyPayload, legacyPayloadToDesignerDocument, parseDesignerDocumentJson, prettyJson } from "../legacySectionAdapter";
import {
  asRecord,
  asArray,
  DesignerBand,
  DesignerColumn,
  DesignerComponent,
  DesignerDocument,
  DesignerField,
  DesignerHistoryState,
  DesignerPageOrientation,
  DesignerPageSize,
  DesignerSelection,
  findBand,
  JsonRecord,
  primaryComponent,
} from "../reportDesignerModel";
import {
  definitionDisplayName,
  definitionValidationRequest,
  readDefinitionValidationResponse,
  DEFINITION_MANAGEMENT_OPTIONS,
  detailToLegacyPayload,
  isReportDefinitionConflict,
  legacyPayloadToUpsertRequest,
  ReportDefinitionDetailV3,
  ReportDefinitionSummaryV3,
} from "../reportDefinitionPersistence";
import { DESIGNER_SAMPLES, clonePayload } from "../samplePayloads";
import {
  DesignerValidationMessage,
  validateDesignerPayload,
} from "../validation";
import styles from "./ReportDesignerPage.module.css";

type DesignerMode = "design" | "preview" | "json" | "validate";
type NativeDataMode = "sample" | "business";
type ExplorerTab = "structure" | "data" | "toolbox";
type DefinitionSource = "local" | "backend";
type SaveStatus = "idle" | "loading" | "saving" | "saved" | "error";
type CanvasValueMode = "sample" | "binding" | "both";
type NativeBandRole = "reportHeader" | "pageHeader" | "detail" | "reportFooter" | "pageFooter";
type PanelSide = "left" | "right";
type FitMode = "width" | "page";
type ZoomPreference = "fit-width" | "fit-page" | "manual";
type DesignSampleFixtureId = "standard" | "edge" | "empty" | "stress";
type DesignSampleOption = { id: DesignSampleFixtureId; label: string; description: string; compatible: boolean };
type AlignmentGuide = { orientation: "vertical" | "horizontal"; position: number; start: number; end: number };
type DatasetKind = "collection" | "single" | "unknown";
type DatasetDescriptor = {
  id: string;
  label: string;
  kind: DatasetKind;
  recordCount: number | null;
  embeddedRecordCount: number | null;
  generatedRecordCount: number | null;
  rowContainerKey?: string;
  hasSampleData: boolean;
  noSampleData: boolean;
  fields: FieldDescriptor[];
  metadata?: JsonRecord;
};
type FieldDescriptor = {
  path: string;
  label: string;
  type: string;
  sampleAvailable: boolean;
  metadata?: JsonRecord;
};
type NativeInteraction =
  | { kind: "drag"; bandId: string; elementIds: string[]; startX: number; startY: number; dx: number; dy: number; cancelled?: boolean }
  | { kind: "resize"; bandId: string; elementId: string; elementIds: string[]; handle: string; startX: number; startY: number; dx: number; dy: number; startBounds: { x: number; y: number; width: number; height: number }; startSourceBounds: { x: number; y: number; width: number; height: number }; draftElement: HTMLElement; overlayElement: HTMLDivElement; cancelled?: boolean }
  | { kind: "bandResize"; bandId: string; startX: number; startY: number; dx: number; dy: number; startHeight: number; overlayElement: HTMLDivElement; cancelled?: boolean };
type PendingInteractionDelta = { dx: number; dy: number; eventTime: number; pointerEventTime: number | null };
type ResizeHandoff = {
  bandId: string;
  elementId: string;
  bounds: Partial<{ x: number; y: number; width: number; height: number }>;
  draftElement: HTMLElement;
  overlayElement: HTMLDivElement;
};
type NativeResizeDiagnostics = {
  active: boolean;
  kind: NativeInteraction["kind"] | null;
  resizeRenderMode: "overlay";
  startedAt: number;
  endedAt: number;
  zoom: number;
  snapEnabled: boolean;
  nativeDesignCanvasRenderCount: number;
  renderCountDuringInteraction: number;
  renderDuringActiveResize: boolean;
  layoutPlanInvocationCount: number;
  pointerMoveCount: number;
  animationFramesRequested: number;
  animationFramesExecuted: number;
  draftGeometryUpdateCount: number;
  documentCommandCount: number;
  pointerLatencyCount: number;
  pointerLatencyTotalMs: number;
  pointerLatencyMaxMs: number;
  pointerEventLatencyCount: number;
  pointerEventLatencyTotalMs: number;
  pointerEventLatencyMaxMs: number;
  animationFrameIntervalCount: number;
  animationFrameIntervalTotalMs: number;
  animationFrameIntervalMaxMs: number;
  longFrameCount: number;
  frameWorkTotalMs: number;
  frameWorkMaxMs: number;
  snappingCalculationCount: number;
  snappingCalculationTotalMs: number;
  snappingCalculationMaxMs: number;
  styleConflictCount: number;
  latestStyleConflict: string;
  lastFrameTime: number;
  lastSummary: string;
};

const DEFAULT_PAGE = {
  pageSize: "letter" as DesignerPageSize,
  orientation: "portrait" as DesignerPageOrientation,
  zoom: 75,
};
const DESIGN_SAMPLE_RECORD_COUNTS = [1, 10, 25, 100];
const ZOOM_PRESETS = [50, 75, 100, 125, 150, 200];

async function readValidatedExportBlob(response: Response, kind: "pdf" | "excel"): Promise<Blob> {
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok) {
    throw new Error(await exportErrorMessage(response, contentType, `${kind.toUpperCase()} export failed.`));
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  const expected = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const isExpectedContentType = contentType.toLowerCase().includes(expected);
  const hasExpectedSignature = kind === "pdf"
    ? bytes.length >= 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d
    : bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
  if (!isExpectedContentType || !hasExpectedSignature) {
    const preview = decodeResponsePreview(bytes);
    const responseKind = contentType.includes("text/html") || preview.trimStart().startsWith("<!DOCTYPE html") || preview.trimStart().startsWith("<html")
      ? "HTML page"
      : contentType.includes("application/json")
        ? "JSON response"
        : contentType || "unexpected response";
    throw new Error(`Expected a ${kind.toUpperCase()} file but received ${responseKind}. Check the PDF server URL, authentication headers, and /reports/reportsv3/${kind} route. ${preview ? `Response began: ${preview.slice(0, 120)}` : ""}`.trim());
  }

  return new Blob([bytes], { type: expected });
}

async function exportErrorMessage(response: Response, contentType: string, fallback: string): Promise<string> {
  if (contentType.includes("application/json")) {
    const details = await response.json().catch(() => null);
    if (details && typeof details === "object" && "error" in details) {
      return String((details as { error?: unknown }).error || fallback);
    }
  }
  const text = await response.text().catch(() => "");
  if (contentType.includes("text/html") || text.trimStart().startsWith("<!DOCTYPE html") || text.trimStart().startsWith("<html")) {
    return `${fallback} The server returned HTML instead of an export file. Verify the PDF server URL and route configuration.`;
  }
  return text || fallback;
}

function decodeResponsePreview(bytes: Uint8Array): string {
  if (!bytes.length) return "";
  try {
    return new TextDecoder().decode(bytes.slice(0, 300)).replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

type DesignerConnection = {
  auth: ReturnType<typeof useAuthToken>;
  userContext: ReturnType<typeof useUser>;
  api: ReturnType<typeof createApiClient> | null;
  permissions: { manage: boolean; pdf: boolean; excel: boolean; context: string };
};

export default function ReportDesignerPage() {
  const auth = useAuthToken();
  const userContext = useUser();
  const { token, getToken } = auth;
  const { user, activeTenantId } = userContext;
  const { hasFeature, loading: tierLoading } = useTier();
  const permissions = {
    manage: !tierLoading && isAdmin(user?.roleId ?? 0) && hasFeature("custom_report_templates"),
    pdf: !tierLoading && isAdmin(user?.roleId ?? 0) && hasFeature("client_ready_reports"),
    excel: !tierLoading && isAdmin(user?.roleId ?? 0) && hasFeature("raw_excel_export"),
    context: JSON.stringify([user?.roleId, tierLoading, ...["invoice_generation", "custom_report_templates", "client_ready_reports", "raw_excel_export", "approval_workflow"].map(key => hasFeature(key))]),
  };
  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({ baseUrl: protectedResources.baseURL.endpoint, token, getToken, tenantId: activeTenantId, userId: user.userId });
  }, [token, getToken, activeTenantId, user?.userId]);
  const formulaSession = useMemo(() => process.env.NODE_ENV !== "production" && new URLSearchParams(window.location.search).get("internalFormulaWorkspace") === "1" ? createInternalFormulaSession(new URLSearchParams(window.location.search).get("internalParameterWorkspace") === "1") : undefined, []);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [templateInstance, setTemplateInstance] = useState<TemplateInstance | null>(null);
  const [templateToast, setTemplateToast] = useState(false);
  const galleryWasOpen = useRef(false);
  useEffect(() => {
    if (galleryOpen) galleryWasOpen.current = true;
    else if (galleryWasOpen.current) {
      galleryWasOpen.current = false;
      document.getElementById("reportDesignerTemplateBrowse")?.focus();
    }
  }, [galleryOpen]);
  const workspaceSession = useMemo(() => templateInstance ? createInternalTemplateSession(templateInstance.payload, templateInstance.provenance.templateId) : formulaSession, [templateInstance, formulaSession]);
  return <>
    {galleryOpen && <ReportTemplateGallery onClose={() => setGalleryOpen(false)} onCreate={instance => { setTemplateInstance(instance); setGalleryOpen(false); setTemplateToast(true); }} />}
    {templateToast && <Toast type="success" message="Independent template report created in the internal workspace." onClose={() => setTemplateToast(false)} />}
    <div hidden={galleryOpen}>
    {templateInstance && <p role="status">Template: {templateInstance.provenance.templateId}, revision {templateInstance.provenance.templateRevision}. Independent internal document; provenance is not persisted.</p>}
    <ReportDesignerEditor key={JSON.stringify([activeTenantId, user?.userId, templateInstance?.payload.definitionId])} connection={{ auth, userContext, api, permissions }} internalSession={workspaceSession}
      onOpenTemplateGallery={process.env.NODE_ENV !== "production" ? () => setGalleryOpen(true) : undefined}
      onOpenFormulaWorkspace={process.env.NODE_ENV !== "production" ? () => {
    const url = new URL(window.location.href);
    url.searchParams.set("internalFormulaWorkspace", "1");
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  } : undefined} />
    </div>
  </>;
}

export function ReportDesignerEditor({ connection, internalSession, onOpenFormulaWorkspace, onOpenTemplateGallery }: { connection?: DesignerConnection; internalSession?: InternalDesignerSession; onOpenFormulaWorkspace?: () => void; onOpenTemplateGallery?: () => void }) {
  const initialDocument = useMemo(() => legacyPayloadToDesignerDocument(clonePayload(internalSession?.initialPayload || DESIGNER_SAMPLES[0].payload), internalSession), [internalSession]);
  const [internalSampleIndex, setInternalSampleIndex] = useState(0);
  const [parameterInputState, setParameterInputState] = useState<{ key: string; values: Readonly<Record<string, unknown>> } | null>(null);
  const [parameterInputEpoch, setParameterInputEpoch] = useState(0);
  const [sampleId, setSampleId] = useState(DESIGNER_SAMPLES[0]?.id || "");
  const [history, setHistory] = useState<DesignerHistoryState>({ past: [], present: initialDocument, future: [], dirty: false });
  const [jsonText, setJsonText] = useState(() => prettyJson(initialDocument));
  const [jsonError, setJsonError] = useState("");
  const [jsonDraftDirty, setJsonDraftDirty] = useState(false);
  const [jsonSearch, setJsonSearch] = useState("");
  const [mode, setMode] = useState<DesignerMode>("design");
  const [nativeDataMode, setNativeDataMode] = useState<NativeDataMode>("sample");
  const [businessExecution, setBusinessExecution] = useState<{ context: string; payload: FormattedInvoicePayload; filters: RenderFilters } | null>(null);
  const [explorerTab, setExplorerTab] = useState<ExplorerTab>("structure");
  const [selection, setSelection] = useState<DesignerSelection>({ type: "report" });
  const [formulaEdit, setFormulaEdit] = useState<{ selection: DesignerSelection; source: DesignerDocument } | null>(null);
  const formulaSession = isInternalFormulaSession(internalSession);
  const templateMode = isInternalFormulaSession(internalSession) && internalSession.templateId !== undefined;
  const [canvasValueMode, setCanvasValueMode] = useState<CanvasValueMode>("binding");
  const [designSampleFixtureId, setDesignSampleFixtureId] = useState<DesignSampleFixtureId>("standard");
  const [designSampleRecordCount, setDesignSampleRecordCount] = useState(25);
  const [definitionSource, setDefinitionSource] = useState<DefinitionSource>("local");
  const [definitions, setDefinitions] = useState<ReportDefinitionSummaryV3[]>([]);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState("");
  const [loadedDefinition, setLoadedDefinition] = useState<ReportDefinitionDetailV3 | null>(null);
  const loadedDefinitionRef = useRef(loadedDefinition);
  loadedDefinitionRef.current = loadedDefinition;
  const [conflictedDefinitionId, setConflictedDefinitionId] = useState<string | null>(null);
  const [definitionsLoading, setDefinitionsLoading] = useState(false);
  const [definitionsError, setDefinitionsError] = useState("");
  const [accessRestriction, setAccessRestriction] = useState<{ message: string; definitionId?: string } | null>(null);
  const [definitionInspection, setDefinitionInspection] = useState<DefinitionInspection | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const closeToast = useCallback(() => setToast(null), []);
  const persistencePending = useRef(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [backendValidationMessages, setBackendValidationMessages] = useState<DesignerValidationMessage[]>([]);
  const [zoom, setZoom] = useState(DEFAULT_PAGE.zoom);
  const [zoomPreference, setZoomPreference] = useState<ZoomPreference>("fit-width");
  const [showGrid, setShowGrid] = useState(false);
  const [showRulers, setShowRulers] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => loadDesignerPreference("leftPanelWidth", 320));
  const [rightPanelWidth, setRightPanelWidth] = useState(() => loadDesignerPreference("rightPanelWidth", 340));
  const [collapsedPanels, setCollapsedPanels] = useState<Record<PanelSide, boolean>>({ left: false, right: false });
  const [focusMode, setFocusMode] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const stageRef = useRef<HTMLElement | null>(null);
  const token = internalSession ? undefined : connection?.auth.token;
  const getToken = internalSession ? undefined : connection?.auth.getToken;
  const user = internalSession ? undefined : connection?.userContext.user;
  const activeTenantId = internalSession ? undefined : connection?.userContext.activeTenantId;
  const api = internalSession ? null : connection?.api || null;
  const canManageDefinitions = Boolean(api && connection?.permissions.manage);
  const canExportPdf = Boolean(api && connection?.permissions.pdf);
  const canExportExcel = Boolean(api && connection?.permissions.excel);
  const accessContext = JSON.stringify([activeTenantId, user?.userId, connection?.permissions.context]);
  const accessContextRef = useRef(accessContext);
  accessContextRef.current = accessContext;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrentAccess = useCallback((context: string) => mounted.current && accessContextRef.current === context, []);

  const activeDocument = history.present;
  const activeDocumentRef = useRef(activeDocument);
  activeDocumentRef.current = activeDocument;
  const payload = useMemo(() => designerDocumentToLegacyPayload(activeDocument, internalSession), [activeDocument, internalSession]);
  const parameterContext = JSON.stringify([payload.definitionId, connection?.userContext.activeTenantId, connection?.userContext.user?.userId, asRecord(payload.layout).parameters]);
  const parameterValues = parameterInputState?.key === parameterContext ? parameterInputState.values : undefined;
  useEffect(() => setParameterInputState(null), [parameterContext]);
  const designSampleOptions = useMemo(() => designTimeSampleOptions(payload), [payload]);
  const samplePreviewPayload = useMemo(() => internalSession ? internalSamplePayload(payload, internalSession, internalSampleIndex) : applyDesignTimeSampleData(payload, designSampleFixtureId, designSampleRecordCount), [payload, designSampleFixtureId, designSampleRecordCount, internalSession, internalSampleIndex]);
  const designSampleNotice = useMemo(() => designTimeSampleNotice(samplePreviewPayload, designSampleFixtureId, designSampleRecordCount), [samplePreviewPayload, designSampleFixtureId, designSampleRecordCount]);
  const isNativeDocument = isNativeReportsV31Payload(payload);
  const businessReady = canManageDefinitions && !accessRestriction && definitionSource === "backend" && Boolean(loadedDefinition?.definitionId) && loadedDefinition?.definitionId === payload.definitionId && !history.dirty && !jsonDraftDirty && !conflictedDefinitionId && loadedDefinition?.isActive !== false && saveStatus !== "loading" && saveStatus !== "saving";
  const businessContext = JSON.stringify([accessContext, loadedDefinition?.definitionId, loadedDefinition?.revision, businessReady]);
  const businessPayload = businessReady && businessExecution?.context === businessContext ? businessExecution.payload : null;
  const useBusinessData = isNativeDocument && nativeDataMode === "business";
  const previewPayload = useMemo(() => useBusinessData ? businessPayload || { ...payload, datasets: {}, assets: {} } : samplePreviewPayload, [useBusinessData, businessPayload, payload, samplePreviewPayload]);
  const nativeExcelSupported = useMemo(() => !isNativeDocument || (nativeWorkbookSupport(previewPayload).supported && (!useBusinessData || !!businessPayload)), [isNativeDocument, previewPayload, useBusinessData, businessPayload]);
  const acceptBusinessExecution = useCallback((result: FormattedInvoicePayload | null, filters?: RenderFilters) => setBusinessExecution(result && filters ? { context: businessContext, payload: result, filters } : null), [businessContext]);
  const frontendValidationMessages = useMemo(() => validateDesignerPayload(previewPayload), [previewPayload]);
  const validationMessages = useMemo(() => [...frontendValidationMessages, ...backendValidationMessages], [frontendValidationMessages, backendValidationMessages]);
  const jsonLineCount = useMemo(() => Math.max(1, jsonText.split(/\r?\n/).length), [jsonText]);
  const jsonSearchMatches = useMemo(() => {
    if (!jsonSearch.trim()) return 0;
    const escaped = jsonSearch.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return (jsonText.match(new RegExp(escaped, "gi")) || []).length;
  }, [jsonSearch, jsonText]);
  const selectedBand = selection.type === "band" || selection.type === "component" || selection.type === "field" || selection.type === "column"
    || selection.type === "nativeTableColumn" || selection.type === "nativeTableGroup"
    ? findBand(activeDocument, selection.bandId) || null
    : null;

  function restrictAccess(error: unknown) {
    if (!isReportAccessDenied(error)) return;
    setAccessRestriction({ message: reportFailureMessage(error, "Report access is unavailable."), definitionId: loadedDefinition?.definitionId || undefined });
    setBusinessExecution(null);
    setDefinitions([]);
    setDefinitionInspection(null);
    setLoadedDefinition(null);
    setSelectedDefinitionId("");
  }

  async function retryAccess() {
    if (!api || !canManageDefinitions || persistencePending.current) return;
    persistencePending.current = true;
    const context = accessContext;
    try {
      const list = await api.get<ReportDefinitionSummaryV3[]>("ReportsV3/definitions", DEFINITION_MANAGEMENT_OPTIONS);
      if (!Array.isArray(list)) throw new Error("The API returned an invalid report definition listing.");
      const detail = accessRestriction?.definitionId
        ? await api.get<ReportDefinitionDetailV3>(`ReportsV3/definitions/${encodeURIComponent(accessRestriction.definitionId)}`, DEFINITION_MANAGEMENT_OPTIONS)
        : null;
      if (!isCurrentAccess(context)) return;
      if (detail) assertReportAdmission(detail, "import");
      setDefinitions(list);
      setLoadedDefinition(detail);
      setSelectedDefinitionId(detail?.definitionId || "");
      setDefinitionsError("");
      setSaveStatus("idle");
      setSaveMessage("");
      setAccessRestriction(null);
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      setAccessRestriction(current => ({ ...current, message: reportFailureMessage(error, "Report access could not be refreshed.") }));
    } finally {
      persistencePending.current = false;
    }
  }

  function commit(label: string, command: (current: DesignerDocument) => commands.DesignerCommandResult) {
    setBusinessExecution(null);
    setHistory((current) => {
      const result = command(current.present);
      const next = result.document;
      setJsonText(prettyJson(next));
      setJsonError("");
      setJsonDraftDirty(false);
      setBackendValidationMessages([]);
      if (result.selection) setSelection(result.selection);
      return {
        past: [...current.past, current.present].slice(-50),
        present: next,
        future: [],
        dirty: true,
      };
    });
    setExportStatus(`${label}.`);
    if (!persistencePending.current && !conflictedDefinitionId) {
      setSaveStatus("idle");
      setSaveMessage("");
    }
  }

  function resetDocument(nextDocument: DesignerDocument, options: { dirty?: boolean; source?: DefinitionSource; detail?: ReportDefinitionDetailV3 | null; sampleId?: string; message?: string } = {}) {
    assertReportAdmission(nextDocument, "import");
    setBusinessExecution(null);
    setHistory({ past: [], present: nextDocument, future: [], dirty: Boolean(options.dirty) });
    setJsonText(prettyJson(nextDocument));
    setJsonError("");
    setJsonDraftDirty(false);
    setBackendValidationMessages([]);
    setConflictedDefinitionId(null);
    setSelection({ type: "report" });
    if (options.source) setDefinitionSource(options.source);
    if (options.detail !== undefined) {
      setLoadedDefinition(options.detail);
      setSelectedDefinitionId(options.detail?.definitionId || "");
    }
    if (options.sampleId) setSampleId(options.sampleId);
    setSaveStatus(options.message ? "saved" : "idle");
    setSaveMessage(options.message || "");
  }

  function confirmDiscard(): boolean {
    if (!history.dirty && !jsonDraftDirty) return true;
    return window.confirm("Discard unsaved report definition changes?");
  }

  function loadDefinition(nextSampleId: string) {
    if (persistencePending.current || !confirmDiscard()) return;
    const sample = DESIGNER_SAMPLES.find((item) => item.id === nextSampleId) || DESIGNER_SAMPLES[0];
    const nextDocument = legacyPayloadToDesignerDocument(editableSamplePayload(sample.payload));
    resetDocument(nextDocument, { source: "local", sampleId: sample.id, detail: null });
  }

  const refreshDefinitions = useCallback(async () => {
    if (!api || !canManageDefinitions) {
      setDefinitions([]);
      return undefined;
    }
    setDefinitionsLoading(true);
    setDefinitionsError("");
    setDefinitionInspection(null);
    const context = accessContextRef.current;
    try {
      const list = await api.get<ReportDefinitionSummaryV3[]>("ReportsV3/definitions", DEFINITION_MANAGEMENT_OPTIONS);
      if (!isCurrentAccess(context)) return undefined;
      if (!Array.isArray(list) || list.some(item => !item || typeof item.definitionId !== "string" || !item.definitionId.trim()) ||
        new Set(list.map(item => item.definitionId)).size !== list.length) throw new Error("The API returned an invalid report definition listing.");
      setDefinitions(list);
      return list;
    } catch (error) {
      if (!isCurrentAccess(context)) return undefined;
      setDefinitions([]);
      const message = reportFailureMessage(error, "Failed to load report definitions.");
      if (isReportAccessDenied(error)) {
        setBusinessExecution(null);
        setDefinitionInspection(null);
        setAccessRestriction(current => ({ message, definitionId: current?.definitionId || loadedDefinitionRef.current?.definitionId || undefined }));
        setLoadedDefinition(null);
        setSelectedDefinitionId("");
      }
      setDefinitionsError(message);
      setToast({ type: "error", message });
      return undefined;
    } finally {
      if (isCurrentAccess(context)) setDefinitionsLoading(false);
    }
  }, [api, canManageDefinitions, isCurrentAccess]);

  async function loadBackendDefinition(definitionId: string) {
    setExportStatus("");
    if (!api || !canManageDefinitions || !definitionId || persistencePending.current || !confirmDiscard()) return;
    setBusinessExecution(null);
    const previousDocument = activeDocument;
    const context = accessContext;
    let inspection: DefinitionInspection | undefined;
    persistencePending.current = true;
    setSaveStatus("loading");
    setDefinitionsError("");
    setSaveMessage(`Loading ${definitionId}...`);
    try {
      const detail = await api.get<ReportDefinitionDetailV3>(`ReportsV3/definitions/${encodeURIComponent(definitionId)}`, DEFINITION_MANAGEMENT_OPTIONS);
      if (!isCurrentAccess(context)) return;
      const admission = assertReportAdmission(detail, "validate");
      inspection = { definitionId, requiredFeatures: admission.requiredFeatures, admitted: false };
      if (activeDocumentRef.current !== previousDocument && !window.confirm("Discard changes made while the saved definition was loading?")) {
        setSaveStatus("idle");
        setSaveMessage("The saved definition was not opened. Newer local edits are preserved.");
        return;
      }
      const nextDocument = legacyPayloadToDesignerDocument(detailToLegacyPayload(detail));
      resetDocument(nextDocument, { source: "backend", detail, message: `Loaded ${definitionDisplayName(detail)}.` });
      setDefinitionInspection({ ...inspection, admitted: true });
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      restrictAccess(error);
      if (inspection) setDefinitionInspection(inspection);
      setSaveStatus("idle");
      const message = reportFailureMessage(error, "Failed to load report definition.");
      setSaveMessage("");
      setDefinitionsError(message);
    } finally {
      persistencePending.current = false;
    }
  }

  function createNewDefinition() {
    if (persistencePending.current || !confirmDiscard()) return;
    const source = designerDocumentToLegacyPayload(activeDocument);
    const timestamp = Date.now();
    source.definitionId = `custom-report-${timestamp}`;
    (source as FormattedInvoicePayload & JsonRecord).name = "New Custom Report";
    source.metadata = { ...asRecord(source.metadata), reportName: "New Custom Report" };
    const nextDocument = legacyPayloadToDesignerDocument(source);
    resetDocument(nextDocument, { source: "backend", detail: null, message: "Created a new unsaved definition." });
    setSelectedDefinitionId("");
    setSaveStatus("idle");
  }

  function createNewNativeDefinition() {
    if (persistencePending.current || !confirmDiscard()) return;
    const result = commands.createNativeDocument(activeDocument);
    resetDocument(result.document, { source: "backend", detail: null, message: "Created a new native reports-v3.1 definition." });
    setSelectedDefinitionId("");
    setSelection(result.selection || { type: "report" });
    setSaveStatus("idle");
  }

  async function saveDefinition(options: { saveAs?: boolean } = {}) {
    setExportStatus("");
    if (!api || !canManageDefinitions || accessRestriction || persistencePending.current || (!options.saveAs && conflictedDefinitionId)) return;
    try {
      assertReportAdmission(activeDocument, "save");
      if (!options.saveAs && loadedDefinition) assertReportAdmission(loadedDefinition, "save");
    } catch (error) {
      if (!(error instanceof NativeConditionError)) throw error;
      setSaveStatus("error");
      setSaveMessage(error.message);
      setToast({ type: "error", message: error.message });
      return;
    }
    if (jsonDraftDirty) {
      setSaveStatus("error");
      setSaveMessage("Apply or revert JSON edits before saving.");
      return;
    }
    const submittedDocument = activeDocument;
    const context = accessContext;
    const legacyPayload = designerDocumentToLegacyPayload(activeDocument);
    const request = legacyPayloadToUpsertRequest(legacyPayload, activeDocument);
    if (request.schemaVersion === "reports-v3.1" && Object.keys(asRecord(activeDocument.assets)).length) {
      setSaveStatus("error");
      setSaveMessage("This native report contains a top-level asset dictionary that the definition API cannot persist. Saving is blocked to avoid losing images. Use supported execution-provided asset references; custom asset persistence requires a backend contract update.");
      setToast({ type: "error", message: "Report assets cannot be saved through the current definition API." });
      return;
    }
    if (options.saveAs) {
      const nextId = window.prompt("Save as definition ID", `${request.definitionId || "custom-report"}-copy`);
      if (!nextId) return;
      request.definitionId = nextId.trim();
      request.name = window.prompt("Save as report name", `${request.name || "Custom Report"} Copy`)?.trim() || request.name;
    }
    if (!request.definitionId) {
      setSaveStatus("error");
      setSaveMessage("Definition ID is required before saving.");
      return;
    }
    const isExistingBackendDefinition = !options.saveAs && definitionSource === "backend" && loadedDefinition?.definitionId === request.definitionId;
    if (isExistingBackendDefinition && loadedDefinition.isEditable === false) {
      setSaveStatus("error");
      setSaveMessage("This saved definition is read-only. Use Save As to create a tenant copy.");
      return;
    }
    if (isExistingBackendDefinition && request.schemaVersion === "reports-v3.1" && !loadedDefinition.revision) {
      setSaveStatus("error");
      setSaveMessage("The server did not return a revision for this native definition. Reload after the updated API and Revision migration are deployed.");
      return;
    }
    if (isExistingBackendDefinition && loadedDefinition.revision) request.revision = loadedDefinition.revision;
    persistencePending.current = true;
    setSaveStatus("saving");
    setSaveMessage("Saving report definition...");
    try {
      const detail = isExistingBackendDefinition
        ? await api.put<ReportDefinitionDetailV3>(`ReportsV3/definitions/${encodeURIComponent(request.definitionId)}`, request)
        : await api.post<ReportDefinitionDetailV3>("ReportsV3/definitions", request);
      if (!isCurrentAccess(context)) return;
      if (activeDocumentRef.current === submittedDocument) {
        const nextDocument = legacyPayloadToDesignerDocument(detailToLegacyPayload(detail));
        resetDocument(nextDocument, { source: "backend", detail, message: `Saved ${definitionDisplayName(detail)}.` });
      } else {
        setLoadedDefinition(detail);
        setSelectedDefinitionId(detail.definitionId || "");
        setDefinitionSource("backend");
        setConflictedDefinitionId(null);
        setHistory((current) => {
          const present = { ...current.present, definitionId: detail.definitionId, definitionVersion: detail.version };
          if (present.metadata.reportName === submittedDocument.metadata.reportName) {
            present.metadata = { ...present.metadata, reportName: detail.name };
          }
          setJsonText(prettyJson(present));
          const savedIdentity = (document: DesignerDocument): DesignerDocument => ({
            ...document, definitionId: detail.definitionId, definitionVersion: detail.version,
          });
          return { past: current.past.map(savedIdentity), present, future: current.future.map(savedIdentity), dirty: true };
        });
        setSaveStatus("saved");
        setSaveMessage("Saved the submitted report. Newer local edits remain unsaved.");
      }
      setToast({ type: "success", message: "Report definition saved." });
      void refreshDefinitions();
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      setSaveStatus("error");
      restrictAccess(error);
      if (isReportDefinitionConflict(error)) {
        setConflictedDefinitionId(request.definitionId);
        setHistory((current) => ({ ...current, dirty: true }));
        setSaveMessage("This report changed in another session. Your local edits are preserved. Reload the saved definition to discard them, or use Save As to keep an independent copy.");
      } else {
        setSaveMessage(reportFailureMessage(error, "Failed to save report definition."));
      }
      setToast({ type: "error", message: "Report changes were not saved." });
    } finally {
      persistencePending.current = false;
    }
  }

  async function duplicateBackendDefinition() {
    if (!api || !canManageDefinitions || accessRestriction || persistencePending.current) return;
    const definitionId = definitionSource === "backend" ? loadedDefinition?.definitionId : null;
    if (!definitionId) {
      await saveDefinition({ saveAs: true });
      return;
    }
    if (!confirmDiscard()) return;
    const previousDocument = activeDocument;
    const context = accessContext;
    persistencePending.current = true;
    setSaveStatus("saving");
    setSaveMessage("Duplicating report definition...");
    try {
      assertReportAdmission(activeDocument, "copy");
      assertReportAdmission(loadedDefinition, "copy");
      const detail = await api.post<ReportDefinitionDetailV3>(`ReportsV3/definitions/${encodeURIComponent(definitionId)}/copy`);
      if (!isCurrentAccess(context)) return;
      if (activeDocumentRef.current === previousDocument) {
        const nextDocument = legacyPayloadToDesignerDocument(detailToLegacyPayload(detail));
        resetDocument(nextDocument, { source: "backend", detail, message: `Duplicated ${definitionDisplayName(detail)}.` });
      } else {
        setSaveStatus("saved");
        setSaveMessage(`Duplicated ${definitionDisplayName(detail)}. Newer local edits are preserved; select the saved copy to open it.`);
      }
      setToast({ type: "success", message: "Saved definition copied." });
      void refreshDefinitions();
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      setSaveStatus("error");
      restrictAccess(error);
      setSaveMessage(reportFailureMessage(error, "Failed to duplicate report definition."));
      setToast({ type: "error", message: "Report definition could not be copied." });
    } finally {
      persistencePending.current = false;
    }
  }

  async function validateWithBackend() {
    if (!api || !canManageDefinitions || accessRestriction || persistencePending.current) return;
    const submittedDocument = activeDocument;
    const context = accessContext;
    persistencePending.current = true;
    setSaveStatus("loading");
    setSaveMessage("Validating with server...");
    try {
      const legacyPayload = designerDocumentToLegacyPayload(activeDocument);
      const request = legacyPayloadToUpsertRequest(legacyPayload, activeDocument);
      const result = await api.post<unknown>("ReportsV3/definitions/validate", definitionValidationRequest(request));
      if (!isCurrentAccess(context)) return;
      if (activeDocumentRef.current !== submittedDocument) {
        setSaveStatus("idle");
        setSaveMessage("The report changed during server validation. Validate again to check the current document.");
        return;
      }
      const response = readDefinitionValidationResponse(result);
      const { errors, warnings } = response;
      const serverMessages: DesignerValidationMessage[] = [
        ...errors.map((message, index): DesignerValidationMessage => ({ level: "error", path: `backend.errors[${index}]`, message })),
        ...warnings.map((message, index): DesignerValidationMessage => ({ level: "warning", path: `backend.warnings[${index}]`, message })),
      ];
      if (!response.isValid && !errors.length) serverMessages.unshift({ level: "error", path: "backend", message: "Backend validation rejected this report without diagnostics." });
      setBackendValidationMessages(serverMessages.length ? serverMessages : [{ level: "info", path: "backend", message: "Backend validation passed." }]);
      setSaveStatus(response.isValid === false ? "error" : "saved");
      setSaveMessage(response.isValid === false ? "Backend validation found issues." : "Backend validation passed.");
      setMode("validate");
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      restrictAccess(error);
      const message = reportFailureMessage(error, "Backend validation failed.");
      setBackendValidationMessages([{ level: "error", path: "backend", message }]);
      setSaveStatus("error");
      setSaveMessage(message);
      setMode("validate");
    } finally {
      persistencePending.current = false;
    }
  }

  function undo() {
    setHistory((current) => {
      const previous = current.past[current.past.length - 1];
      if (!previous) return current;
      setJsonText(prettyJson(previous));
      setJsonDraftDirty(false);
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
        dirty: true,
      };
    });
  }

  function redo() {
    setHistory((current) => {
      const next = current.future[0];
      if (!next) return current;
      setJsonText(prettyJson(next));
      setJsonDraftDirty(false);
      return {
        past: [...current.past, current.present],
        present: next,
        future: current.future.slice(1),
        dirty: true,
      };
    });
  }

  function updateJson(nextText: string) {
    setJsonText(nextText);
    const parsed = parseDesignerDocumentJson(nextText, internalSession);
    setJsonError(parsed.error);
    setJsonDraftDirty(true);
  }

  function applyJson() {
    if (persistencePending.current) return;
    const parsed = parseDesignerDocumentJson(jsonText, internalSession);
    setJsonError(parsed.error);
    if (!parsed.document) return;
    setBackendValidationMessages([]);
    setSaveStatus("idle");
    setSaveMessage("");
    setHistory((current) => ({
      past: [...current.past, current.present].slice(-50),
      present: parsed.document as DesignerDocument,
      future: [],
      dirty: true,
    }));
    setJsonDraftDirty(false);
    setExportStatus("Applied JSON changes.");
  }

  function revertJson() {
    setJsonText(prettyJson(activeDocument));
    setJsonError("");
    setJsonDraftDirty(false);
    setExportStatus("Reverted unapplied JSON edits.");
  }

  function openJsonEditor() {
    if (!jsonDraftDirty) setJsonText(prettyJson(activeDocument));
    setMode("json");
  }

  function updateDefinitionField(key: string, value: unknown) {
    if (key !== "definitionId" && key !== "name") return;
    commit("Updated report", (current) => commands.updateDefinitionField(current, key, value));
  }

  function updateMetadataField(key: string, value: unknown) {
    commit("Updated report metadata", (current) => commands.updateMetadataField(current, key, value));
  }

  function updatePageSetup(key: "size" | "orientation", value: DesignerPageSize | DesignerPageOrientation) {
    commit("Updated page setup", (current) => commands.updatePageSetup(current, key, value));
  }

  function updateBand(bandId: string, key: "id" | "datasetId" | "styleId" | "outputs", value: unknown) {
    commit("Updated band", (current) => commands.updateBand(current, bandId, key, value));
  }

  function updateComponent(componentSelection: Extract<DesignerSelection, { type: "component" }>, key: "datasetId" | "styleId" | "outputs", value: unknown) {
    commit("Updated component", (current) => commands.updateComponent(current, componentSelection, key, value));
  }

  function updateField(fieldSelection: Extract<DesignerSelection, { type: "field" }>, key: keyof Pick<DesignerField, "label" | "key" | "format" | "horizontalAlign" | "styleId" | "column" | "row" | "marginTop">, value: unknown) {
    commit("Updated field", (current) => commands.updateField(current, fieldSelection, key, value));
  }

  function updateColumn(columnSelection: Extract<DesignerSelection, { type: "column" }>, key: keyof Pick<DesignerColumn, "label" | "key" | "format" | "horizontalAlign" | "width">, value: unknown) {
    commit("Updated column", (current) => commands.updateColumn(current, columnSelection, key, value));
  }

  function addSection(type: "text" | "keyValueGrid" | "table") {
    commit(`Added ${type} section`, (current) => commands.addBand(current, type));
  }

  function addNativeBand(role: NativeBandRole) {
    commit(`Added ${role} band`, (current) => commands.addNativeBand(current, role));
  }

  function addNativeTextElement(bandId: string, at?: { x: number; y: number }, parentElementId?: string) {
    commit("Added text element", (current) => commands.addNativeTextElement(current, bandId, at, parentElementId));
  }

  function addNativeFieldElement(bandId: string, datasetName: string, fieldKey: string, at?: { x: number; y: number }, parentElementId?: string) {
    commit("Added bound field", (current) => commands.addNativeFieldElement(current, bandId, datasetName, fieldKey, at, parentElementId));
  }

  function addNativeContainerElement(bandId: string, parentElementId?: string, at?: { x: number; y: number }) {
    commit("Added container", (current) => commands.addNativeContainerElement(current, bandId, parentElementId, at));
  }

  function addNativeImageElement(bandId: string, asset?: string, at?: { x: number; y: number }, parentElementId?: string) {
    commit("Added image element", (current) => commands.addNativeImageElement(current, bandId, asset, at, parentElementId));
  }

  function addNativeInvoiceHeaderContainer(bandId: string) {
    commit("Added invoice header container", (current) => commands.addNativeInvoiceHeaderContainer(current, bandId));
  }

  function addNativeTableElement(bandId: string, datasetName = "", fields: string[] = [], at?: { x: number; y: number }) {
    commit("Added repeating table", (current) => commands.addNativeTableElement(current, bandId, datasetName, fields, at));
  }

  function updateNativeBand(bandId: string, key: "id" | "height" | "sizing", value: unknown) {
    commit("Updated native band", (current) => commands.updateNativeBand(current, bandId, key, value));
  }

  function resizeNativeBand(bandId: string, height: number) {
    commit("Resized native band", (current) => commands.resizeNativeBand(current, bandId, height));
  }

  function updateNativeElement(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: Parameters<typeof commands.updateNativeElement>[2]) {
    commit("Updated element", (current) => commands.updateNativeElement(current, elementSelection, patch));
  }

  function updateNativeTable(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: Parameters<typeof commands.updateNativeTable>[2]) {
    commit("Updated table", (current) => commands.updateNativeTable(current, elementSelection, patch));
  }

  function addNativeTableColumn(bandId: string, tableId: string, fieldKey: string, datasetName?: string) {
    commit("Added table column", (current) => commands.addNativeTableColumn(current, bandId, tableId, fieldKey, datasetName));
  }

  function addNativeCalculatedTableColumn(bandId: string, tableId: string, options?: Parameters<typeof commands.addNativeCalculatedTableColumn>[3]) {
    commit("Added calculated field", (current) => commands.addNativeCalculatedTableColumn(current, bandId, tableId, options));
  }

  function updateNativeTableColumn(columnSelection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, patch: Parameters<typeof commands.updateNativeTableColumn>[2]) {
    commit("Updated table column", (current) => commands.updateNativeTableColumn(current, columnSelection, patch));
  }

  function resizeNativeTableColumn(columnSelection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, width: number, adjacentColumnId?: string, adjacentWidth?: number) {
    commit("Resized table column", (current) => commands.resizeNativeTableColumn(current, columnSelection, width, adjacentColumnId, adjacentWidth));
  }

  function resizeNativeTable(tableSelection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number) {
    commit("Resized table", (current) => commands.resizeNativeTable(current, tableSelection, width));
  }

  function fitNativeTableToPrintableWidth(tableSelection: Extract<DesignerSelection, { type: "nativeElement" }>) {
    commit("Fit table to printable width", (current) => commands.fitNativeTableToPrintableWidth(current, tableSelection));
  }

  function reorderNativeTableColumn(columnSelection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, direction: -1 | 1) {
    commit("Reordered table column", (current) => commands.reorderNativeTableColumn(current, columnSelection, direction));
  }

  function moveNativeTableColumn(columnSelection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) {
    commit("Moved table column", (current) => commands.moveNativeTableColumn(current, columnSelection, targetIndex));
  }
  function addNativeTableSort(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, field: string) {
    commit("Added table sorting rule", (current) => commands.addNativeTableSort(current, tableSelection, field));
  }
  function updateNativeTableSort(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, patch: Parameters<typeof commands.updateNativeTableSort>[3]) {
    commit("Updated table sorting rule", (current) => commands.updateNativeTableSort(current, tableSelection, index, patch));
  }
  function removeNativeTableSort(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number) {
    commit("Removed table sorting rule", (current) => commands.removeNativeTableSort(current, tableSelection, index));
  }
  function moveNativeTableSort(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, direction: -1 | 1) {
    commit("Moved table sorting rule", (current) => commands.moveNativeTableSort(current, tableSelection, index, direction));
  }
  function setNativeTableSortDirection(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>, field: string, direction: "asc" | "desc", type?: "auto" | "date" | "number" | "string") {
    commit("Updated table sorting", (current) => commands.setNativeTableSortDirection(current, tableSelection, field, direction, type));
  }
  function updateNativeTableGroup(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableGroup>[2]) {
    commit("Updated table grouping", (current) => commands.updateNativeTableGroup(current, tableSelection, patch));
  }
  function updateNativeTableSummary(tableSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableSummary>[2]) {
    commit("Updated table summary row", (current) => commands.updateNativeTableSummary(current, tableSelection, patch));
  }
  function moveNativeElements(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, delta: { dx: number; dy: number }) {
    commit("Moved element", (current) => commands.moveNativeElements(current, elementSelection, delta));
  }

  function resizeNativeElement(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Parameters<typeof commands.resizeNativeElement>[2]) {
    commit("Resized element", (current) => commands.resizeNativeElement(current, elementSelection, bounds));
  }

  function alignNativeElements(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, alignment: Parameters<typeof commands.alignNativeElements>[2]) {
    commit("Aligned elements", (current) => commands.alignNativeElements(current, elementSelection, alignment));
  }

  function reorderNativeElement(bandId: string, elementId: string, direction: -1 | 1) {
    commit("Reordered native element", (current) => commands.reorderNativeElement(current, bandId, elementId, direction));
  }

  function fitNativeBandToContent(bandId: string, height: number) {
    commit("Fit band to content", (current) => commands.fitNativeBandToContent(current, bandId, height));
  }

  function fitNativeContainerToContent(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Parameters<typeof commands.resizeNativeElement>[2]) {
    commit("Fit container to content", (current) => commands.fitNativeContainerToContent(current, elementSelection, bounds));
  }

  function reparentNativeElement(elementSelection: Extract<DesignerSelection, { type: "nativeElement" }>, targetParentId: string | null, bounds: { x: number; y: number; width: number; height: number }) {
    commit("Moved element to parent", (current) => commands.reparentNativeElement(current, elementSelection, targetParentId, bounds));
  }

  function addFieldToBand(bandId: string, datasetName: string, fieldKey: string) {
    commit("Added data field", (current) => commands.addFieldToBand(current, bandId, datasetName, fieldKey));
  }

  function moveField(fieldSelection: Extract<DesignerSelection, { type: "field" }>, column: number, insertIndex?: number) {
    commit("Moved field", (current) => commands.moveField(current, fieldSelection, column, insertIndex));
  }

  function reorderColumn(columnSelection: Extract<DesignerSelection, { type: "column" }>, targetColumnId: string) {
    commit("Reordered column", (current) => commands.reorderColumn(current, columnSelection, targetColumnId));
  }

  function resizeColumn(columnSelection: Extract<DesignerSelection, { type: "column" }>, width: number) {
    commit("Resized column", (current) => commands.resizeColumn(current, columnSelection, width));
  }

  function moveBand(bandId: string, direction: -1 | 1) {
    commit("Moved band", (current) => commands.moveBand(current, bandId, direction));
  }

  function moveNativeBand(bandId: string, direction: -1 | 1) {
    commit("Moved native band", (current) => commands.moveNativeBand(current, bandId, direction));
  }

  function duplicateSelection() {
    commit("Duplicated selection", (current) => commands.duplicateSelection(current, selection));
  }

  function deleteSelection() {
    if (selection.type === "nativeTableColumn") {
      const dependencies = nativeTableColumnDependencies(previewPayload, selection);
      if (dependencies.length) {
        const confirmed = window.confirm(`This calculated field is referenced by ${dependencies.length} configuration${dependencies.length === 1 ? "" : "s"}:\n\n${dependencies.join("\n")}\n\nDelete it anyway? Dependent expressions, sorting, and totals will not be rewritten automatically.`);
        if (!confirmed) return;
      }
    }
    commit("Deleted selection", (current) => commands.deleteSelection(current, selection));
  }

  async function exportPayload(kind: "pdf" | "excel") {
    const context = accessContext;
    if (accessRestriction || (kind === "pdf" ? !canExportPdf : !canExportExcel)) {
      setExportStatus("This export is unavailable with your current subscription features.");
      return;
    }
    if (internalSession) {
      const message = "Internal authoring workspaces are memory-only. PDF and Excel export are unavailable.";
      setExportStatus(message);
      setToast({ message, type: "error" });
      return;
    }
    if (!useBusinessData || !businessPayload || !loadedDefinition?.revision || !businessExecution) {
      const message = "Execute Business Data for the saved report and current filters before exporting. No sample data will be used.";
      setExportStatus(message);
      setToast({ message, type: "error" });
      return;
    }
    if (!token || !getToken || !activeTenantId || !user?.userId) {
      setExportStatus("Export requires an authenticated user, tenant, and token.");
      return;
    }
    setExportStatus(`Generating ${kind.toUpperCase()}...`);
    try {
      assertReportAdmission(previewPayload);
      const renderContext = buildRenderContext({
        ...businessExecution.filters, reportId: loadedDefinition.reportKey || loadedDefinition.reportType,
        definitionId: loadedDefinition.definitionId, revision: loadedDefinition.revision, schemaVersion: previewPayload.schemaVersion,
      }, kind, "v3");
      if (kind === "excel" && isNativeDocument) {
        const warnings = await exportNativeExcel({
          payload: { ...previewPayload, metadata: { ...previewPayload.metadata, reportName: String(asRecord(payload.metadata).reportName || asRecord(payload).name || "Native report") } },
          fileName: `${payload.definitionId || "designer-report"}.xlsx`,
          token, getToken, tenantId: activeTenantId, userId: user.userId, sourceMode: useBusinessData ? "business" : "sample",
          isCurrent: () => isCurrentAccess(context),
          renderContext,
        });
        if (!isCurrentAccess(context)) return;
        const message = `EXCEL generated.${warnings ? " See Export Info for export notices." : ""}`;
        setExportStatus(message);
        setToast({ message, type: "success" });
        return;
      }
      const exportApi = createApiClient({ baseUrl: getPdfServerUrl(), token, getToken, tenantId: activeTenantId, userId: user.userId });
      const response = await exportApi.postResponse(`reports/reportsv3/${kind}`, {
        payload: previewPayload, fileName: `${payload.definitionId || "designer-report"}.${kind === "pdf" ? "pdf" : "xlsx"}`, ...renderContext,
      }, { accept: kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const blob = await readValidatedExportBlob(response, kind);
      if (!isCurrentAccess(context)) return;
      const objectUrl = window.URL.createObjectURL(blob);
      if (kind === "pdf") {
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
      } else {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${payload.definitionId || "designer-report"}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      }
      setExportStatus(`${kind.toUpperCase()} generated.`);
    } catch (error) {
      if (!isCurrentAccess(context)) return;
      const message = reportFailureMessage(error, `${kind.toUpperCase()} export failed.`);
      setExportStatus(message);
      if (kind === "excel" && isNativeDocument) setToast({ message, type: "error" });
    }
  }

  function downloadJson() {
    if (internalSession) {
      setToast({ type: "error", message: "Internal authoring workspaces are memory-only. Use the explicit canonical round-trip controls." });
      return;
    }
    assertReportAdmission(activeDocument, "serialize");
    const blob = new Blob([prettyJson(activeDocument)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${payload.definitionId || "report-definition"}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function startPanelResize(side: "left" | "right", event: React.MouseEvent) {
    event.preventDefault();
    const startX = event.clientX;
    const startLeft = leftPanelWidth;
    const startRight = rightPanelWidth;
    function handleMove(moveEvent: MouseEvent) {
      const delta = moveEvent.clientX - startX;
      if (side === "left") {
        const width = clamp(startLeft + delta, 300, 460);
        setLeftPanelWidth(width);
        saveDesignerPreference("leftPanelWidth", width);
      } else {
        const width = clamp(startRight - delta, 280, 460);
        setRightPanelWidth(width);
        saveDesignerPreference("rightPanelWidth", width);
      }
    }
    function handleUp() {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    }
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  }

  function togglePanel(side: PanelSide) {
    setCollapsedPanels((current) => ({ ...current, [side]: !current[side] }));
  }

  const fitCanvas = useCallback((mode: FitMode, options: { remember?: boolean } = {}) => {
    const viewport = stageRef.current?.getElementsByClassName(styles["designer-canvas-viewport"]).item(0);
    const viewportBounds = viewport?.getBoundingClientRect() || stageRef.current?.getBoundingClientRect();
    if (!viewportBounds) return;
    if (viewportBounds.width <= 0 || viewportBounds.height <= 0) return;
    const viewportStyle = viewport ? window.getComputedStyle(viewport) : null;
    const horizontalPadding = viewportStyle ? parseFloat(viewportStyle.paddingLeft) + parseFloat(viewportStyle.paddingRight) : 88;
    const verticalPadding = viewportStyle ? parseFloat(viewportStyle.paddingTop) + parseFloat(viewportStyle.paddingBottom) : 84;
    const metrics = currentPagePixelSize(activeDocument.page.size, activeDocument.page.orientation, payload);
    const rulerWidth = showRulers ? NATIVE_RULER_VERTICAL_SIZE : 0;
    const rulerHeight = showRulers ? NATIVE_RULER_HORIZONTAL_SIZE : 0;
    const availableWidth = Math.max(160, viewportBounds.width - horizontalPadding - (rulerWidth * 2));
    const availableHeight = Math.max(160, viewportBounds.height - verticalPadding - rulerHeight);
    const scale = mode === "width"
      ? availableWidth / metrics.width
      : Math.min(availableWidth / metrics.width, availableHeight / metrics.height);
    setZoom(clamp(Math.round(scale * 100), 25, 200));
    if (options.remember !== false) setZoomPreference(mode === "width" ? "fit-width" : "fit-page");
  }, [activeDocument.page.orientation, activeDocument.page.size, payload, showRulers]);

  function setManualZoom(nextZoom: number) {
    setZoom(clamp(nextZoom, 25, 200));
    setZoomPreference("manual");
  }

  useEffect(() => {
    if (!api) {
      setDefinitions([]);
      return;
    }
    void refreshDefinitions();
  }, [api, refreshDefinitions]);

  useEffect(() => {
    if (canManageDefinitions) return;
    setDefinitions([]);
    setDefinitionsLoading(false);
    setSaveStatus("idle");
    setBusinessExecution(null);
    setDefinitionInspection(null);
    setLoadedDefinition(null);
  }, [canManageDefinitions]);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (!history.dirty && !jsonDraftDirty) return;
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [history.dirty, jsonDraftDirty]);

  useLayoutEffect(() => {
    if (zoomPreference === "manual") return undefined;
    const stage = stageRef.current;
    let frame = 0;
    const requestFit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => fitCanvas(zoomPreference === "fit-page" ? "page" : "width", { remember: false }));
    };
    requestFit();
    if (!stage || typeof ResizeObserver === "undefined") {
      return () => window.cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(requestFit);
    observer.observe(stage);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [activeDocument.page.orientation, activeDocument.page.size, collapsedPanels.left, collapsedPanels.right, fitCanvas, focusMode, leftPanelWidth, mode, payload, rightPanelWidth, showRulers, zoomPreference]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (accessRestriction) return;
      if (formulaEdit || event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;
      const isCommand = event.ctrlKey || event.metaKey;
      if (isCommand && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
      if (isCommand && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redo();
      }
      if (isCommand && event.key.toLowerCase() === "d") {
        event.preventDefault();
        duplicateSelection();
      }
      if (selection.type === "nativeElement" && event.key.startsWith("Arrow")) {
        event.preventDefault();
        const amount = event.shiftKey ? 10 : 1;
        const dx = event.key === "ArrowLeft" ? -amount : event.key === "ArrowRight" ? amount : 0;
        const dy = event.key === "ArrowUp" ? -amount : event.key === "ArrowDown" ? amount : 0;
        moveNativeElements(selection, { dx, dy });
      }
      if (event.key === "Escape") {
        setSelection({ type: "report" });
      }
      if (event.key === "Delete" || event.key === "Backspace") {
        if (selection.type === "report") return;
        event.preventDefault();
        deleteSelection();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (accessRestriction) return <div data-testid="report-designer" className={styles["designer-shell"]}>
    <p role="alert">{accessRestriction.message}</p>
    <p>Unsaved report changes are preserved in this tab. Saved report details and business data are hidden until access is confirmed.</p>
    <button type="button" className={styles["designer-tool-btn"]} disabled={!canManageDefinitions} onClick={() => { void retryAccess(); }}>Refresh access</button>
  </div>;

  return (
    <div data-testid="report-designer" className={`${styles["designer-shell"]} ${internalSession ? styles["designer-shell--internal"] : ""}`}>
      <div>
      {onOpenTemplateGallery && <button id="reportDesignerTemplateBrowse" className={styles["designer-tool-btn"]} type="button" onClick={() => { if (!persistencePending.current && confirmDiscard()) onOpenTemplateGallery(); }}>Browse report templates</button>}
      {!internalSession && !canManageDefinitions && <p role="status">Saved report administration requires Owner or Admin access and the custom report templates feature. Local drafts remain available.</p>}
      {!internalSession && <button id="reportDesignerBrowseDefinitions" className={styles["designer-tool-btn"]} type="button" disabled={!canManageDefinitions || saveStatus === "saving" || saveStatus === "loading"} onClick={() => {
        setDefinitionSource("backend");
        document.getElementById("definitionBrowserSearch")?.focus();
      }}>Browse saved reports</button>}
      <DesignerToolbar
        internalAuthoring={Boolean(internalSession)}
        canRedo={history.future.length > 0}
        canUndo={history.past.length > 0}
        definitionSource={definitionSource}
        definitions={definitions}
        dirty={history.dirty || jsonDraftDirty}
        duplicateBackendDefinition={duplicateBackendDefinition}
        exportPayload={exportPayload}
        canManageDefinitions={canManageDefinitions}
        canSave={canManageDefinitions && !conflictedDefinitionId && loadedDefinition?.isEditable !== false}
        definitionsLoading={definitionsLoading}
        savedToBackend={definitionSource === "backend" && Boolean(loadedDefinition)}
        canExportExcel={canExportExcel && nativeExcelSupported}
        canExportPdf={canExportPdf && (!useBusinessData || Boolean(businessPayload))}
        pdfExportRestriction={!canExportPdf ? "PDF export requires current Owner/Admin access and the client_ready_reports subscription feature." : undefined}
        excelExportRestriction={!canExportExcel ? "Excel export requires current Owner/Admin access and the raw_excel_export subscription feature." : undefined}
        nativeDataMode={isNativeDocument && !internalSession ? nativeDataMode : undefined}
        setNativeDataMode={(next) => {
          setNativeDataMode(next);
          setBusinessExecution(null);
          if (next === "business") setMode("preview");
        }}
        mode={mode}
        sampleId={sampleId}
        designSampleFixtureId={designSampleFixtureId}
        designSampleNotice={designSampleNotice}
        designSampleOptions={designSampleOptions}
        designSampleRecordCount={designSampleRecordCount}
        loadDefinition={loadDefinition}
        loadBackendDefinition={loadBackendDefinition}
        refreshDefinitions={refreshDefinitions}
        reportName={String(asRecord(payload.metadata).reportName || asRecord(payload).name || payload.definitionId || "Untitled report")}
        saveDefinition={saveDefinition}
        saveStatus={saveStatus}
        selectedDefinitionId={selectedDefinitionId}
        canvasValueMode={canvasValueMode}
        setCanvasValueMode={setCanvasValueMode}
        setDesignSampleFixtureId={setDesignSampleFixtureId}
        setDesignSampleRecordCount={setDesignSampleRecordCount}
        setDefinitionSource={(source) => {
          if (!persistencePending.current && (source === definitionSource || confirmDiscard())) setDefinitionSource(source);
        }}
        setMode={setMode}
        setShowGrid={setShowGrid}
        setShowRulers={setShowRulers}
        setSnapEnabled={setSnapEnabled}
        setZoom={setManualZoom}
        zoomPreference={zoomPreference}
        showGrid={showGrid}
        showRulers={showRulers}
        snapEnabled={snapEnabled}
        undo={undo}
        validateWithBackend={validateWithBackend}
        openJsonEditor={openJsonEditor}
        redo={redo}
        zoom={zoom}
        downloadJson={downloadJson}
        createNewDefinition={createNewDefinition}
        createNewNativeDefinition={createNewNativeDefinition}
        collapsedPanels={collapsedPanels}
        deleteSelection={deleteSelection}
        duplicateSelection={duplicateSelection}
        fitCanvas={fitCanvas}
        focusMode={focusMode}
        alignNativeElements={alignNativeElements}
        selection={selection}
        setFocusMode={setFocusMode}
        togglePanel={togglePanel}
      />
      {saveMessage && !definitionsError && !conflictedDefinitionId && <p className={styles["designer-action-status"]} role={saveStatus === "error" ? "alert" : "status"}>{saveMessage}</p>}
      {exportStatus && !definitionsError && !conflictedDefinitionId && <p className={styles["designer-action-status"]} role="status">{exportStatus}</p>}
      {!internalSession && definitionSource === "backend" && <ReportDefinitionBrowser definitions={definitions} loading={definitionsLoading}
        busy={!canManageDefinitions || saveStatus === "saving" || saveStatus === "loading"} error={definitionsError} selectedId={loadedDefinition?.definitionId || ""} inspection={definitionInspection}
        onOpen={id => { void loadBackendDefinition(id); }} onRefresh={() => { void refreshDefinitions(); }} />}
      {internalSession && <div className={styles["designer-internal-tools"]} role="region" aria-label="Internal report authoring controls" tabIndex={0}>
      {formulaSession && <InternalFormulaTools payload={payload} templateMode={templateMode} onSelect={setSelection} onReopen={json => {
        const parsed = parseDesignerDocumentJson(json, internalSession);
        if (!parsed.document) { setToast({ type: "error", message: parsed.error }); return; }
        const document = parsed.document;
        setParameterInputState(null);
        setParameterInputEpoch(epoch => epoch + 1);
        commit("Reopened canonical formula workspace", () => ({ document }));
      }} />}
      {formulaSession && <InternalParameterPanel key={JSON.stringify([payload.definitionId, connection?.userContext.activeTenantId, connection?.userContext.user?.userId, parameterInputEpoch])}
        parameters={reportParameters(payload)} layout={asRecord(payload.layout)} onRun={values => setParameterInputState({ key: parameterContext, values })}
        onChange={parameters => {
          const source = activeDocumentRef.current;
          const result = commands.applyInternalParametersCommand(source, parameters);
          commit("Updated report parameters", current => current === source ? result : commands.applyInternalParametersCommand(current, parameters));
        }} />}
      {formulaSession && <InternalFilterPanel payload={payload} parameterValues={parameterValues} onChange={filters => {
        const source = activeDocumentRef.current;
        const result = commands.applyInternalFiltersCommand(source, filters);
        commit("Updated presentation filters", current => current === source ? result : commands.applyInternalFiltersCommand(current, filters));
      }} />}
      {internalSession && !formulaSession && <InternalConditionalTools payload={payload} session={internalSession} sampleIndex={internalSampleIndex} setSampleIndex={setInternalSampleIndex} onReopen={json => {
        const parsed = parseDesignerDocumentJson(json, internalSession);
        if (!parsed.document) { setToast({ type: "error", message: parsed.error }); return; }
        const document = parsed.document;
        commit("Reopened canonical internal fixture", () => ({ document }));
      }} />}
      </div>}
      {conflictedDefinitionId && (
        <div className={styles["designer-conflict"]} role="alert">
          <span>The saved report changed in another session. Local edits have not been overwritten.</span>
          <button className={styles["designer-tool-btn"]} disabled={saveStatus === "loading" || saveStatus === "saving"} type="button" onClick={() => loadBackendDefinition(conflictedDefinitionId)}>Reload saved definition</button>
        </div>
      )}
      </div>

      <div className={`${styles["designer-workspace"]} ${focusMode || mode !== "design" ? styles["designer-workspace-focus"] : ""}`} style={{ gridTemplateColumns: workspaceGridTemplate(focusMode || mode !== "design", collapsedPanels) }}>
        {mode === "design" && !focusMode && !collapsedPanels.left && (
        <aside className={styles["designer-panel"]} aria-label="Report explorer" style={{ width: leftPanelWidth }}>
          <PanelTabs active={explorerTab} setActive={setExplorerTab} />
          <ExplorerPanelIntro tab={explorerTab} isNative={isNativeDocument} />
          <div className={styles["designer-panel-body"]}>
            {explorerTab === "structure" && (
              <StructureTree bands={activeDocument.bands} isNative={isNativeDocument} moveNativeTableColumn={moveNativeTableColumn} nativeBands={nativeBandsFromPayload(payload)} selection={selection} setSelection={setSelection} />
            )}
            {explorerTab === "data" && (
              <DataExplorer
                updateExecutionSources={(data) => commit("Updated invoice execution sources", (current) => commands.updateExecutionSources(current, data))}
                addFieldToBand={addFieldToBand}
                addNativeFieldElement={addNativeFieldElement}
                addNativeTableColumn={addNativeTableColumn}
                moveNativeTableColumn={moveNativeTableColumn}
                datasets={activeDocument.datasets}
                document={activeDocument}
                isNative={isNativeDocument}
                nativeBands={nativeBandsFromPayload(previewPayload)}
                payload={previewPayload}
                selection={selection}
              />
            )}
            {explorerTab === "toolbox" && (
              <Toolbox addNativeBand={addNativeBand} addNativeContainerElement={addNativeContainerElement} addNativeImageElement={addNativeImageElement} addNativeInvoiceHeaderContainer={addNativeInvoiceHeaderContainer} addNativeTableElement={addNativeTableElement} addNativeTextElement={addNativeTextElement} addSection={addSection} datasets={activeDocument.datasets} payload={previewPayload} isNative={isNativeDocument} selection={selection} />
            )}
          </div>
        </aside>
        )}
        {mode === "design" && !focusMode && !collapsedPanels.left && <div className={styles["designer-resize-handle"]} role="separator" aria-label="Resize report explorer" onMouseDown={(event) => startPanelResize("left", event)} />}

        <main className={styles["designer-stage"]} ref={stageRef}>
          {mode === "design" && (
            isNativeDocument ? (
              <NativeDesignCanvas
                addNativeFieldElement={addNativeFieldElement}
                addNativeTableColumn={addNativeTableColumn}
                addNativeContainerElement={addNativeContainerElement}
                addNativeImageElement={addNativeImageElement}
                addNativeTextElement={addNativeTextElement}
                moveNativeElements={moveNativeElements}
                moveNativeTableColumn={moveNativeTableColumn}
                payload={previewPayload}
                resizeNativeElement={resizeNativeElement}
                resizeNativeTableColumn={resizeNativeTableColumn}
                resizeNativeTable={resizeNativeTable}
                resizeNativeBand={resizeNativeBand}
                selection={selection}
                setSelection={setSelection}
                showGrid={showGrid}
                showRulers={showRulers}
                snapEnabled={snapEnabled}
                valueMode={canvasValueMode}
                zoom={zoom}
              />
            ) : (
              <DesignCanvas
              addFieldToBand={addFieldToBand}
              datasets={activeDocument.datasets}
              document={activeDocument}
              moveField={moveField}
              reorderColumn={reorderColumn}
              resizeColumn={resizeColumn}
              orientation={activeDocument.page.orientation}
              pageSize={activeDocument.page.size}
              payload={previewPayload}
              selection={selection}
              setSelection={setSelection}
              showGrid={showGrid}
              showRulers={showRulers}
              stylesRecord={activeDocument.styles}
              valueMode={canvasValueMode}
              zoom={zoom}
              />
            )
          )}
          {(mode === "preview" || useBusinessData) && (
            <div className={styles["designer-preview"]} hidden={mode !== "preview"}>
              {useBusinessData ? (
                <>
                  <NativeBusinessDataControls api={canManageDefinitions ? api : null} key={businessContext} definitionId={loadedDefinition?.definitionId || ""} definitionVersion={loadedDefinition?.version} data={activeDocument.data} reportType={activeDocument.reportType} requireInvoice={asRecord(activeDocument.requiredFilters).invoice === true} ready={businessReady} onResult={acceptBusinessExecution} onAccessDenied={restrictAccess} />
                  {mode === "preview" && businessPayload && <ReportsV3Preview api={api} payload={businessPayload} />}
                  {!businessPayload && <p className={styles["designer-empty-note"]}>No current Business Data result. Configure sources, save, select filters and execute.</p>}
                </>
              ) : (
                <>
                {formulaSession ? <InternalTypedPreview payload={previewPayload} parameterValues={parameterValues} /> : internalSession ? <InternalConditionalPreview payload={previewPayload} /> : <><SampleDataBanner notice={designSampleNotice} /><ReportsV3Preview api={null} payload={previewPayload} /></>}
                </>
              )}
            </div>
          )}
          {mode === "json" && (
            <div className={styles["designer-editor-pane"]}>
              <div className={styles["designer-json-header"]}>
                <div>
                  <span className={styles["designer-label"]}>Report JSON</span>
                  <p className={styles["designer-json-help"]}>Edit the full report definition, then Apply JSON to update the designer document.</p>
                </div>
                <div className={styles["designer-json-actions"]}>
                  <label className={styles["designer-json-search"]}>
                    <span>Search JSON</span>
                    <input value={jsonSearch} onChange={(event) => setJsonSearch(event.target.value)} placeholder="Find text..." />
                  </label>
                  <button className={styles["designer-tool-btn"]} type="button" onClick={applyJson} disabled={Boolean(jsonError) || !jsonDraftDirty}>Apply JSON</button>
                  <button className={styles["designer-tool-btn"]} type="button" onClick={revertJson} disabled={!jsonDraftDirty}>Revert</button>
                </div>
              </div>
              <div className={styles["designer-json-meta"]}>
                <span>{jsonLineCount.toLocaleString()} lines</span>
                <span>{jsonDraftDirty ? "Unapplied edits" : "In sync with document"}</span>
                {jsonSearch.trim() && <span>{jsonSearchMatches.toLocaleString()} matches</span>}
              </div>
              <div className={styles["designer-json-editor-shell"]}>
                <pre className={styles["designer-json-line-numbers"]} aria-hidden="true">
                  {Array.from({ length: jsonLineCount }, (_, index) => index + 1).join("\n")}
                </pre>
                <textarea
                  aria-label="Report JSON"
                  disabled={saveStatus === "saving" || saveStatus === "loading"}
                  className={styles["designer-textarea"]}
                  spellCheck={false}
                  value={jsonText}
                  onChange={(event) => updateJson(event.target.value)}
                />
              </div>
              {jsonError ? <p className={styles["designer-error"]}>{jsonError}</p> : <p className={styles["designer-json-valid"]}>JSON parses successfully. Apply to update Preview, validation, undo/redo, and export payloads.</p>}
            </div>
          )}
          {mode === "validate" && (
            <ValidationPanel messages={validationMessages} />
          )}
        </main>
        {mode === "design" && !focusMode && !collapsedPanels.right && <div className={styles["designer-resize-handle"]} role="separator" aria-label="Resize properties inspector" onMouseDown={(event) => startPanelResize("right", event)} />}

        {mode === "design" && !focusMode && !collapsedPanels.right && (
        <PropertiesInspector
          formulaTools={formulaSession ? <PropertySection title="Formula Builder">
            <p>Internal presentation only. Text uses its declared band scope; table formulas use row scope. No API persistence.</p>
            <InternalFormulaValueControls payload={payload} selection={selection} onMode={binding => commit("Changed presentation value mode", document => commands.setInternalValueModeCommand(document, selection, binding))} />
            {(isFormulaTarget(selection) && !(templateMode && (["amount", "lineExpenses"].includes(selection.columnId) || formulaColumn(payload, selection.columnId).binding === selection.columnId))) || formulaTextTarget(payload, selection) ? <button className={styles["designer-tool-btn"]} type="button" onClick={() => { setToast(null); setFormulaEdit({ selection, source: activeDocument }); }}>Open Formula Builder</button> : <p>Select a presentation column or direct text element. Source-named columns cannot become calculated declarations; authoritative financial values remain protected.</p>}
          </PropertySection> : onOpenFormulaWorkspace ? <PropertySection title="Internal formula authoring"><p>Explore typed expressions in a separate controlled workspace. This report remains unchanged.</p><button className={styles["designer-tool-btn"]} type="button" onClick={onOpenFormulaWorkspace}>Open internal formula workspace</button></PropertySection> : undefined}
          onConditionalCommand={internalSession && !formulaSession ? (targetKey, action) => {
            const source = activeDocumentRef.current;
            const result = commands.applyConditionalRuleCommand(source, selection, targetKey, action);
            commit("Updated conditional rules", current => current === source ? result : commands.applyConditionalRuleCommand(current, selection, targetKey, action));
          } : undefined}
          deleteSelection={deleteSelection}
          duplicateSelection={duplicateSelection}
          pageSize={activeDocument.page.size}
          orientation={activeDocument.page.orientation}
          payload={previewPayload}
          selection={selection}
          selectedBand={selectedBand}
          setOrientation={(value) => updatePageSetup("orientation", value)}
          setPageSize={(value) => updatePageSetup("size", value)}
          setSelection={setSelection}
          styleNames={Object.keys(activeDocument.styles)}
          updateColumn={updateColumn}
          updateDefinitionField={updateDefinitionField}
          updateField={updateField}
          updateMetadataField={updateMetadataField}
          updateComponent={updateComponent}
          updateBand={updateBand}
          updateNativeBand={updateNativeBand}
          updateNativeElement={updateNativeElement}
          updateNativeTable={updateNativeTable}
          addNativeCalculatedTableColumn={addNativeCalculatedTableColumn}
          updateNativeTableColumn={updateNativeTableColumn}
          reorderNativeTableColumn={reorderNativeTableColumn}
          addNativeTableSort={addNativeTableSort}
          updateNativeTableSort={updateNativeTableSort}
          removeNativeTableSort={removeNativeTableSort}
          moveNativeTableSort={moveNativeTableSort}
          setNativeTableSortDirection={setNativeTableSortDirection}
          updateNativeTableGroup={updateNativeTableGroup}
          updateNativeTableSummary={updateNativeTableSummary}
          resizeNativeTable={resizeNativeTable}
          fitNativeTableToPrintableWidth={fitNativeTableToPrintableWidth}
          alignNativeElements={alignNativeElements}
          fitNativeBandToContent={fitNativeBandToContent}
          fitNativeContainerToContent={fitNativeContainerToContent}
          reparentNativeElement={reparentNativeElement}
          moveBand={moveBand}
          moveNativeBand={moveNativeBand}
          reorderNativeElement={reorderNativeElement}
          width={rightPanelWidth}
        />
        )}
      </div>
      {formulaEdit && <FormulaBuilder {...formulaEditingContext(payload, formulaEdit.selection)} parameterValues={parameterValues} onClose={() => setFormulaEdit(null)} onApply={(node, remove) => {
        if (activeDocumentRef.current !== formulaEdit.source) throw new Error("The document changed while the builder was open. Reopen the formula before applying.");
        const result = commands.applyInternalFormulaCommand(formulaEdit.source, formulaEdit.selection, node, remove);
        commit(remove ? "Removed internal formula" : "Applied internal formula", () => result);
        setToast({ type: "success", message: "Formula workspace updated in memory only." });
      }} />}
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
    </div>
  );
}

function DesignerToolbar({
  internalAuthoring = false,
  canRedo,
  canUndo,
  collapsedPanels,
  createNewDefinition,
  createNewNativeDefinition,
  deleteSelection,
  definitionSource,
  definitions,
  dirty,
  downloadJson,
  duplicateSelection,
  duplicateBackendDefinition,
  exportPayload,
  canSave,
  canManageDefinitions,
  definitionsLoading,
  savedToBackend,
  canExportExcel,
  canExportPdf,
  pdfExportRestriction,
  excelExportRestriction,
  nativeDataMode,
  setNativeDataMode,
  fitCanvas,
  focusMode,
  alignNativeElements,
  mode,
  sampleId,
  designSampleFixtureId,
  designSampleNotice,
  designSampleOptions,
  designSampleRecordCount,
  loadDefinition,
  loadBackendDefinition,
  openJsonEditor,
  refreshDefinitions,
  reportName,
  redo,
  saveDefinition,
  saveStatus,
  selectedDefinitionId,
  selection,
  canvasValueMode,
  setCanvasValueMode,
  setDesignSampleFixtureId,
  setDesignSampleRecordCount,
  setDefinitionSource,
  setFocusMode,
  setMode,
  setShowGrid,
  setShowRulers,
  setSnapEnabled,
  setZoom,
  zoomPreference,
  showGrid,
  showRulers,
  snapEnabled,
  togglePanel,
  undo,
  validateWithBackend,
  zoom,
}: {
  internalAuthoring?: boolean;
  canRedo: boolean;
  canUndo: boolean;
  collapsedPanels: Record<PanelSide, boolean>;
  createNewDefinition: () => void;
  createNewNativeDefinition: () => void;
  deleteSelection: () => void;
  definitionSource: DefinitionSource;
  definitions: ReportDefinitionSummaryV3[];
  dirty: boolean;
  downloadJson: () => void;
  duplicateSelection: () => void;
  duplicateBackendDefinition: () => void;
  exportPayload: (kind: "pdf" | "excel") => void;
  canSave: boolean;
  canManageDefinitions: boolean;
  definitionsLoading: boolean;
  savedToBackend: boolean;
  canExportExcel: boolean;
  canExportPdf: boolean;
  pdfExportRestriction?: string;
  excelExportRestriction?: string;
  nativeDataMode?: NativeDataMode;
  setNativeDataMode: (mode: NativeDataMode) => void;
  fitCanvas: (mode: FitMode) => void;
  focusMode: boolean;
  alignNativeElements: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, alignment: Parameters<typeof commands.alignNativeElements>[2]) => void;
  mode: DesignerMode;
  sampleId: string;
  designSampleFixtureId: DesignSampleFixtureId;
  designSampleNotice: string;
  designSampleOptions: DesignSampleOption[];
  designSampleRecordCount: number;
  loadDefinition: (sampleId: string) => void;
  loadBackendDefinition: (definitionId: string) => void;
  openJsonEditor: () => void;
  refreshDefinitions: () => Promise<ReportDefinitionSummaryV3[] | undefined>;
  reportName: string;
  redo: () => void;
  saveDefinition: (options?: { saveAs?: boolean }) => void;
  saveStatus: SaveStatus;
  selectedDefinitionId: string;
  selection: DesignerSelection;
  canvasValueMode: CanvasValueMode;
  setCanvasValueMode: (mode: CanvasValueMode) => void;
  setDesignSampleFixtureId: (fixtureId: DesignSampleFixtureId) => void;
  setDesignSampleRecordCount: (count: number) => void;
  setDefinitionSource: (source: DefinitionSource) => void;
  setFocusMode: (enabled: boolean) => void;
  setMode: (mode: DesignerMode) => void;
  setShowGrid: (value: boolean) => void;
  setShowRulers: (value: boolean) => void;
  setSnapEnabled: (value: boolean) => void;
  setZoom: (value: number) => void;
  zoomPreference: ZoomPreference;
  showGrid: boolean;
  showRulers: boolean;
  snapEnabled: boolean;
  togglePanel: (side: PanelSide) => void;
  undo: () => void;
  validateWithBackend: () => void;
  zoom: number;
}) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const zoomValue = zoomPreference === "fit-width" || zoomPreference === "fit-page" ? zoomPreference : String(zoom);
  function handleZoomChange(value: string) {
    if (value === "fit-width") {
      fitCanvas("width");
      return;
    }
    if (value === "fit-page") {
      fitCanvas("page");
      return;
    }
    setZoom(Number(value));
  }

  useEffect(() => {
    setOpenMenu(null);
  }, [mode, sampleId, selectedDefinitionId]);

  return (
    <header className={styles["designer-toolbar"]}>
      <div className={styles["designer-toolbar-row"]}>
        <div className={styles["designer-document-title"]}>
          <strong>{reportName}</strong>
          <span>{internalAuthoring ? "In-memory fixture - not persisted" : dirty ? "Unsaved changes" : savedToBackend ? "Saved to server" : "Saved locally"}</span>
        </div>
        <div className={styles["designer-toolbar-section"]} aria-label="Document actions">
          <span className={styles["designer-toolbar-label"]}>Document</span>
          {internalAuthoring ? <span>Internal fixture</span> : definitionSource === "local" ? (
            <select aria-label="Loaded definition" disabled={saveStatus === "saving" || saveStatus === "loading"} className={styles["designer-definition-select"]} value={sampleId} onChange={(event) => loadDefinition(event.target.value)}>
              {DESIGNER_SAMPLES.map((sample) => <option key={sample.id} value={sample.id}>{sample.name}</option>)}
            </select>
          ) : (
            <select aria-label="Saved definition" disabled={definitionsLoading || saveStatus === "saving" || saveStatus === "loading"} className={styles["designer-definition-select"]} value={selectedDefinitionId} onChange={(event) => loadBackendDefinition(event.target.value)}>
              <option value="">Select definition...</option>
              {definitions.map((definition) => (
                <option key={definition.definitionId || definitionDisplayName(definition)} value={definition.definitionId || ""}>{definitionDisplayName(definition)}{definition.isActive === false ? " (inactive)" : ""}</option>
              ))}
            </select>
          )}
          <button className={`${styles["designer-tool-btn"]} ${styles["designer-tool-btn-primary"]}`} disabled={!canSave || saveStatus === "saving" || saveStatus === "loading"} title={canSave ? "Save report definition" : "Saving is unavailable. Resolve any conflict or use Save As for a read-only definition."} type="button" onClick={() => saveDefinition()}>Save</button>
          <button aria-label="Undo" className={styles["designer-tool-btn"]} disabled={!canUndo} title="Undo last designer change" type="button" onClick={undo}>↶</button>
          <button aria-label="Redo" className={styles["designer-tool-btn"]} disabled={!canRedo} title="Redo last undone designer change" type="button" onClick={redo}>↷</button>
          <ToolbarMenu id="document" label="More" ariaLabel="Document menu" openMenu={openMenu} setOpenMenu={setOpenMenu} width={260} controls={<>
              <ToolbarMenuGroupLabel>Definition</ToolbarMenuGroupLabel>
              <label className={styles["designer-menu-field"]}>
                <span className={styles["designer-menu-label"]}>Source</span>
                <select aria-label="Definition source" disabled={internalAuthoring} className={styles["designer-menu-select"]} value={definitionSource} onChange={(event) => setDefinitionSource(event.target.value as DefinitionSource)}>
                  <option value="local">Local samples</option>
                  <option value="backend">Saved definitions</option>
                </select>
              </label>
              <ToolbarMenuSeparator />
              </>}>
            {(close) => (
              <>
              <ToolbarMenuGroupLabel>Create</ToolbarMenuGroupLabel>
              <ToolbarMenuItem close={close} disabled={internalAuthoring} icon="+" label="New report" onClick={createNewDefinition} />
              <ToolbarMenuItem close={close} disabled={internalAuthoring} icon="N" label="New native report" onClick={createNewNativeDefinition} />
              <ToolbarMenuSeparator />
              <ToolbarMenuGroupLabel>Edit</ToolbarMenuGroupLabel>
              <ToolbarMenuItem close={close} disabled={selection.type === "report"} icon="D" label="Duplicate selected item" title="Duplicate selected item" onClick={duplicateSelection} />
              <ToolbarMenuItem close={close} danger disabled={selection.type === "report"} icon="-" label="Delete selected item" title="Delete selected item" onClick={deleteSelection} />
              {selection.type === "nativeElement" && selection.elementIds.length > 1 && (
                <div className={styles["designer-menu-grid"]}>
                  <ToolbarMenuItem close={close} icon="L" label="Align left" onClick={() => alignNativeElements(selection, "left")} />
                  <ToolbarMenuItem close={close} icon="C" label="Align center" onClick={() => alignNativeElements(selection, "hCenter")} />
                  <ToolbarMenuItem close={close} icon="R" label="Align right" onClick={() => alignNativeElements(selection, "right")} />
                </div>
              )}
              <ToolbarMenuSeparator />
              <ToolbarMenuGroupLabel>Saved definitions</ToolbarMenuGroupLabel>
              {definitionSource === "backend" && <ToolbarMenuItem close={close} icon="R" label="Refresh saved definitions" onClick={() => { void refreshDefinitions(); }} />}
              <ToolbarMenuItem close={close} disabled={!canManageDefinitions || saveStatus === "saving" || saveStatus === "loading"} icon="S" label="Save As" onClick={() => saveDefinition({ saveAs: true })} />
              <ToolbarMenuItem close={close} disabled={!canManageDefinitions || saveStatus === "saving" || saveStatus === "loading"} icon="C" label="Duplicate saved definition" onClick={duplicateBackendDefinition} />
              </>
            )}
          </ToolbarMenu>
        </div>
        <div className={styles["designer-toolbar-section"]} aria-label="Mode">
          <span className={styles["designer-toolbar-label"]}>Mode</span>
          <div className={styles["designer-segment"]} aria-label="Designer mode">
            {(["design", "preview"] as DesignerMode[]).map((item) => (
              <button className={mode === item ? styles["designer-segment-active"] : ""} key={item} type="button" onClick={() => setMode(item)}>
                {titleize(item)}
              </button>
            ))}
          </div>
        </div>
        <div className={styles["designer-toolbar-section"]} aria-label="Zoom">
          <span className={styles["designer-toolbar-label"]}>Zoom</span>
          <select aria-label="Zoom" className={styles["designer-compact-select"]} value={zoomValue} onChange={(event) => handleZoomChange(event.target.value)}>
            <option value="fit-width">Fit to Width</option>
            <option value="fit-page">Fit to Page</option>
            {!ZOOM_PRESETS.includes(zoom) && zoomPreference === "manual" && <option value={zoom}>{zoom}%</option>}
            {ZOOM_PRESETS.map((preset) => <option key={preset} value={preset}>{preset}%</option>)}
          </select>
        </div>
        <ToolbarMenu id="view" label="View" ariaLabel="View menu" openMenu={openMenu} setOpenMenu={setOpenMenu} width={250}>
          {(close) => (
            <>
            <ToolbarMenuGroupLabel>Panels</ToolbarMenuGroupLabel>
            <ToolbarCheckItem checked={!collapsedPanels.left} close={close} label="Explorer panel" onClick={() => togglePanel("left")} />
            <ToolbarCheckItem checked={!collapsedPanels.right} close={close} label="Inspector panel" onClick={() => togglePanel("right")} />
            <ToolbarMenuSeparator />
            <ToolbarMenuGroupLabel>Canvas</ToolbarMenuGroupLabel>
            <ToolbarCheckItem checked={showGrid} close={close} label="Grid" onClick={() => setShowGrid(!showGrid)} />
            <ToolbarCheckItem checked={showRulers} close={close} label="Rulers" onClick={() => setShowRulers(!showRulers)} />
            <ToolbarCheckItem checked={snapEnabled} close={close} label="Snap" onClick={() => setSnapEnabled(!snapEnabled)} />
            <ToolbarMenuSeparator />
            <ToolbarMenuItem close={close} icon="F" label={focusMode ? "Exit Focus mode" : "Focus mode"} onClick={() => setFocusMode(!focusMode)} title="Maximize canvas workspace" />
            <span className={styles["designer-menu-note"]}>Margin guides are shown from the printable area and do not change report geometry.</span>
            </>
          )}
        </ToolbarMenu>
        {nativeDataMode && (
          <label className={styles["designer-toolbar-section"]}>
            <span className={styles["designer-toolbar-label"]}>Preview data</span>
            <select aria-label="Preview data mode" className={styles["designer-compact-select"]} value={nativeDataMode} onChange={(event) => setNativeDataMode(event.target.value as NativeDataMode)}>
              <option value="sample">Sample Data</option>
              <option value="business">Business Data</option>
            </select>
          </label>
        )}
        <ToolbarMenu id="sample-data" label="Sample data" ariaLabel="Sample data menu" openMenu={openMenu} setOpenMenu={setOpenMenu} variant="popover" width={300}>
          {(close) => (
            <>
            <div className={styles["designer-menu-heading"]}>
              <strong>Sample data</strong>
              <span>{nativeDataMode === "business" ? "Not used by Business Data Preview or PDF." : "Editor-only preview inputs; not actual financial records."}</span>
            </div>
            <label className={styles["designer-menu-field"]}>
              <span className={styles["designer-menu-label"]}>Fixture</span>
              <select aria-label="Design-time sample fixture" disabled={internalAuthoring} className={styles["designer-menu-select"]} value={designSampleFixtureId} onChange={(event) => setDesignSampleFixtureId(event.target.value as DesignSampleFixtureId)}>
                {designSampleOptions.map((option) => (
                  <option disabled={!option.compatible} key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className={styles["designer-menu-field"]}>
              <span className={styles["designer-menu-label"]}>Repeating records</span>
              <select aria-label="Sample record count" disabled={internalAuthoring} className={styles["designer-menu-select"]} value={designSampleRecordCount} onChange={(event) => setDesignSampleRecordCount(Number(event.target.value))}>
                {DESIGN_SAMPLE_RECORD_COUNTS.map((count) => <option key={count} value={count}>{count}</option>)}
              </select>
            </label>
            <label className={styles["designer-menu-field"]}>
              <span className={styles["designer-menu-label"]}>Canvas values</span>
              <select aria-label="Canvas value display" className={styles["designer-menu-select"]} value={canvasValueMode} onChange={(event) => setCanvasValueMode(event.target.value as CanvasValueMode)}>
                <option value="sample">Sample values</option>
                <option value="binding">Bindings</option>
                <option value="both">Sample + binding</option>
              </select>
            </label>
            <p className={styles["designer-sample-indicator"]}>{internalAuthoring ? "Controlled fixture inputs only. Use the internal fixture-state selector; normal sample generation is disabled." : designSampleNotice}</p>
            </>
          )}
        </ToolbarMenu>
        <div className={styles["designer-toolbar-spacer"]} />
        <ToolbarMenu id="export" label="Export" ariaLabel="Export menu" openMenu={openMenu} setOpenMenu={setOpenMenu} width={250}>
          {(close) => (
            <>
            <ToolbarMenuItem close={close} disabled={!canExportPdf} icon="P" label="Export PDF" description={internalAuthoring ? "Internal workspaces are memory-only." : pdfExportRestriction || (canExportPdf ? undefined : "Execute Business Data using the current saved report and filters first.")} onClick={() => exportPayload("pdf")} title="Export a PDF using the current preview data" />
            <ToolbarMenuItem close={close} disabled={!canExportExcel} icon="X" label="Export Excel" description={internalAuthoring ? "Internal workspaces are memory-only." : excelExportRestriction || (canExportExcel ? undefined : "Native Excel requires a supported flow-table definition and current execution data.")} onClick={() => exportPayload("excel")} title={canExportExcel ? "Export Excel workbook" : excelExportRestriction || "Native Excel requires a supported flow-table definition and current execution data."} />
            <ToolbarMenuItem close={close} disabled={internalAuthoring} icon="J" label="Export JSON" onClick={downloadJson} />
            </>
          )}
        </ToolbarMenu>
        <ToolbarMenu id="advanced" label="Advanced" ariaLabel="Advanced" openMenu={openMenu} setOpenMenu={setOpenMenu} width={240}>
          {(close) => (
            <>
            <ToolbarMenuItem close={close} disabled={!canManageDefinitions} icon="V" label="Validate API" onClick={validateWithBackend} />
            <ToolbarMenuItem close={close} icon="!" label="Validation panel" onClick={() => setMode("validate")} />
            <ToolbarMenuItem close={close} icon="{" label="JSON editor" onClick={openJsonEditor} />
            </>
          )}
        </ToolbarMenu>
      </div>
    </header>
  );
}

function ToolbarMenu({
  ariaLabel,
  children,
  controls,
  id,
  label,
  openMenu,
  setOpenMenu,
  variant = "menu",
  width = 240,
}: {
  ariaLabel?: string;
  children: (close: () => void) => ReactNode;
  controls?: ReactNode;
  id: string;
  label: string;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
  variant?: "menu" | "popover";
  width?: number;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, ready: false });
  const open = openMenu === id;
  const menuId = `designer-toolbar-menu-${id}`;
  const close = useCallback(() => {
    setOpenMenu(null);
    window.requestAnimationFrame(() => buttonRef.current?.focus());
  }, [setOpenMenu]);
  const updatePosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const viewportPadding = 10;
    const measuredWidth = Math.max(width, menuRef.current?.offsetWidth || 0);
    const measuredHeight = menuRef.current?.offsetHeight || 0;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const top = measuredHeight > 0 && measuredHeight > spaceBelow && rect.top > spaceBelow
      ? Math.max(viewportPadding, rect.top - measuredHeight - gap)
      : Math.min(window.innerHeight - viewportPadding, rect.bottom + gap);
    const left = Math.min(
      Math.max(viewportPadding, rect.right - measuredWidth),
      Math.max(viewportPadding, window.innerWidth - measuredWidth - viewportPadding),
    );
    setPosition({ top, left, ready: true });
  }, [width]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpenMenu(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (isEditableTarget(event.target)) return;
      const focusables = menuFocusableElements(menuRef.current);
      if (!focusables.length) return;
      event.preventDefault();
      const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);
      const offset = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = currentIndex < 0 ? 0 : (currentIndex + offset + focusables.length) % focusables.length;
      focusables[nextIndex]?.focus();
    };
    const handleReposition = () => updatePosition();
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [close, open, setOpenMenu, updatePosition]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || variant !== "menu") return undefined;
    const frame = window.requestAnimationFrame(() => menuFocusableElements(menuRef.current)[0]?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open, variant]);

  return (
    <>
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup={variant === "menu" ? "menu" : undefined}
        aria-label={ariaLabel}
        className={styles["designer-toolbar-menu-trigger"]}
        onClick={() => setOpenMenu(open ? null : id)}
        ref={buttonRef}
        type="button"
      >
        {label}
      </button>
      {open && createPortal(
        <div
          className={`${styles["designer-toolbar-popover"]} ${variant === "popover" ? styles["designer-toolbar-popover--settings"] : ""}`}
          id={menuId}
          ref={menuRef}
          role={variant === "popover" ? "group" : undefined}
          aria-label={variant === "popover" ? `${label} settings` : undefined}
          style={{ left: position.left, minWidth: width, top: position.top, visibility: position.ready ? "visible" : "hidden" }}
        >
          {controls}
          {variant === "menu" ? <div role="menu" aria-label={`${label} actions`}>{children(close)}</div> : children(close)}
        </div>,
        document.body,
      )}
    </>
  );
}

function menuFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>("button:not(:disabled), select:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex='-1'])"));
}

function ToolbarMenuItem({
  close,
  danger = false,
  description,
  disabled,
  icon,
  label,
  onClick,
  title,
}: {
  close: () => void;
  danger?: boolean;
  description?: string;
  disabled?: boolean;
  icon: string;
  label: string;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      className={`${styles["designer-menu-item"]} ${danger ? styles["designer-menu-item--danger"] : ""}`}
      disabled={disabled}
      role="menuitem"
      title={title || description || label}
      type="button"
      onClick={() => {
        onClick();
        close();
      }}
    >
      <span className={styles["designer-menu-item-icon"]} aria-hidden="true">{icon}</span>
      <span className={styles["designer-menu-item-copy"]}>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
    </button>
  );
}

function ToolbarCheckItem({ checked, close, label, onClick }: { checked: boolean; close: () => void; label: string; onClick: () => void }) {
  return (
    <button
      aria-checked={checked}
      className={`${styles["designer-menu-item"]} ${checked ? styles["designer-menu-item--checked"] : ""}`}
      role="menuitemcheckbox"
      type="button"
      onClick={() => {
        onClick();
        close();
      }}
    >
      <span className={styles["designer-menu-item-icon"]} aria-hidden="true">{checked ? "✓" : ""}</span>
      <span className={styles["designer-menu-item-copy"]}><strong>{label}</strong></span>
    </button>
  );
}

function ToolbarMenuGroupLabel({ children }: { children: ReactNode }) {
  return <span className={styles["designer-menu-group-label"]}>{children}</span>;
}

function ToolbarMenuSeparator() {
  return <span className={styles["designer-menu-separator"]} aria-hidden="true" />;
}

function PanelTabs({ active, setActive }: { active: ExplorerTab; setActive: (tab: ExplorerTab) => void }) {
  return (
    <div className={styles["designer-panel-tabs"]}>
      {(["structure", "data", "toolbox"] as ExplorerTab[]).map((tab) => (
        <button className={active === tab ? styles["designer-panel-tab-active"] : ""} key={tab} type="button" onClick={() => setActive(tab)}>
          {titleize(tab)}
        </button>
      ))}
    </div>
  );
}

function ExplorerPanelIntro({ isNative, tab }: { isNative: boolean; tab: ExplorerTab }) {
  const content = {
    structure: {
      title: "Structure Explorer",
      description: isNative ? "Navigate bands, containers, images, fields, and columns." : "Navigate report sections, fields, and columns.",
    },
    data: {
      title: "Data Explorer",
      description: "Search datasets and drag fields into the selected report surface.",
    },
    toolbox: {
      title: "Toolbox",
      description: isNative ? "Insert native bands and positioned report elements." : "Insert structured report sections.",
    },
  }[tab];
  return (
    <div className={styles["designer-panel-intro"]}>
      <strong>{content.title}</strong>
      <span>{content.description}</span>
    </div>
  );
}

function SampleDataBanner({ notice }: { notice: string }) {
  return (
    <div className={styles["designer-sample-banner"]} role="status">
      {notice} Sample data is editor-only and is not included in exported report JSON.
    </div>
  );
}

function editableSamplePayload(source: FormattedInvoicePayload): FormattedInvoicePayload {
  const payload = clonePayload(source);
  if (isNativeReportsV31Payload(payload)) payload.datasets = {};
  return payload;
}

function StructureBranch({ label, control, children }: { label: string; control: ReactNode; children: ReactNode }) {
  const [expanded, setExpanded] = useState(true);
  const id = `designerStructure${useId().replace(/:/g, "")}`;
  return <div className={styles["designer-tree-node"]} data-structure-branch>
    <div className={styles["designer-tree-branch-row"]}>
      <button type="button" data-structure-toggle aria-expanded={expanded} aria-controls={id} aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`} onClick={() => setExpanded(!expanded)} className={styles["designer-tree-toggle"]}>{expanded ? "-" : "+"}</button>
      {control}
    </div>
    <div id={id} hidden={!expanded} className={styles["designer-tree-children"]}>{children}</div>
  </div>;
}

function StructureTree({ bands, isNative, moveNativeTableColumn, nativeBands, selection, setSelection }: { bands: DesignerBand[]; isNative: boolean; moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void; nativeBands: JsonRecord[]; selection: DesignerSelection; setSelection: (selection: DesignerSelection) => void }) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const nativeVisibleBands = normalizedQuery
    ? nativeBands.filter((band) => nativeBandMatches(asRecord(band), normalizedQuery))
    : nativeBands;
  const visibleBands = normalizedQuery
    ? bands.filter((band) => legacyBandMatches(band, normalizedQuery))
    : bands;
  return (
    <div className={styles["designer-tree"]} onKeyDown={handleTreeKeyDown}>
      <label className={styles["designer-panel-search"]}>
        <span className={styles["designer-label"]}>Search elements</span>
        <input className={styles["designer-input"]} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search elements..." />
      </label>
      <div role="region" aria-label="Report structure">
      <button aria-pressed={selection.type === "report"} className={`${styles["designer-tree-item"]} ${selection.type === "report" ? styles["designer-tree-item-active"] : ""}`} data-structure-item title="Report document" type="button" onClick={() => setSelection({ type: "report" })}>
        <span className={styles["designer-tree-icon"]} aria-hidden="true">▣</span>
        <span className={styles["designer-tree-label"]}><strong>Document</strong><small>report definition</small></span>
      </button>
      {isNative && nativeVisibleBands.map((band) => (
        <StructureBranch key={String(band.id)} label={nativeBandLabel(String(band.role))} control={<button type="button" aria-pressed={selection.type === "band" && selection.bandId === band.id} className={`${styles["designer-tree-item"]} ${selection.type === "band" && selection.bandId === band.id ? styles["designer-tree-item-active"] : ""}`} data-structure-item title={`${nativeBandLabel(String(band.role))} — ${nativeBandDescription(String(band.role))} · ${String(band.id)}`} onClick={() => setSelection({ type: "band", bandId: String(band.id) })}>
              <span className={styles["designer-tree-icon"]} aria-hidden="true">▤</span>
              <span className={styles["designer-tree-label"]}><strong>{nativeBandLabel(String(band.role))}</strong><small>{nativeBandDescription(String(band.role))} · {String(band.id)}</small></span>
              <span className={styles["designer-tree-hover-action"]}>Band</span>
          </button>}>
            {asArray(band.elements).map((elementValue) => (
              <NativeStructureNode
                bandId={String(band.id)}
                element={asRecord(elementValue)}
                key={String(asRecord(elementValue).id)}
                selection={selection}
                setSelection={setSelection}
                moveNativeTableColumn={moveNativeTableColumn}
              />
            ))}
        </StructureBranch>
      ))}
      {!isNative && visibleBands.map((band) => {
        const component = primaryComponent(band);
        const fields = component?.kind === "keyValueGrid" ? component.fields : [];
        const columns = component?.kind === "table" ? component.columns : [];
        const isBandSelected = selection.type === "band" && selection.bandId === band.id;
        return (
          <StructureBranch key={band.id} label={legacyBandLabel(band)} control={<button type="button" aria-pressed={isBandSelected} className={`${styles["designer-tree-item"]} ${isBandSelected ? styles["designer-tree-item-active"] : ""}`} data-structure-item title={`${legacyBandLabel(band)} · ${band.legacyId || band.id}`} onClick={() => setSelection({ type: "band", bandId: band.id })}>
                  <span className={styles["designer-tree-icon"]} aria-hidden="true">▤</span>
                  <span className={styles["designer-tree-label"]}><strong>{legacyBandLabel(band)}</strong><small>{componentLabel(component)}</small></span>
                  <span className={styles["designer-tree-hover-action"]}>Band</span>
              </button>}>
              {component && (
                <button
                  className={`${styles["designer-tree-item"]} ${selection.type === "component" && selection.componentId === component.id ? styles["designer-tree-item-active"] : ""}`}
                  aria-pressed={selection.type === "component" && selection.componentId === component.id}
                  data-structure-item
                  title={`${componentLabel(component)} · ${component.id}`}
                  type="button"
                  onClick={() => setSelection({ type: "component", bandId: band.id, componentId: component.id })}
                >
                  <span className={styles["designer-tree-icon"]} aria-hidden="true">{component.kind === "table" ? "▦" : component.kind === "keyValueGrid" ? "≡" : "¶"}</span>
                  <span className={styles["designer-tree-label"]}><strong>{componentLabel(component)}</strong><small>{component.id}</small></span>
                  <span className={styles["designer-tree-hover-action"]}>Edit</span>
                </button>
              )}
              {fields.map((field) => (
                <button
                  className={`${styles["designer-tree-item"]} ${selection.type === "field" && selection.fieldId === field.id ? styles["designer-tree-item-active"] : ""}`}
                  aria-pressed={selection.type === "field" && selection.fieldId === field.id}
                  key={field.id}
                  data-structure-item
                  title={`${field.label || field.key || field.id}${field.key ? ` · ${field.key}` : ""}`}
                  type="button"
                  onClick={() => component && setSelection({ type: "field", bandId: band.id, componentId: component.id, fieldId: field.id })}
                >
                  <span className={styles["designer-tree-icon"]} aria-hidden="true">◇</span>
                  <span className={styles["designer-tree-label"]}><strong>{field.label || humanizeIdentifier(field.key || field.id)}</strong><small>{field.key || "unbound"}</small></span>
                  <span className={styles["designer-tree-hover-action"]}>Edit</span>
                </button>
              ))}
              {columns.map((column) => (
                <button
                  className={`${styles["designer-tree-item"]} ${selection.type === "column" && selection.columnId === column.id ? styles["designer-tree-item-active"] : ""}`}
                  aria-pressed={selection.type === "column" && selection.columnId === column.id}
                  key={column.id}
                  data-structure-item
                  title={`${column.label || column.key || column.id}${column.key ? ` · ${column.key}` : ""}`}
                  type="button"
                  onClick={() => component && setSelection({ type: "column", bandId: band.id, componentId: component.id, columnId: column.id })}
                >
                  <span className={styles["designer-tree-icon"]} aria-hidden="true">▥</span>
                  <span className={styles["designer-tree-label"]}><strong>{column.label || humanizeIdentifier(column.key || column.id)}</strong><small>{column.key || "unbound"}</small></span>
                  <span className={styles["designer-tree-hover-action"]}>Edit</span>
                </button>
              ))}
          </StructureBranch>
        );
      })}
      </div>
      {((isNative && !nativeVisibleBands.length) || (!isNative && !visibleBands.length)) && <p className={styles["designer-empty-note"]}>No matching elements.</p>}
    </div>
  );
}

function NativeStructureNode({ bandId, element, moveNativeTableColumn, selection, setSelection }: { bandId: string; element: JsonRecord; moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void; selection: DesignerSelection; setSelection: (selection: DesignerSelection) => void }) {
  const elementId = String(element.id || "");
  const type = String(element.type || "element");
  const selected = selection.type === "nativeElement" && selection.elementIds.includes(elementId);
  const children = type === "container" ? asArray(element.elements).map(asRecord) : [];
  const tableColumns = type === "table" ? asArray(element.columns).map(asRecord) : [];
  const tableGroup = type === "table" ? asRecord(asArray(element.groups).map(asRecord)[0]) : {};
  const hasTableGroup = Boolean(Object.keys(tableGroup).length);
  const button = (
    <button
      data-testid={`native-structure-${elementId}`}
      className={`${styles["designer-tree-item"]} ${selected ? styles["designer-tree-item-active"] : ""}`}
      aria-pressed={selected}
      data-structure-item
      title={`${nativeElementLabel(element)} · ${type}`}
      type="button"
      onClick={() => setSelection({ type: "nativeElement", bandId, elementIds: [elementId] })}
    >
      <span className={styles["designer-tree-icon"]} aria-hidden="true">{type === "container" ? "▧" : type === "table" ? "▦" : type === "image" ? "▧" : type === "field" ? "ƒ" : "T"}</span>
      <span className={styles["designer-tree-label"]}><strong>{nativeElementLabel(element)}</strong><small>{type}{Object.keys(asRecord(element.conditions)).length > 0 ? " / Conditional rules" : ""}</small></span>
      <span className={styles["designer-tree-hover-action"]}>Edit</span>
    </button>
  );
  if (!children.length && !tableColumns.length && !hasTableGroup) return button;
  return (
    <StructureBranch label={nativeElementLabel(element)} control={button}>
        {children.map((child) => (
          <NativeStructureNode bandId={bandId} element={child} key={String(child.id)} moveNativeTableColumn={moveNativeTableColumn} selection={selection} setSelection={setSelection} />
        ))}
        {hasTableGroup && (
          <StructureBranch label={`Group: ${groupFieldLabel(tableGroup)}`} control={<button
                aria-pressed={selection.type === "nativeTableGroup" && selection.tableId === elementId}
                className={`${styles["designer-tree-item"]} ${selection.type === "nativeTableGroup" && selection.tableId === elementId ? styles["designer-tree-item-active"] : ""}`}
                onClick={() => setSelection({ type: "nativeTableGroup", bandId, tableId: elementId, region: "group" })}
                data-structure-item
                type="button"
              >
                <span className={styles["designer-tree-icon"]} aria-hidden="true">☰</span>
                <span className={styles["designer-tree-label"]}><strong>Group: {groupFieldLabel(tableGroup)}</strong><small>group order and template regions</small></span>
              </button>}>
              {(["header", "detail", "footer"] as const).map((region) => (
                <button
                  aria-pressed={selection.type === "nativeTableGroup" && selection.tableId === elementId && selection.region === region}
                  className={`${styles["designer-tree-item"]} ${selection.type === "nativeTableGroup" && selection.tableId === elementId && selection.region === region ? styles["designer-tree-item-active"] : ""}`}
                  key={region}
                  onClick={() => setSelection({ type: "nativeTableGroup", bandId, tableId: elementId, region })}
                  data-structure-item
                  type="button"
                >
                  <span className={styles["designer-tree-icon"]} aria-hidden="true">{region === "detail" ? "▥" : "▤"}</span>
                  <span className={styles["designer-tree-label"]}><strong>{region === "header" ? "Group Header" : region === "footer" ? "Group Footer — Subtotal" : "Detail Row Template"}</strong><small>{region === "detail" ? "sorted records render here" : "logical group template"}</small></span>
                </button>
              ))}
          </StructureBranch>
        )}
        {type === "table" && (
          <button
            aria-pressed={selection.type === "nativeTableGroup" && selection.tableId === elementId && selection.region === "tableFooter"}
            className={`${styles["designer-tree-item"]} ${selection.type === "nativeTableGroup" && selection.tableId === elementId && selection.region === "tableFooter" ? styles["designer-tree-item-active"] : ""}`}
            onClick={() => setSelection({ type: "nativeTableGroup", bandId, tableId: elementId, region: "tableFooter" })}
            data-structure-item
            type="button"
          >
            <span className={styles["designer-tree-icon"]} aria-hidden="true">∑</span>
            <span className={styles["designer-tree-label"]}><strong>Table Footer — Summary</strong><small>logical summary template</small></span>
          </button>
        )}
        {tableColumns.map((column, index) => {
          const columnId = String(column.id || "");
          const columnSelected = selection.type === "nativeTableColumn" && selection.bandId === bandId && selection.tableId === elementId && selection.columnId === columnId;
          return (
            <button
              data-testid={`native-structure-column-${columnId}`}
              aria-pressed={columnSelected}
              className={`${styles["designer-tree-item"]} ${columnSelected ? styles["designer-tree-item-active"] : ""}`}
              draggable
              key={columnId}
              onClick={() => setSelection({ type: "nativeTableColumn", bandId, tableId: elementId, columnId })}
              onDragStart={(event) => {
                event.dataTransfer.setData("application/x-landagility-native-column", JSON.stringify({ bandId, tableId: elementId, columnId }));
                event.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                event.currentTarget.classList.add(styles["native-table-column-drop-target"]);
              }}
              onDragLeave={(event) => {
                event.currentTarget.classList.remove(styles["native-table-column-drop-target"]);
              }}
              onDrop={(event) => {
                event.preventDefault();
                event.currentTarget.classList.remove(styles["native-table-column-drop-target"]);
                const raw = event.dataTransfer.getData("application/x-landagility-native-column");
                if (!raw) return;
                const parsed = JSON.parse(raw) as { bandId: string; tableId: string; columnId: string };
                if (parsed.bandId !== bandId || parsed.tableId !== elementId) return;
                moveNativeTableColumn({ type: "nativeTableColumn", bandId, tableId: elementId, columnId: parsed.columnId }, index);
              }}
              data-structure-item
              type="button"
            >
              <span className={styles["designer-tree-icon"]} aria-hidden="true">↔</span>
              <span className={styles["designer-tree-label"]}><strong>{String(column.label || humanizeIdentifier(String(column.binding || column.id)))}</strong><small>{String(column.binding || "unbound")}{Object.keys(asRecord(column.conditions)).length || Object.keys(asRecord(asRecord(column.detail).conditions)).length ? " / Conditional rules" : ""}</small></span>
            </button>
          );
        })}
    </StructureBranch>
  );
}

function nativeElementLabel(element: JsonRecord) {
  const type = String(element.type || "");
  const id = String(element.id || "");
  if (type === "table") return `${humanizeIdentifier(String(element.dataset || "Broker Summary"))}`;
  if (type === "container") {
    if (/invoice.*header/i.test(id)) return "Invoice Header";
    if (/company/i.test(id)) return "Company Information";
    if (/bill.*to/i.test(id)) return "Bill To";
    if (/invoice.*meta|details/i.test(id)) return "Invoice Details";
    return String(element.text || humanizeIdentifier(id) || "Container");
  }
  if (type === "image") {
    const asset = String(element.asset || "");
    if (/company.*logo/i.test(asset) || /company.*logo/i.test(id)) return "Company Logo";
    return asset ? `${humanizeIdentifier(asset)} image` : "Image";
  }
  if (type === "field") return humanizeIdentifier(String(element.binding || element.id || "Field"));
  return String(element.text || humanizeIdentifier(String(element.binding || element.id || "Text")));
}

function groupFieldLabel(group: JsonRecord): string {
  const key = asRecord(group.key);
  return humanizeIdentifier(String(group.field || key.field || group.id || "Group"));
}

function nativeBandMatches(band: JsonRecord, query: string): boolean {
  const label = `${nativeBandLabel(String(band.role))} ${String(band.id || "")}`.toLowerCase();
  return label.includes(query) || asArray(band.elements).some((element) => nativeElementMatches(asRecord(element), query));
}

function nativeElementMatches(element: JsonRecord, query: string): boolean {
  const label = `${nativeElementLabel(element)} ${String(element.id || "")} ${String(element.type || "")} ${String(element.binding || "")} ${String(element.dataset || "")}`.toLowerCase();
  return label.includes(query)
    || asArray(element.elements).some((child) => nativeElementMatches(asRecord(child), query))
    || asArray(element.columns).some((column) => `${String(asRecord(column).label || "")} ${String(asRecord(column).binding || "")}`.toLowerCase().includes(query));
}

function legacyBandMatches(band: DesignerBand, query: string): boolean {
  const component = primaryComponent(band);
  const haystack = [
    legacyBandLabel(band),
    band.id,
    band.legacyId,
    componentLabel(component),
    component?.id,
    ...(component?.kind === "keyValueGrid" ? component.fields.flatMap((field) => [field.label, field.key, field.id]) : []),
    ...(component?.kind === "table" ? component.columns.flatMap((column) => [column.label, column.key, column.id]) : []),
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(query);
}

function DataExplorer({
  updateExecutionSources,
  addFieldToBand,
  addNativeFieldElement,
  addNativeTableColumn,
  moveNativeTableColumn,
  datasets,
  document,
  isNative,
  nativeBands,
  payload,
  selection,
}: {
  updateExecutionSources: (data: JsonRecord) => void;
  addFieldToBand: (bandId: string, datasetName: string, fieldKey: string) => void;
  addNativeFieldElement: (bandId: string, datasetName: string, fieldKey: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addNativeTableColumn: (bandId: string, tableId: string, fieldKey: string, datasetName?: string) => void;
  moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void;
  datasets: JsonRecord;
  document: DesignerDocument;
  isNative: boolean;
  nativeBands: JsonRecord[];
  payload: FormattedInvoicePayload;
  selection: DesignerSelection;
}) {
  const [query, setQuery] = useState("");
  const datasetDescriptors = discoverReportDatasets(payload);
  const nativeTargetBandId = isNative && selection.type !== "report" ? selection.bandId : "";
  const nativeTargetTable = selection.type === "nativeTableColumn"
    ? { bandId: selection.bandId, tableId: selection.tableId }
    : selection.type === "nativeTableGroup"
      ? { bandId: selection.bandId, tableId: selection.tableId }
    : selection.type === "nativeElement"
      ? findSelectedNativeTable(nativeBands, selection)
      : null;
  const nativeTargetElementId = resolveNativeDataInsertionTarget(nativeBands, selection);
  const targetBand = !isNative && selection.type !== "report" ? findBand(document, selection.bandId) : null;
  const targetComponent = primaryComponent(targetBand || undefined);
  const canAddToTarget = isNative ? Boolean((nativeTargetTable || nativeTargetBandId) && nativeBands.some((band) => band.id === nativeTargetBandId || band.id === nativeTargetTable?.bandId)) : targetComponent?.kind === "keyValueGrid" || targetComponent?.kind === "table";
  const normalizedQuery = query.trim().toLowerCase();
  const visibleDatasets = datasetDescriptors
    .map((dataset) => ({
      dataset,
      fields: dataset.fields.filter((field) => !normalizedQuery || `${dataset.label} ${dataset.id} ${field.label} ${field.path}`.toLowerCase().includes(normalizedQuery)),
    }))
    .filter(({ dataset, fields }) => !normalizedQuery || fields.length > 0 || `${dataset.label} ${dataset.id}`.toLowerCase().includes(normalizedQuery));
  return (
    <div className={styles["designer-panel-list"]}>
      {isNative && <NativeInvoiceSourceEditor data={document.data} reportType={document.reportType} onChange={updateExecutionSources} />}
      <label className={styles["designer-panel-search"]}>
        <span className={styles["designer-label"]}>Search fields</span>
        <input className={styles["designer-input"]} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Field name..." />
      </label>
      {visibleDatasets.map(({ dataset, fields }) => (
        <details className={styles["designer-tree-node"]} key={dataset.id} open>
          <summary className={styles["designer-dataset-title"]}>
            <span>{dataset.label}</span>
            <small>{datasetKindLabel(dataset)}{dataset.noSampleData ? " · no sample data" : ""}</small>
          </summary>
          <div className={styles["designer-tree-children"]}>
            {fields
              .map((field) => (
                <div className={styles["designer-data-field-row"]} key={`${dataset.id}-${field.path}`}>
                  <button
                    aria-label={`${field.label} ${field.path} · ${field.type}`}
                    className={styles["designer-data-field"]}
                    draggable
                    title={`${field.label} (${field.type})${field.metadata?.description ? `: ${String(field.metadata.description)}` : ""}`}
                    type="button"
                    onDragStart={(event) => event.dataTransfer.setData("application/x-landagility-field", JSON.stringify({ datasetName: dataset.id, fieldKey: field.path }))}
                  >
                    <span className={styles["designer-data-field-name"]}>{field.label}</span>
                    <small className={styles["designer-data-field-type"]}>{field.type}</small>
                  </button>
                  {canAddToTarget && (targetBand || nativeTargetBandId || nativeTargetTable) && (
                    <button aria-label="Add" className={styles["designer-inline-add"]} title={`Add ${field.label}`} type="button" onClick={() => isNative ? (nativeTargetTable ? addNativeTableColumn(nativeTargetTable.bandId, nativeTargetTable.tableId, field.path, dataset.id) : addNativeFieldElement(nativeTargetBandId, dataset.id, field.path, undefined, nativeTargetElementId)) : targetBand && addFieldToBand(targetBand.id, dataset.id, field.path)}>
                      +
                    </button>
                  )}
                </div>
              ))}
            {!dataset.fields.length && <p className={styles["designer-empty-note"]}>{dataset.noSampleData ? "No sample fields are available for this dataset yet." : "No scalar fields found for this dataset."}</p>}
          </div>
        </details>
      ))}
      {!datasetDescriptors.length && <p className={styles["designer-empty-note"]}>No dataset metadata or sample datasets are embedded in this definition. Use manual bindings only when the backend schema is known.</p>}
    </div>
  );
}

function Toolbox({
  addNativeBand,
  addNativeContainerElement,
  addNativeImageElement,
  addNativeInvoiceHeaderContainer,
  addNativeTableElement,
  addNativeTextElement,
  addSection,
  datasets,
  payload,
  isNative,
  selection,
}: {
  addNativeBand: (role: NativeBandRole) => void;
  addNativeContainerElement: (bandId: string, parentElementId?: string, at?: { x: number; y: number }) => void;
  addNativeImageElement: (bandId: string, asset?: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addNativeInvoiceHeaderContainer: (bandId: string) => void;
  addNativeTableElement: (bandId: string, datasetName?: string, fields?: string[], at?: { x: number; y: number }) => void;
  addNativeTextElement: (bandId: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addSection: (type: "text" | "keyValueGrid" | "table") => void;
  datasets: JsonRecord;
  payload: FormattedInvoicePayload;
  isNative: boolean;
  selection: DesignerSelection;
}) {
  if (isNative) {
    const selectedBandId = selection.type === "band" || selection.type === "nativeElement" || selection.type === "nativeTableColumn" || selection.type === "nativeTableGroup" ? selection.bandId : "";
    const selectedElementId = selection.type === "nativeElement" && selection.elementIds.length === 1 ? selection.elementIds[0] : undefined;
    const defaultDataset = preferredTableDataset(discoverReportDatasets(payload));
    const firstDataset = defaultDataset?.id || "";
    const firstFields = defaultDataset?.fields.slice(0, 4).map((field) => field.path) || [];
    return (
      <div className={styles["designer-toolbox"]}>
        <ToolboxGroup title="Report Bands">
          <ToolboxButton icon="RH" label="Report Header band" description={nativeBandDescription("reportHeader")} onClick={() => addNativeBand("reportHeader")} />
          <ToolboxButton icon="PH" label="Page Header band" description={nativeBandDescription("pageHeader")} onClick={() => addNativeBand("pageHeader")} />
          <ToolboxButton icon="DT" label="Detail band" description={nativeBandDescription("detail")} onClick={() => addNativeBand("detail")} />
          <ToolboxButton icon="RF" label="Report Footer band" description={nativeBandDescription("reportFooter")} onClick={() => addNativeBand("reportFooter")} />
          <ToolboxButton icon="PF" label="Page Footer band" description={nativeBandDescription("pageFooter")} onClick={() => addNativeBand("pageFooter")} />
        </ToolboxGroup>
        <ToolboxGroup title="Text & Data">
          <ToolboxButton disabled={!selectedBandId} icon="T" label="Text element" description="Static labels, headings, or notes." onClick={() => selectedBandId && addNativeTextElement(selectedBandId, undefined, selectedElementId)} title={selectedBandId ? "Insert text into the selected band or container." : "Select a band before inserting text."} />
        </ToolboxGroup>
        <ToolboxGroup title="Images">
          <ToolboxButton disabled={!selectedBandId} icon="▧" label="Image / logo" description="Use a named report asset." onClick={() => selectedBandId && addNativeImageElement(selectedBandId, "companyLogo", undefined, selectedElementId)} title={selectedBandId ? "Insert an image bound to a named report asset." : "Select a band before inserting an image."} />
        </ToolboxGroup>
        <ToolboxGroup title="Containers">
          <ToolboxButton disabled={!selectedBandId} icon="▢" label="Container" description="Group and position related elements." onClick={() => selectedBandId && addNativeContainerElement(selectedBandId, selectedElementId)} title={selectedBandId ? "Insert a layout container." : "Select a band before inserting a container."} />
          <ToolboxButton disabled={!selectedBandId} icon="INV" label="Invoice header container" description="Editable branded invoice header." onClick={() => selectedBandId && addNativeInvoiceHeaderContainer(selectedBandId)} title={selectedBandId ? "Insert the editable native invoice header pattern." : "Select a band before inserting the invoice header."} />
        </ToolboxGroup>
        <ToolboxGroup title="Tables">
          <ToolboxButton disabled={!selectedBandId || Boolean(selectedElementId)} icon="▦" label="Repeating table" description="Flow rows from a collection dataset." onClick={() => selectedBandId && addNativeTableElement(selectedBandId, firstDataset, firstFields)} title={!selectedBandId ? "Select a band before inserting a table." : selectedElementId ? "Native paginated tables must be direct band children." : "Insert a repeating native table using the preferred collection dataset."} />
          <p>Repeating tables are native flow content. Nested paginated tables are not supported.</p>
        </ToolboxGroup>
      </div>
    );
  }
  return (
    <div className={styles["designer-toolbox"]}>
      <ToolboxGroup title="Legacy sections">
        <ToolboxButton icon="T" label="Text band" description="Narrative report section." onClick={() => addSection("text")} />
        <ToolboxButton icon="≡" label="Key-value grid" description="Header-style label/value layout." onClick={() => addSection("keyValueGrid")} />
        <ToolboxButton icon="▦" label="Table" description="Structured dataset table." onClick={() => addSection("table")} />
        <p>Native freeform editing requires a reports-v3.1 definition. Use New Native to start one.</p>
      </ToolboxGroup>
    </div>
  );
}

function ToolboxGroup({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <section className={styles["designer-toolbox-group"]} aria-label={title}>
      <h3>{title}</h3>
      <div>{children}</div>
    </section>
  );
}

function ToolboxButton({ description, disabled, icon, label, onClick, title }: { description?: string; disabled?: boolean; icon: string; label: string; onClick: () => void; title?: string }) {
  return (
    <button aria-label={label} disabled={disabled} title={title || label} type="button" onClick={onClick}>
      <span aria-hidden="true">{icon}</span>
      <strong>{label}</strong>
      {description && <small>{description}</small>}
    </button>
  );
}

function DesignCanvas({
  addFieldToBand,
  datasets,
  document,
  moveField,
  reorderColumn,
  resizeColumn,
  orientation,
  pageSize,
  payload,
  selection,
  setSelection,
  showGrid,
  showRulers,
  stylesRecord,
  valueMode,
  zoom,
}: {
  addFieldToBand: (bandId: string, datasetName: string, fieldKey: string) => void;
  datasets: JsonRecord;
  document: DesignerDocument;
  moveField: (selection: Extract<DesignerSelection, { type: "field" }>, column: number, insertIndex?: number) => void;
  reorderColumn: (selection: Extract<DesignerSelection, { type: "column" }>, targetColumnId: string) => void;
  resizeColumn: (selection: Extract<DesignerSelection, { type: "column" }>, width: number) => void;
  orientation: DesignerPageOrientation;
  pageSize: DesignerPageSize;
  payload: FormattedInvoicePayload;
  selection: DesignerSelection;
  setSelection: (selection: DesignerSelection) => void;
  showGrid: boolean;
  showRulers: boolean;
  stylesRecord: JsonRecord;
  valueMode: CanvasValueMode;
  zoom: number;
}) {
  const pageClass = `${styles["designer-page"]} ${styles[`designer-page-${pageSize}-${orientation}`]} ${showGrid ? styles["designer-page-grid"] : ""}`;
  const pageMetrics = currentPagePixelSize(pageSize, orientation, payload);
  const scale = zoom / 100;
  const rulerLeft = showRulers ? NATIVE_RULER_VERTICAL_SIZE : 0;
  const rulerTop = showRulers ? NATIVE_RULER_HORIZONTAL_SIZE : 0;
  return (
    <div className={styles["designer-canvas-viewport"]}>
      <div
        className={`${styles["native-page-shell"]} ${showRulers ? styles["native-page-shell--rulers"] : ""}`}
        style={{
          width: pageMetrics.width * scale,
          height: pageMetrics.height * scale + rulerTop,
          paddingLeft: rulerLeft,
          paddingRight: rulerLeft,
          paddingTop: rulerTop,
        }}
      >
        {showRulers && <CanvasRulers height={pageMetrics.height} unitsPerInch={96} width={pageMetrics.width} zoom={zoom} />}
        <div className={pageClass} style={{ transform: `scale(${scale})` }}>
          <div className={styles["designer-page-margin"]}>
            {document.bands.map((band) => (
              <BandSurface
                addFieldToBand={addFieldToBand}
                datasets={datasets}
                key={band.id}
                moveField={moveField}
                reorderColumn={reorderColumn}
                resizeColumn={resizeColumn}
                payload={payload}
                band={band}
                selection={selection}
                setSelection={setSelection}
                stylesRecord={stylesRecord}
                valueMode={valueMode}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NativeDesignCanvas({
  addNativeFieldElement,
  addNativeTableColumn,
  addNativeContainerElement,
  addNativeImageElement,
  addNativeTextElement,
  moveNativeElements,
  moveNativeTableColumn,
  payload,
  resizeNativeElement,
  resizeNativeTableColumn,
  resizeNativeTable,
  resizeNativeBand,
  selection,
  setSelection,
  showGrid,
  showRulers,
  snapEnabled,
  valueMode,
  zoom,
}: {
  addNativeFieldElement: (bandId: string, datasetName: string, fieldKey: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addNativeTableColumn: (bandId: string, tableId: string, fieldKey: string, datasetName?: string) => void;
  addNativeContainerElement: (bandId: string, parentElementId?: string, at?: { x: number; y: number }) => void;
  addNativeImageElement: (bandId: string, asset?: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addNativeTextElement: (bandId: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  moveNativeElements: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, delta: { dx: number; dy: number }) => void;
  moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void;
  payload: FormattedInvoicePayload;
  resizeNativeElement: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Partial<{ x: number; y: number; width: number; height: number }>) => void;
  resizeNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, width: number, adjacentColumnId?: string, adjacentWidth?: number) => void;
  resizeNativeTable: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number) => void;
  resizeNativeBand: (bandId: string, height: number) => void;
  selection: DesignerSelection;
  setSelection: (selection: DesignerSelection) => void;
  showGrid: boolean;
  showRulers: boolean;
  snapEnabled: boolean;
  valueMode: CanvasValueMode;
  zoom: number;
}) {
  const diagnosticsEnabled = useMemo(() => nativeResizeDiagnosticsEnabled(), []);
  const diagnosticsRef = useRef<NativeResizeDiagnostics>(createNativeResizeDiagnostics());
  const diagnosticsOutputRef = useRef<HTMLPreElement | null>(null);
  const designPayload = useMemo(() => nativeDesignTemplatePayload(payload), [payload]);
  const plan = useMemo(() => {
    recordLayoutPlanInvocation(diagnosticsRef.current, diagnosticsEnabled);
    return resolveNativeLayoutPlan(designPayload);
  }, [designPayload, diagnosticsEnabled]);
  const [interaction, setInteraction] = useState<NativeInteraction | null>(null);
  const [activeDropTarget, setActiveDropTarget] = useState<string>("");
  const interactionRef = useRef<NativeInteraction | null>(null);
  const pendingInteractionDeltaRef = useRef<PendingInteractionDelta | null>(null);
  const interactionFrameRef = useRef<number | null>(null);
  const resizeHandoffRef = useRef<ResizeHandoff | null>(null);
  const suppressCanvasClickRef = useRef(false);
  const selectedElementIds = selection.type === "nativeElement" ? new Set(selection.elementIds) : new Set<string>();
  const isInteracting = interaction !== null;
  recordNativeDesignCanvasRender(diagnosticsRef.current, diagnosticsEnabled, interactionRef.current || interaction);

  useEffect(() => {
    interactionRef.current = interaction;
    if (!interaction && interactionFrameRef.current !== null) {
      cancelInteractionFrame(interactionFrameRef.current);
      interactionFrameRef.current = null;
      pendingInteractionDeltaRef.current = null;
    }
    publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
  }, [diagnosticsEnabled, interaction]);

  useLayoutEffect(() => {
    recordResizeStyleConflict(diagnosticsRef.current, diagnosticsEnabled, interactionRef.current);
    completeResizeHandoffIfReady(resizeHandoffRef);
    publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
  });

  useEffect(() => {
    if (!isInteracting) return undefined;
    function flushPendingInteraction(): NativeInteraction | null {
      if (interactionFrameRef.current !== null) {
        cancelInteractionFrame(interactionFrameRef.current);
        interactionFrameRef.current = null;
      }
      const pending = pendingInteractionDeltaRef.current;
      pendingInteractionDeltaRef.current = null;
      if (!pending || !interactionRef.current) return interactionRef.current;
      const next = { ...interactionRef.current, dx: pending.dx, dy: pending.dy };
      interactionRef.current = next;
      if (next.kind === "resize") applyResizeDraft(next);
      if (next.kind === "bandResize") applyBandResizeDraft(next);
      return next;
    }

    function clearInteraction() {
      if (interactionFrameRef.current !== null) {
        cancelInteractionFrame(interactionFrameRef.current);
        interactionFrameRef.current = null;
      }
      pendingInteractionDeltaRef.current = null;
      interactionRef.current = null;
      setInteraction(null);
    }

    function applyResizeDraft(current: Extract<NativeInteraction, { kind: "resize" }>) {
      const startedAt = performance.now();
      const bounds = nextResizeBounds(current.startBounds, current.handle, resizeDraftDelta(current.dx, snapEnabled), resizeDraftDelta(current.dy, snapEnabled));
      applyResizeBoundsToElement(current.overlayElement, bounds, current.startBounds);
      recordDraftGeometryUpdate(diagnosticsRef.current, diagnosticsEnabled, performance.now() - startedAt);
    }

    function applyBandResizeDraft(current: Extract<NativeInteraction, { kind: "bandResize" }>) {
      const startedAt = performance.now();
      applyBandResizeHeightToElement(current.overlayElement, Math.max(12, current.startHeight + resizeDraftDelta(current.dy, snapEnabled)));
      recordDraftGeometryUpdate(diagnosticsRef.current, diagnosticsEnabled, performance.now() - startedAt);
    }

    function resetResizeDraft(current: NativeInteraction | null) {
      if (current?.kind !== "resize") return;
      removeResizeOverlay(current);
    }

    function handlePointerMove(event: PointerEvent) {
      const current = interactionRef.current;
      if (!current) return;
      pendingInteractionDeltaRef.current = {
        dx: (event.clientX - current.startX) / (zoom / 100),
        dy: (event.clientY - current.startY) / (zoom / 100),
        eventTime: performance.now(),
        pointerEventTime: normalizePointerEventTimestamp(event.timeStamp),
      };
      recordPointerMove(diagnosticsRef.current, diagnosticsEnabled);
      if (interactionFrameRef.current !== null) return;
      recordAnimationFrameRequested(diagnosticsRef.current, diagnosticsEnabled);
      interactionFrameRef.current = requestInteractionFrame(() => {
        const frameStartedAt = performance.now();
        recordAnimationFrameExecuted(diagnosticsRef.current, diagnosticsEnabled, frameStartedAt);
        interactionFrameRef.current = null;
        const pending = pendingInteractionDeltaRef.current;
        pendingInteractionDeltaRef.current = null;
        if (!pending) return;
        if (interactionRef.current?.kind === "resize") {
          const next = { ...interactionRef.current, dx: pending.dx, dy: pending.dy };
          interactionRef.current = next;
          applyResizeDraft(next);
          recordPointerToDraftApplication(diagnosticsRef.current, diagnosticsEnabled, pending.eventTime, performance.now());
          recordPointerEventToDraftApplication(diagnosticsRef.current, diagnosticsEnabled, pending.pointerEventTime, performance.now());
          recordAnimationFrameWork(diagnosticsRef.current, diagnosticsEnabled, performance.now() - frameStartedAt);
          return;
        }
        if (interactionRef.current?.kind === "bandResize") {
          const next = { ...interactionRef.current, dx: pending.dx, dy: pending.dy };
          interactionRef.current = next;
          applyBandResizeDraft(next);
          recordPointerToDraftApplication(diagnosticsRef.current, diagnosticsEnabled, pending.eventTime, performance.now());
          recordPointerEventToDraftApplication(diagnosticsRef.current, diagnosticsEnabled, pending.pointerEventTime, performance.now());
          recordAnimationFrameWork(diagnosticsRef.current, diagnosticsEnabled, performance.now() - frameStartedAt);
          return;
        }
        setInteraction((latest) => {
          if (!latest) return latest;
          const next = { ...latest, dx: pending.dx, dy: pending.dy };
          interactionRef.current = next;
          return next;
        });
        recordAnimationFrameWork(diagnosticsRef.current, diagnosticsEnabled, performance.now() - frameStartedAt);
      });
    }
    function handlePointerUp() {
      const current = flushPendingInteraction();
      if (current && !current.cancelled) {
        const snapStartedAt = performance.now();
        const snap = snapNativeInteraction(current, plan, snapEnabled);
        recordSnappingCalculation(diagnosticsRef.current, diagnosticsEnabled, performance.now() - snapStartedAt);
        const dx = snap.dx;
        const dy = snap.dy;
        if (current.kind === "drag" && (dx || dy)) {
          recordDocumentCommand(diagnosticsRef.current, diagnosticsEnabled);
          moveNativeElements({ type: "nativeElement", bandId: current.bandId, elementIds: current.elementIds }, { dx, dy });
        }
        if (current.kind === "resize") {
          recordDocumentCommand(diagnosticsRef.current, diagnosticsEnabled);
          const finalBounds = nextResizeBounds(current.startBounds, current.handle, dx, dy);
          const finalSourceBounds = nextResizeBounds(current.startSourceBounds, current.handle, dx, dy);
          applyResizeBoundsToElement(current.overlayElement, finalBounds, current.startBounds);
          current.draftElement.dataset.resizeHandoff = "true";
          resizeHandoffRef.current = {
            bandId: current.bandId,
            elementId: current.elementId,
            bounds: finalBounds,
            draftElement: current.draftElement,
            overlayElement: current.overlayElement,
          };
          resizeNativeElement(
            { type: "nativeElement", bandId: current.bandId, elementIds: current.elementIds },
            finalSourceBounds,
          );
        }
        if (current.kind === "bandResize") {
          recordDocumentCommand(diagnosticsRef.current, diagnosticsEnabled);
          resizeNativeBand(current.bandId, Math.max(12, current.startHeight + dy));
          removeBandResizeOverlay(current);
        }
      }
      finishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsEnabled);
      publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
      clearInteraction();
      window.setTimeout(() => {
        suppressCanvasClickRef.current = false;
      }, 0);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    }
    function handlePointerCancel() {
      suppressCanvasClickRef.current = true;
      resetResizeDraft(interactionRef.current);
      if (interactionRef.current?.kind === "bandResize") removeBandResizeOverlay(interactionRef.current);
      finishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsEnabled, "cancelled");
      publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
      clearInteraction();
      window.setTimeout(() => {
        suppressCanvasClickRef.current = false;
      }, 0);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    }
    const diagnosticsInterval = diagnosticsEnabled
      ? window.setInterval(() => publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled), 500)
      : null;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);
    return () => {
      if (interactionFrameRef.current !== null) {
        cancelInteractionFrame(interactionFrameRef.current);
        interactionFrameRef.current = null;
      }
      pendingInteractionDeltaRef.current = null;
      if (diagnosticsInterval !== null) window.clearInterval(diagnosticsInterval);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [diagnosticsEnabled, isInteracting, moveNativeElements, plan, resizeNativeBand, resizeNativeElement, snapEnabled, zoom]);

  useEffect(() => {
    if (!interaction) return undefined;
    function cancelInteraction(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      suppressCanvasClickRef.current = true;
      const current = interactionRef.current;
      if (interactionFrameRef.current !== null) {
        cancelInteractionFrame(interactionFrameRef.current);
        interactionFrameRef.current = null;
      }
      if (current?.kind === "resize") {
        removeResizeOverlay(current);
      }
      if (current?.kind === "bandResize") {
        removeBandResizeOverlay(current);
      }
      pendingInteractionDeltaRef.current = null;
      interactionRef.current = null;
      finishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsEnabled, "escape");
      publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
      setInteraction(null);
    }
    window.addEventListener("keydown", cancelInteraction);
    return () => window.removeEventListener("keydown", cancelInteraction);
  }, [diagnosticsEnabled, interaction]);

  function dropField(event: DragEvent<HTMLElement>, band: NativeResolvedBand) {
    event.preventDefault();
    const raw = event.dataTransfer.getData("application/x-landagility-field");
    if (!raw) return;
    const parsed = JSON.parse(raw) as { datasetName: string; fieldKey: string };
    const point = eventPointInBand(event, zoom);
    addNativeFieldElement(band.id, parsed.datasetName, parsed.fieldKey, snapPoint(point, snapEnabled));
    setActiveDropTarget("");
  }

  function dropFieldIntoTable(event: DragEvent<HTMLElement>, bandId: string, tableId: string) {
    event.preventDefault();
    event.stopPropagation();
    const raw = event.dataTransfer.getData("application/x-landagility-field");
    if (!raw) return;
    const parsed = JSON.parse(raw) as { datasetName: string; fieldKey: string };
    addNativeTableColumn(bandId, tableId, parsed.fieldKey, parsed.datasetName);
    setActiveDropTarget("");
  }

  return (
    <div className={styles["designer-canvas-viewport"]}>
      <div className={styles["native-pages"]} onClick={() => {
        if (suppressCanvasClickRef.current) {
          suppressCanvasClickRef.current = false;
          return;
        }
        setSelection({ type: "report" });
      }}>
        {plan.pages.map((page) => {
          const scale = zoom / 100;
          const rulerLeft = showRulers ? NATIVE_RULER_VERTICAL_SIZE : 0;
          const rulerTop = showRulers ? NATIVE_RULER_HORIZONTAL_SIZE : 0;
          return (
          <div
            className={`${styles["native-page-shell"]} ${showRulers ? styles["native-page-shell--rulers"] : ""}`}
            data-testid={`native-page-shell-${page.pageNumber}`}
            key={page.pageNumber}
            style={{
              width: page.width * scale,
              height: page.height * scale + rulerTop,
              paddingLeft: rulerLeft,
              paddingRight: rulerLeft,
              paddingTop: rulerTop,
            }}
          >
            {showRulers && <NativePageRulers page={page} zoom={zoom} />}
          <div
            className={`${styles["native-page"]} ${showGrid ? styles["native-page-grid"] : ""}`}
            style={{ width: page.width, height: page.height, transform: `scale(${scale})` }}
          >
            <div className={styles["native-printable-area"]} style={{
              left: page.printableArea.x,
              top: page.printableArea.y,
              width: page.printableArea.width,
              height: page.printableArea.height,
            }} />
            {page.bands.map((band, bandIndex) => {
              const selectedBand = selection.type === "band" && selection.bandId === band.id;
              const bandSnap = interaction && interaction.bandId === band.id ? snapNativeInteraction(interaction, plan, snapEnabled) : null;
              return (
                <section
                  className={`${styles["native-band"]} ${selectedBand ? styles["native-band-selected"] : ""} ${activeDropTarget === `band:${band.id}` ? styles["native-drop-target-active"] : ""}`}
                  data-band-id={band.id}
                  key={`${page.pageNumber}-${band.id}-${band.rowIndex ?? bandIndex}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelection({ type: "band", bandId: band.id });
                  }}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    const point = snapPoint(eventPointInBand(event, zoom), snapEnabled);
                    if (event.shiftKey) addNativeContainerElement(band.id, undefined, point);
                    else addNativeTextElement(band.id, point);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setActiveDropTarget(`band:${band.id}`);
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActiveDropTarget("");
                  }}
                  onDrop={(event) => dropField(event, band)}
                  style={{ left: band.bounds.x, top: band.bounds.y, width: band.bounds.width, height: band.bounds.height }}
                >
                  <div className={styles["native-band-label"]} title={`${nativeBandLabel(band.role)} — ${nativeBandDescription(band.role)}`}>{nativeBandLabel(band.role)}{band.rowIndex !== undefined ? ` row ${band.rowIndex + 1}` : ""}</div>
                  {selectedBand && (
                    <span
                      aria-label={`Resize ${nativeBandLabel(band.role)} band`}
                      className={styles["native-band-resize-handle"]}
                      role="separator"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.setPointerCapture?.(event.pointerId);
                        const overlayElement = createBandResizeOverlay(event.currentTarget.closest(`.${styles["native-band"]}`) as HTMLElement | null, band.bounds.height);
                        if (!overlayElement) return;
                        suppressCanvasClickRef.current = true;
                        setInteraction({
                          kind: "bandResize",
                          bandId: band.id,
                          startX: event.clientX,
                          startY: event.clientY,
                          dx: 0,
                          dy: 0,
                          startHeight: band.bounds.height,
                          overlayElement,
                        });
                      }}
                    />
                  )}
                  {bandSnap?.guides.map((guide, index) => (
                    <span
                      className={`${styles["native-guide"]} ${guide.orientation === "vertical" ? styles["native-guide-vertical"] : styles["native-guide-horizontal"]}`}
                      data-testid="native-alignment-guide"
                      key={`${guide.orientation}-${guide.position}-${index}`}
                      style={guide.orientation === "vertical"
                        ? { left: guide.position, top: guide.start, height: guide.end - guide.start }
                        : { top: guide.position, left: guide.start, width: guide.end - guide.start }}
                    />
                  ))}
                  {band.elements.map((element) => (
                    <NativeCanvasElement
                      bandId={band.id}
                      addNativeFieldElement={addNativeFieldElement}
                      addNativeTableColumn={addNativeTableColumn}
                      activeDropTarget={activeDropTarget}
                      diagnosticsEnabled={diagnosticsEnabled}
                      diagnosticsOutputRef={diagnosticsOutputRef}
                      diagnosticsRef={diagnosticsRef}
                      element={element}
                      moveNativeTableColumn={moveNativeTableColumn}
                      interaction={interaction}
                      key={element.id}
                      payload={payload}
                      selectedElementIds={selectedElementIds}
                      resizeNativeTableColumn={resizeNativeTableColumn}
                      resizeNativeTable={resizeNativeTable}
                      selection={selection}
                      setInteraction={setInteraction}
                      setSelection={setSelection}
                      setActiveDropTarget={setActiveDropTarget}
                      snap={bandSnap}
                      snapEnabled={snapEnabled}
                      suppressCanvasClickRef={suppressCanvasClickRef}
                      dropFieldIntoTable={dropFieldIntoTable}
                      valueMode={valueMode}
                      zoom={zoom}
                    />
                  ))}
                </section>
              );
            })}
          </div>
          </div>
          );
        })}
      </div>
      {diagnosticsEnabled && (
        <div className={styles["native-resize-diagnostics"]} aria-label="Native resize diagnostics">
          <div className={styles["native-resize-diagnostics-header"]}>
            <strong>Resize diagnostics</strong>
            <button
              type="button"
              onClick={() => copyNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current)}
            >
              Copy
            </button>
          </div>
          <pre ref={diagnosticsOutputRef}>{nativeResizeDiagnosticsSummary(diagnosticsRef.current)}</pre>
        </div>
      )}
    </div>
  );
}

function NativeCanvasElement({
  addNativeFieldElement,
  addNativeTableColumn,
  activeDropTarget,
  bandId,
  diagnosticsEnabled,
  diagnosticsOutputRef,
  diagnosticsRef,
  element,
  moveNativeTableColumn,
  interaction,
  payload,
  resizeNativeTableColumn,
  resizeNativeTable,
  selectedElementIds,
  selection,
  setInteraction,
  setSelection,
  setActiveDropTarget,
  snap,
  snapEnabled,
  suppressCanvasClickRef,
  dropFieldIntoTable,
  valueMode,
  zoom,
}: {
  addNativeFieldElement: (bandId: string, datasetName: string, fieldKey: string, at?: { x: number; y: number }, parentElementId?: string) => void;
  addNativeTableColumn: (bandId: string, tableId: string, fieldKey: string, datasetName?: string) => void;
  moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void;
  activeDropTarget: string;
  bandId: string;
  diagnosticsEnabled: boolean;
  diagnosticsOutputRef: React.MutableRefObject<HTMLPreElement | null>;
  diagnosticsRef: React.MutableRefObject<NativeResizeDiagnostics>;
  element: NativeResolvedElement;
  payload: FormattedInvoicePayload;
  interaction: NativeInteraction | null;
  resizeNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, width: number, adjacentColumnId?: string, adjacentWidth?: number) => void;
  resizeNativeTable: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number) => void;
  selectedElementIds: Set<string>;
  selection: DesignerSelection;
  setInteraction: (interaction: NativeInteraction) => void;
  setSelection: (selection: DesignerSelection) => void;
  setActiveDropTarget: (target: string) => void;
  snap: { dx: number; dy: number; guides: AlignmentGuide[] } | null;
  snapEnabled: boolean;
  suppressCanvasClickRef: React.MutableRefObject<boolean>;
  dropFieldIntoTable: (event: DragEvent<HTMLElement>, bandId: string, tableId: string) => void;
  valueMode: CanvasValueMode;
  zoom: number;
}) {
  const selected = selectedElementIds.has(element.id);
  const preview = interaction?.kind === "drag" && interaction.bandId === bandId && interaction.elementIds.includes(element.id) && snap
    ? { x: snap.dx, y: snap.dy }
    : { x: 0, y: 0 };
  const isContainer = element.type === "container";
  const isTable = element.type === "table";
  const elementBounds = {
    x: Number(element.x ?? 0),
    y: Number(element.y ?? 0),
    width: Number(element.width ?? 80),
    height: Number(element.height ?? 18),
  };
  const style = element.style || {};
  function selectElement(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
    event.stopPropagation();
    if (event.shiftKey && selection.type === "nativeElement" && selection.bandId === bandId) {
      const nextIds = selected ? selection.elementIds.filter((id) => id !== element.id) : [...selection.elementIds, element.id];
      setSelection(nextIds.length ? { type: "nativeElement", bandId, elementIds: nextIds } : { type: "band", bandId });
      return;
    }
    setSelection({ type: "nativeElement", bandId, elementIds: [element.id] });
  }
  function startDrag(event: React.PointerEvent<HTMLElement>) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const nextIds = selection.type === "nativeElement" && selection.bandId === bandId && selection.elementIds.includes(element.id) ? selection.elementIds : [element.id];
    setSelection({ type: "nativeElement", bandId, elementIds: nextIds });
    suppressCanvasClickRef.current = true;
    setInteraction({ kind: "drag", bandId, elementIds: nextIds, startX: event.clientX, startY: event.clientY, dx: 0, dy: 0 });
  }
  return (
    <div
      className={`${styles["native-element"]} ${isContainer ? styles["native-container"] : ""} ${isTable ? styles["native-table-element"] : ""} ${selected ? styles["native-element-selected"] : ""} ${element.overflow ? styles["native-element-overflow"] : ""} ${activeDropTarget === `container:${element.id}` || activeDropTarget === `table:${element.id}` ? styles["native-drop-target-active"] : ""}`}
      data-testid={`native-element-${element.id}`}
      onDragEnter={isContainer || isTable ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        setActiveDropTarget(`${isTable ? "table" : "container"}:${element.id}`);
      } : undefined}
      onDragLeave={isContainer || isTable ? (event) => {
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActiveDropTarget("");
      } : undefined}
      onDragOver={isContainer || isTable ? (event) => {
        event.preventDefault();
        event.stopPropagation();
      } : undefined}
      onDrop={isContainer || isTable ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        const raw = event.dataTransfer.getData("application/x-landagility-field");
        if (!raw) return;
        const parsed = JSON.parse(raw) as { datasetName: string; fieldKey: string };
        if (isTable) addNativeTableColumn(bandId, element.id, parsed.fieldKey, parsed.datasetName);
        else addNativeFieldElement(bandId, parsed.datasetName, parsed.fieldKey, snapPoint(eventPointInElement(event, zoom), snapEnabled), element.id);
        setActiveDropTarget("");
      } : undefined}
      onClick={selectElement}
      onKeyDown={event => {
        if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        selectElement(event);
      }}
      onPointerDown={startDrag}
      role={isContainer || isTable ? "group" : "button"}
      aria-label={isContainer || isTable ? `${humanizeIdentifier(element.id)} ${element.type}` : undefined}
      tabIndex={0}
      style={{
        left: elementBounds.x + preview.x,
        top: elementBounds.y + preview.y,
        width: elementBounds.width,
        height: elementBounds.height,
        color: style.color,
        background: style.backgroundColor || (isContainer ? "rgba(248, 251, 255, 0.42)" : "transparent"),
        borderColor: style.borderColor,
        borderWidth: style.borderWidth,
        fontSize: style.fontSize,
        fontWeight: style.bold ? 700 : 400,
        fontStyle: style.italic ? "italic" : "normal",
        textAlign: style.horizontalAlign || "left",
      }}
    >
      {isTable ? (
        <NativeTableCanvas
          bandId={bandId}
          element={element}
          moveNativeTableColumn={moveNativeTableColumn}
          resizeNativeTableColumn={resizeNativeTableColumn}
          resizeNativeTable={resizeNativeTable}
          sourcePayload={payload}
          selectedColumnId={selection.type === "nativeTableColumn" && selection.tableId === element.id ? selection.columnId : ""}
          selectedGroupRegion={selection.type === "nativeTableGroup" && selection.tableId === element.id ? selection.region || "group" : ""}
          setSelection={setSelection}
          tableSelected={(selection.type === "nativeElement" && selected) || (selection.type === "nativeTableGroup" && selection.tableId === element.id)}
          valueMode={valueMode}
          zoom={zoom}
        />
      ) : isContainer ? (
        <>
          {(element.elements || []).map((child) => (
            <NativeCanvasElement
              addNativeFieldElement={addNativeFieldElement}
              addNativeTableColumn={addNativeTableColumn}
              moveNativeTableColumn={moveNativeTableColumn}
              activeDropTarget={activeDropTarget}
              bandId={bandId}
              diagnosticsEnabled={diagnosticsEnabled}
              diagnosticsOutputRef={diagnosticsOutputRef}
              diagnosticsRef={diagnosticsRef}
              element={child}
              interaction={interaction}
              key={child.id}
              payload={payload}
              resizeNativeTableColumn={resizeNativeTableColumn}
              resizeNativeTable={resizeNativeTable}
              selectedElementIds={selectedElementIds}
              selection={selection}
              setInteraction={setInteraction}
              setSelection={setSelection}
              setActiveDropTarget={setActiveDropTarget}
              snap={snap}
              snapEnabled={snapEnabled}
              suppressCanvasClickRef={suppressCanvasClickRef}
              dropFieldIntoTable={dropFieldIntoTable}
              valueMode={valueMode}
              zoom={zoom}
            />
          ))}
          {!element.elements?.length && <span className={styles["native-container-placeholder"]}>Container</span>}
        </>
      ) : element.type === "image" ? (
        nativeImageCanvasContent(element, payload)
      ) : (
        <span>{element.value || element.text || element.binding || element.id}</span>
      )}
      {selected && !isTable && (
        <ResizeHandles
          bandId={bandId}
          element={element}
          selectedElementIds={selection.type === "nativeElement" && selection.bandId === bandId ? selection.elementIds : [element.id]}
          startDiagnostics={(nextInteraction) => {
            startNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsEnabled, nextInteraction, zoom, snapEnabled);
            publishNativeResizeDiagnostics(diagnosticsRef.current, diagnosticsOutputRef.current, diagnosticsEnabled);
          }}
          setInteraction={setInteraction}
          suppressCanvasClickRef={suppressCanvasClickRef}
        />
      )}
    </div>
  );
}

function NativeTableCanvas({
  bandId,
  element,
  moveNativeTableColumn,
  resizeNativeTableColumn,
  resizeNativeTable,
  sourcePayload,
  selectedColumnId,
  selectedGroupRegion,
  setSelection,
  tableSelected,
  valueMode,
  zoom,
}: {
  bandId: string;
  element: NativeResolvedElement;
  moveNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number) => void;
  resizeNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, width: number, adjacentColumnId?: string, adjacentWidth?: number) => void;
  resizeNativeTable: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number) => void;
  sourcePayload: FormattedInvoicePayload;
  selectedColumnId: string;
  selectedGroupRegion: string;
  setSelection: (selection: DesignerSelection) => void;
  tableSelected: boolean;
  valueMode: CanvasValueMode;
  zoom: number;
}) {
  const table = element.table;
  const resolvedRows = table?.rows || [];
  const rows = [
    resolvedRows.find((row) => row.kind === "groupHeader"),
    table?.header || resolvedRows.find((row) => row.kind === "tableHeader" || row.kind === "header"),
    resolvedRows.find((row) => row.kind === "detailRow" || row.kind === "detail" || row.kind === "empty"),
    resolvedRows.find((row) => row.kind === "groupFooter"),
    table?.footer || resolvedRows.find((row) => row.kind === "tableFooter" || row.kind === "footer"),
  ].filter(Boolean) as NonNullable<NonNullable<NativeResolvedElement["table"]>["header"]>[];
  const columns = table?.columns || [];
  const selectedColumn = selectedColumnId ? columns.find((column) => column.id === selectedColumnId) : null;
  if (!table || !columns.length) {
    return <div className={styles["native-table-placeholder"]}>Repeating table: add columns from Data Explorer.</div>;
  }
  const repeatedRowCount = getRows(getDataset(asRecord(sourcePayload.datasets), table.dataset || "")).length;
  return (
    <div className={styles["native-table-design"]}>
      {rows.map((row, rowIndex) => (
        <div
          className={`${styles["native-table-design-row"]} ${styles[`native-table-design-row--${row.kind}`] || ""} ${selectedGroupRegion && groupRegionForRowKind(row.kind) === selectedGroupRegion ? styles["native-table-design-row-selected"] : ""}`}
          key={`${row.kind}-${row.rowIndex ?? rowIndex}`}
          style={{ top: row.y, height: row.height }}
        >
          {row.cells.map((cell) => {
            const selected = selectedColumnId === cell.columnId;
            const column = columns.find((candidate) => candidate.id === cell.columnId);
            const isHeaderRow = row.kind === "header" || row.kind === "tableHeader";
            const isDetailRow = row.kind === "detail" || row.kind === "detailRow";
            const groupRegion = groupRegionForRowKind(row.kind);
            const selectedHeader = selected && isHeaderRow;
            const selectedTemplateCell = selected && isDetailRow;
            return (
              <button
                aria-label={groupRegion ? `Select ${groupRegion} template` : `Select table column ${column?.label || cell.columnId}`}
                className={`${styles["native-table-design-cell"]} ${selectedHeader ? styles["native-table-design-cell-selected"] : ""} ${selectedTemplateCell ? styles["native-table-design-cell-column-selected"] : ""}`}
                data-testid={`native-table-column-${cell.columnId}`}
                draggable={isHeaderRow && !groupRegion}
                key={`${row.kind}-${cell.columnId}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (groupRegion) {
                    setSelection({ type: "nativeTableGroup", bandId, tableId: element.id, region: groupRegion });
                    return;
                  }
                  setSelection({ type: "nativeTableColumn", bandId, tableId: element.id, columnId: cell.columnId });
                }}
                onDragStart={(event) => {
                  if (!isHeaderRow) return;
                  event.stopPropagation();
                  event.dataTransfer.setData("application/x-landagility-native-column", JSON.stringify({ bandId, tableId: element.id, columnId: cell.columnId }));
                  event.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(event) => {
                  if (!isHeaderRow) return;
                  event.preventDefault();
                  event.stopPropagation();
                  event.dataTransfer.dropEffect = "move";
                  event.currentTarget.classList.add(styles["native-table-column-drop-target"]);
                }}
                onDragLeave={(event) => {
                  event.currentTarget.classList.remove(styles["native-table-column-drop-target"]);
                }}
                onDrop={(event) => {
                  if (!isHeaderRow) return;
                  event.preventDefault();
                  event.stopPropagation();
                  event.currentTarget.classList.remove(styles["native-table-column-drop-target"]);
                  const raw = event.dataTransfer.getData("application/x-landagility-native-column");
                  if (!raw) return;
                  const parsed = JSON.parse(raw) as { bandId: string; tableId: string; columnId: string };
                  if (parsed.bandId !== bandId || parsed.tableId !== element.id) return;
                  const targetIndex = columns.findIndex((candidate) => candidate.id === cell.columnId);
                  moveNativeTableColumn({ type: "nativeTableColumn", bandId, tableId: element.id, columnId: parsed.columnId }, targetIndex);
                }}
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                  left: cell.x,
                  top: 0,
                  width: cell.width,
                  height: cell.height,
                  textAlign: (cell.style?.horizontalAlign || "left") as React.CSSProperties["textAlign"],
                  background: cell.style?.backgroundColor,
                  color: cell.style?.color,
                  fontWeight: cell.style?.bold ? 700 : 400,
                  fontSize: cell.style?.fontSize,
                }}
                title={nativeTableDesignCellText(cell, row.kind, column, valueMode, element)}
                type="button"
              >
                {nativeTableDesignCellText(cell, row.kind, column, valueMode, element)}
              </button>
            );
          })}
        </div>
      ))}
      {repeatedRowCount > 1 && (
        <div className={styles["native-table-repeat-hint"]} data-testid={`native-table-repeat-hint-${element.id}`}>
          Repeats for each record in {humanizeIdentifier(String(table.dataset || "dataset"))} ({repeatedRowCount.toLocaleString()} sample rows in Preview/PDF).
        </div>
      )}
      {tableSelected && (
        <>
          {["nw", "ne", "se", "sw"].map((corner) => (
            <span className={`${styles["native-table-selection-handle"]} ${styles[`native-table-selection-handle-${corner}`]}`} key={corner} />
          ))}
        </>
      )}
      {selectedColumn && (
        <span
          aria-label={`Resize table column ${selectedColumn.label || selectedColumn.id}`}
          className={styles["native-table-column-resize"]}
          role="separator"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const index = columns.findIndex((candidate) => candidate.id === selectedColumn.id);
            const adjacent = columns[index + 1] || columns[index - 1];
            if (!adjacent) return;
            startNativeTableColumnResize({
              adjacentStartWidth: Number(adjacent.width || 0),
              commitResize: (nextWidth, nextAdjacentWidth) => resizeNativeTableColumn(
                { type: "nativeTableColumn", bandId, tableId: element.id, columnId: selectedColumn.id },
                nextWidth,
                adjacent.id,
                nextAdjacentWidth,
              ),
              startWidth: Number(selectedColumn.width || 0),
              startX: event.clientX,
              zoom,
            });
          }}
          style={{ left: selectedColumn.x + selectedColumn.width - 4 }}
        />
      )}
      {tableSelected && (
        <span
          aria-label="Resize table width"
          className={styles["native-table-width-resize"]}
          role="separator"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startNativeTableWidthResize({
              commitResize: (nextWidth) => resizeNativeTable({ type: "nativeElement", bandId, elementIds: [element.id] }, nextWidth),
              startWidth: Number(element.width || element.sourceBounds?.width || 0),
              startX: event.clientX,
              zoom,
            });
          }}
        />
      )}
    </div>
  );
}

function nativeImageCanvasContent(element: NativeResolvedElement, payload: FormattedInvoicePayload) {
  const assetKey = String((element as unknown as JsonRecord).asset || "");
  const fit = String((element as unknown as JsonRecord).fit || "contain");
  return <NativeReportImage payload={payload} reference={assetKey} fit={fit} className={styles["native-image-preview"]} missingClassName={styles["native-image-placeholder"]} />;
}

function nativeDesignTemplatePayload(payload: FormattedInvoicePayload): FormattedInvoicePayload {
  if (!isNativeReportsV31Payload(payload)) return payload;
  const next = clonePayload(payload);
  inheritInternalSession(payload, next);
  const datasets = { ...asRecord(next.datasets) };
  for (const descriptor of discoverReportDatasets(payload)) {
    if (descriptor.kind !== "collection") continue;
    const originalDataset = datasets[descriptor.id];
    const firstRow = getRows(originalDataset)[0];
    const templateRows = firstRow ? [firstRow] : [];
    datasets[descriptor.id] = withGeneratedCollectionRows(originalDataset, templateRows, descriptor.rowContainerKey);
  }
  next.datasets = datasets;
  return next;
}

function nativeTableDesignCellText(
  cell: NonNullable<NonNullable<NativeResolvedElement["table"]>["header"]>["cells"][number],
  rowKind: string,
  column: NonNullable<NativeResolvedElement["table"]>["columns"][number] | undefined,
  valueMode: CanvasValueMode,
  element?: NativeResolvedElement,
): string {
  if ((rowKind === "groupHeader" || rowKind === "groupFooter") && valueMode !== "sample") {
    const placeholder = nativeGroupTemplatePlaceholder(cell, rowKind, column, element);
    if (valueMode === "binding") return placeholder;
    return `${String(cell.value || "—")} · ${placeholder}`;
  }
  if ((rowKind === "tableFooter" || rowKind === "footer") && valueMode !== "sample") {
    const placeholder = nativeFooterTemplatePlaceholder(cell, column, element);
    if (placeholder) return valueMode === "binding" ? placeholder : `${String(cell.value || "—")} · ${placeholder}`;
  }
  if (rowKind !== "detail" && rowKind !== "detailRow") return String(cell.value || "\u00a0");
  return formatDesignerValue((cell as unknown as JsonRecord).rawValue, nativeColumnBindingPlaceholder(column), valueMode);
}

function nativeGroupTemplatePlaceholder(
  cell: NonNullable<NonNullable<NativeResolvedElement["table"]>["header"]>["cells"][number],
  rowKind: string,
  column: NonNullable<NativeResolvedElement["table"]>["columns"][number] | undefined,
  element?: NativeResolvedElement,
): string {
  const group = asRecord(asArray((element as unknown as JsonRecord | undefined)?.groups).map(asRecord)[0]);
  const groupField = String(group.field || asRecord(group.key).field || "");
  if (rowKind === "groupHeader") {
    const label = String(asRecord(group.header).label || group.label || humanizeIdentifier(groupField || "group"));
    return groupField ? `${label}: {${groupField}}` : `${label}: {group}`;
  }
  const footerCells = asArray(asRecord(group.footer).cells).map(asRecord);
  const footerCell = footerCells.find((candidate) => String(candidate.columnId || "") === cell.columnId) || footerCells.find((candidate) => candidate.aggregate);
  const aggregateConfig = asRecord(footerCell?.aggregate);
  const aggregate = Object.keys(aggregateConfig).length ? String(aggregateConfig.fn || "aggregate") : String(footerCell?.aggregate || "");
  if (aggregate) {
    const field = String(aggregateConfig.field || footerCell?.field || footerCell?.binding || column?.binding || column?.key || column?.id || "value");
    return `{${aggregate}(${field})}`;
  }
  return String(footerCell?.value || cell.value || "{group footer}");
}

function nativeFooterTemplatePlaceholder(
  cell: NonNullable<NonNullable<NativeResolvedElement["table"]>["header"]>["cells"][number],
  column: NonNullable<NativeResolvedElement["table"]>["columns"][number] | undefined,
  element?: NativeResolvedElement,
): string {
  const footerCells = asArray(asRecord((element as unknown as JsonRecord | undefined)?.footer).cells).map(asRecord);
  const footerCell = footerCells.find((candidate) => String(candidate.columnId || "") === cell.columnId) || footerCells.find((candidate) => candidate.aggregate);
  const aggregateConfig = asRecord(footerCell?.aggregate);
  const aggregate = Object.keys(aggregateConfig).length ? String(aggregateConfig.fn || "aggregate") : String(footerCell?.aggregate || "");
  if (!aggregate) return "";
  const field = String(aggregateConfig.field || footerCell?.field || footerCell?.binding || column?.binding || column?.key || column?.id || "value");
  const scope = String(aggregateConfig.scope || "report");
  return scope === "report" ? `{${aggregate}(${field}), report}` : `{${aggregate}(${field})}`;
}

function nativeColumnBindingPlaceholder(column: NonNullable<NativeResolvedElement["table"]>["columns"][number] | undefined): string {
  if (!column) return "Unbound";
  const record = column as unknown as JsonRecord;
  const expression = asRecord(record.expr || record.calculation);
  if (Object.keys(expression).length) return expressionPlaceholder(expression);
  return String(column.binding || column.key || column.id || "Unbound");
}

function expressionPlaceholder(expression: JsonRecord): string {
  return `{${expressionPlaceholderInner(expression)}}`;
}

function expressionPlaceholderInner(expression: JsonRecord): string {
  if (Object.prototype.hasOwnProperty.call(expression, "const")) return JSON.stringify(expression.const);
  if (typeof expression.field === "string") return expression.field;
  if (expression.aggregate) {
    const aggregate = asRecord(expression.aggregate);
    const fn = String(aggregate.fn || "aggregate");
    const field = String(aggregate.field || "value");
    const scope = String(aggregate.scope || "report");
    return scope === "report" ? `${fn}(${field}), report` : `${fn}(${field})`;
  }
  const op = String(expression.op || "");
  const args = Array.isArray(expression.args) ? expression.args : [expression.left, expression.right].filter(Boolean);
  const rendered = args.map((item) => {
    const child = asRecord(item);
    const text = expressionPlaceholderInner(child);
    return child.op ? `(${text})` : text;
  }).join(` ${operatorSymbol(op)} `);
  return rendered || "calculated";
}

function operatorSymbol(op: string): string {
  if (op === "add") return "+";
  if (op === "sub") return "-";
  if (op === "mul") return "×";
  if (op === "div") return "÷";
  return op || "?";
}

function groupRegionForRowKind(rowKind: string): "header" | "detail" | "footer" | "tableFooter" | "" {
  if (rowKind === "groupHeader") return "header";
  if (rowKind === "detailRow" || rowKind === "detail") return "detail";
  if (rowKind === "groupFooter") return "footer";
  if (rowKind === "tableFooter" || rowKind === "footer") return "tableFooter";
  return "";
}

const NATIVE_RULER_HORIZONTAL_SIZE = 24;
const NATIVE_RULER_VERTICAL_SIZE = 30;
const POINTS_PER_INCH = 72;

function NativePageRulers({ page, zoom }: { page: ReturnType<typeof resolveNativeLayoutPlan>["pages"][number]; zoom: number }) {
  return (
    <CanvasRulers
      height={page.height}
      margin={{ x: page.printableArea.x, y: page.printableArea.y, width: page.printableArea.width, height: page.printableArea.height }}
      unitsPerInch={POINTS_PER_INCH}
      width={page.width}
      zoom={zoom}
    />
  );
}

function CanvasRulers({
  height,
  margin,
  unitsPerInch,
  width,
  zoom,
}: {
  height: number;
  margin?: { x: number; y: number; width: number; height: number };
  unitsPerInch: number;
  width: number;
  zoom: number;
}) {
  const scale = zoom / 100;
  const horizontalTicks = buildRulerTicks(width, scale, unitsPerInch);
  const verticalTicks = buildRulerTicks(height, scale, unitsPerInch);
  const marginLeft = margin ? margin.x * scale : 0;
  const marginRight = margin ? (margin.x + margin.width) * scale : 0;
  const marginTop = margin ? margin.y * scale : 0;
  const marginBottom = margin ? (margin.y + margin.height) * scale : 0;
  return (
    <div className={styles["native-page-rulers"]} aria-label="Page rulers">
      <div className={styles["native-page-ruler-corner"]} aria-hidden="true" />
      <div className={styles["native-page-ruler-horizontal"]} data-testid="native-page-ruler-horizontal" style={{ left: NATIVE_RULER_VERTICAL_SIZE, width: width * scale }}>
        {margin && <span className={styles["native-page-ruler-margin"]} style={{ left: marginLeft, width: marginRight - marginLeft }} aria-hidden="true" />}
        {horizontalTicks.map((tick) => (
          <span
            className={`${styles["native-page-ruler-tick"]} ${styles[`native-page-ruler-tick--${tick.kind}`]}`}
            key={`x-${tick.point}`}
            style={{ left: tick.position }}
          >
            {tick.label && <span className={styles["native-page-ruler-label"]}>{tick.label}</span>}
          </span>
        ))}
      </div>
      <div className={styles["native-page-ruler-vertical"]} data-testid="native-page-ruler-vertical" style={{ top: NATIVE_RULER_HORIZONTAL_SIZE, height: height * scale }}>
        {margin && <span className={styles["native-page-ruler-margin"]} style={{ top: marginTop, height: marginBottom - marginTop }} aria-hidden="true" />}
        {verticalTicks.map((tick) => (
          <span
            className={`${styles["native-page-ruler-tick"]} ${styles[`native-page-ruler-tick--${tick.kind}`]}`}
            key={`y-${tick.point}`}
            style={{ top: tick.position }}
          >
            {tick.label && <span className={styles["native-page-ruler-label"]}>{tick.label}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

function buildRulerTicks(length: number, scale: number, unitsPerInch: number): Array<{ point: number; position: number; kind: "major" | "medium" | "minor"; label: string }> {
  const step = unitsPerInch / 4;
  const count = Math.floor(length / step);
  return Array.from({ length: count + 1 }, (_, index) => {
    const point = index * step;
    const quarter = index % 4;
    const inch = point / unitsPerInch;
    return {
      point,
      position: point * scale,
      kind: quarter === 0 ? "major" : quarter === 2 ? "medium" : "minor",
      label: quarter === 0 ? (inch === 0 ? "0" : `${inch} in`) : "",
    };
  });
}

function ResizeHandles({
  bandId,
  element,
  selectedElementIds,
  startDiagnostics,
  setInteraction,
  suppressCanvasClickRef,
}: {
  bandId: string;
  element: NativeResolvedElement;
  selectedElementIds: string[];
  startDiagnostics: (interaction: NativeInteraction) => void;
  setInteraction: (interaction: NativeInteraction) => void;
  suppressCanvasClickRef: React.MutableRefObject<boolean>;
}) {
  return (
    <>
      {["nw", "n", "ne", "e", "se", "s", "sw", "w"].map((handle) => (
        <span
          aria-label={`Resize ${handle}`}
          className={`${styles["native-resize-handle"]} ${styles[`native-resize-handle-${handle}`]}`}
          key={handle}
          role="separator"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const draftElement = event.currentTarget.closest(`.${styles["native-element"]}`) as HTMLElement | null;
            if (!draftElement) return;
            suppressCanvasClickRef.current = true;
            const startBounds = { x: Number(element.x || 0), y: Number(element.y || 0), width: Number(element.width || 0), height: Number(element.height || 0) };
            const sourceBoundsRecord = asRecord((element as unknown as JsonRecord).sourceBounds);
            const startSourceBounds = {
              x: Number(sourceBoundsRecord.x ?? startBounds.x),
              y: Number(sourceBoundsRecord.y ?? startBounds.y),
              width: Number(sourceBoundsRecord.width ?? startBounds.width),
              height: Number(sourceBoundsRecord.height ?? startBounds.height),
            };
            const overlayElement = createResizeOverlay(draftElement, startBounds);
            if (!overlayElement) return;
            const nextInteraction: NativeInteraction = {
              kind: "resize",
              bandId,
              elementId: element.id,
              elementIds: selectedElementIds.includes(element.id) ? selectedElementIds : [element.id],
              handle,
              startX: event.clientX,
              startY: event.clientY,
              dx: 0,
              dy: 0,
              startBounds,
              startSourceBounds,
              draftElement,
              overlayElement,
            };
            startDiagnostics(nextInteraction);
            setInteraction(nextInteraction);
          }}
        />
      ))}
    </>
  );
}

function BandSurface({
  addFieldToBand,
  datasets,
  moveField,
  reorderColumn,
  resizeColumn,
  payload,
  band,
  selection,
  setSelection,
  stylesRecord,
  valueMode,
}: {
  addFieldToBand: (bandId: string, datasetName: string, fieldKey: string) => void;
  datasets: JsonRecord;
  moveField: (selection: Extract<DesignerSelection, { type: "field" }>, column: number, insertIndex?: number) => void;
  reorderColumn: (selection: Extract<DesignerSelection, { type: "column" }>, targetColumnId: string) => void;
  resizeColumn: (selection: Extract<DesignerSelection, { type: "column" }>, width: number) => void;
  payload: FormattedInvoicePayload;
  band: DesignerBand;
  selection: DesignerSelection;
  setSelection: (selection: DesignerSelection) => void;
  stylesRecord: JsonRecord;
  valueMode: CanvasValueMode;
}) {
  const component = primaryComponent(band);
  const isSelected = selection.type === "band" && selection.bandId === band.id;
  const isComponentSelected = selection.type === "component" && selection.bandId === band.id && selection.componentId === component?.id;
  const supportsDrop = component?.kind === "keyValueGrid" || component?.kind === "table";
  function handleDrop(event: DragEvent) {
    if (!supportsDrop) return;
    event.preventDefault();
    const raw = event.dataTransfer.getData("application/x-landagility-field");
    if (!raw) return;
    const parsed = JSON.parse(raw) as { datasetName: string; fieldKey: string };
    addFieldToBand(band.id, parsed.datasetName, parsed.fieldKey);
  }
  return (
    <section
      className={`${styles["designer-band"]} ${isSelected ? styles["designer-band-selected"] : ""}`}
      onClick={() => setSelection({ type: "band", bandId: band.id })}
      onDragOver={(event) => supportsDrop && event.preventDefault()}
      onDrop={handleDrop}
    >
      <div className={styles["designer-band-label"]}>
        <span>{band.title}</span>
        <strong>{band.legacyId || band.id}</strong>
      </div>
      {component?.kind === "keyValueGrid" && (
        <div className={isComponentSelected ? styles["designer-component-selected"] : ""} onClick={(event) => {
          event.stopPropagation();
          setSelection({ type: "component", bandId: band.id, componentId: component.id });
        }}>
        <KeyValueGridDesign
          bandId={band.id}
          component={component}
          dataset={getDataset(datasets, component.datasetId || "")}
          moveField={moveField}
          selection={selection}
          setSelection={setSelection}
          style={asRecord(stylesRecord[component.styleId || ""])}
          valueMode={valueMode}
        />
        </div>
      )}
      {component?.kind === "table" && (
        <div className={isComponentSelected ? styles["designer-component-selected"] : ""} onClick={(event) => {
          event.stopPropagation();
          setSelection({ type: "component", bandId: band.id, componentId: component.id });
        }}>
          <TableDesign
            bandId={band.id}
            component={component}
            dataset={getDataset(datasets, component.datasetId || "")}
            reorderColumn={reorderColumn}
            resizeColumn={resizeColumn}
            selection={selection}
            setSelection={setSelection}
            valueMode={valueMode}
          />
        </div>
      )}
      {component?.kind === "text" && (
        <div className={styles["designer-text-section"]}>{String(resolveTemplate(payload, component.content || "") || "Text section")}</div>
      )}
      {component?.kind === "rendererSection" && (
        <div className={styles["designer-unsupported-section"]}>{component.legacyType} renderer-backed band</div>
      )}
    </section>
  );
}

function KeyValueGridDesign({
  bandId,
  component,
  dataset,
  moveField,
  selection,
  setSelection,
  style,
  valueMode,
}: {
  bandId: string;
  component: Extract<DesignerComponent, { kind: "keyValueGrid" }>;
  dataset: unknown;
  moveField: (selection: Extract<DesignerSelection, { type: "field" }>, column: number, insertIndex?: number) => void;
  selection: DesignerSelection;
  setSelection: (selection: DesignerSelection) => void;
  style: JsonRecord;
  valueMode: CanvasValueMode;
}) {
  const fields = component.fields;
  const columns = Math.max(1, Number(style.columns || 3));
  const fieldsByColumn = Array.from({ length: columns }, () => [] as DesignerField[]);
  fields.forEach((field) => {
    const column = Math.min(columns, Math.max(1, Number(field.column || 1)));
    fieldsByColumn[column - 1].push(field);
  });
  return (
    <div className={styles["designer-kv-grid"]} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {fieldsByColumn.map((columnFields, columnIndex) => (
        <div className={styles["designer-kv-column"]} key={`column-${columnIndex}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => {
          const fieldId = event.dataTransfer.getData("text/plain");
          if (fieldId) moveField({ type: "field", bandId, componentId: component.id, fieldId }, columnIndex + 1, component.fields.length);
        }}>
          {columnFields.map((field) => {
            const selected = selection.type === "field" && selection.fieldId === field.id;
            const fieldSelection: Extract<DesignerSelection, { type: "field" }> = { type: "field", bandId, componentId: component.id, fieldId: field.id };
            const fieldIndex = component.fields.findIndex((item) => item.id === field.id);
            return (
              <button
                className={`${styles["designer-kv-field"]} ${selected ? styles["designer-element-selected"] : ""}`}
                draggable
                key={field.id}
                type="button"
                data-testid={`designer-field-${field.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelection(fieldSelection);
                }}
                onDragStart={(event) => event.dataTransfer.setData("text/plain", field.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const draggedFieldId = event.dataTransfer.getData("text/plain");
                  if (!draggedFieldId) return;
                  moveField({ type: "field", bandId, componentId: component.id, fieldId: draggedFieldId }, Number(field.column || columnIndex + 1), fieldIndex);
                }}
              >
                {field.label ? <strong>{field.label}</strong> : null}
                <span>{formatDesignerValue(getPath(dataset, field.key || ""), field.key, valueMode)}</span>
                {valueMode !== "sample" && <small>{field.key || "unbound"}</small>}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function TableDesign({
  bandId,
  component,
  dataset,
  reorderColumn,
  resizeColumn,
  selection,
  setSelection,
  valueMode,
}: {
  bandId: string;
  component: Extract<DesignerComponent, { kind: "table" }>;
  dataset: unknown;
  reorderColumn: (selection: Extract<DesignerSelection, { type: "column" }>, targetColumnId: string) => void;
  resizeColumn: (selection: Extract<DesignerSelection, { type: "column" }>, width: number) => void;
  selection: DesignerSelection;
  setSelection: (selection: DesignerSelection) => void;
  valueMode: CanvasValueMode;
}) {
  const columns = component.columns;
  const rows = getRows(dataset).slice(0, 3);
  const gridTemplateColumns = columns.length ? columns.map((column) => columnWidthToCss(column.width)).join(" ") : "minmax(160px, 1fr)";
  return (
    <div className={styles["designer-table"]}>
      <div className={styles["designer-table-row"]} style={{ gridTemplateColumns }}>
        {columns.map((column) => (
          <div
            className={`${styles["designer-table-header"]} ${selection.type === "column" && selection.columnId === column.id ? styles["designer-element-selected"] : ""}`}
            draggable
            key={column.id}
            data-testid={`designer-column-${column.id}`}
            onClick={(event) => {
              event.stopPropagation();
              setSelection({ type: "column", bandId, componentId: component.id, columnId: column.id });
            }}
            onDragStart={(event) => event.dataTransfer.setData("text/plain", column.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const draggedColumnId = event.dataTransfer.getData("text/plain");
              if (!draggedColumnId) return;
              reorderColumn({ type: "column", bandId, componentId: component.id, columnId: draggedColumnId }, column.id);
            }}
          >
            <button type="button">{column.label || column.key || column.id}</button>
            <span
              aria-label={`Resize ${column.label || column.key || column.id}`}
              className={styles["designer-column-resize"]}
              role="separator"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                startColumnResize(event.clientX, Number(column.width || 12), (width) => resizeColumn({ type: "column", bandId, componentId: component.id, columnId: column.id }, width));
              }}
            />
          </div>
        ))}
      </div>
      {rows.length ? rows.map((row, rowIndex) => (
        <div className={styles["designer-table-row"]} key={`row-${rowIndex}`} style={{ gridTemplateColumns }}>
          {columns.map((column, columnIndex) => (
            <div className={styles["designer-table-cell"]} key={`${rowIndex}-${columnIndex}`} style={{ textAlign: column.horizontalAlign as React.CSSProperties["textAlign"] }}>
              {formatDesignerValue(getPath(row, column.key || ""), column.key, valueMode)}
            </div>
          ))}
        </div>
      )) : (
        <div className={styles["designer-table-empty"]}>No sample rows available. Switch to binding display mode or load a report result to preview real data.</div>
      )}
    </div>
  );
}

function PropertiesInspector({
  formulaTools,
  onConditionalCommand,
  deleteSelection,
  duplicateSelection,
  orientation,
  pageSize,
  payload,
  selection,
  selectedBand,
  setOrientation,
  setPageSize,
  setSelection,
  styleNames,
  updateColumn,
  updateComponent,
  updateDefinitionField,
  updateField,
  updateMetadataField,
  updateBand,
  updateNativeBand,
  updateNativeElement,
  updateNativeTable,
  addNativeCalculatedTableColumn,
  updateNativeTableColumn,
  reorderNativeTableColumn,
  addNativeTableSort,
  updateNativeTableSort,
  removeNativeTableSort,
  moveNativeTableSort,
  setNativeTableSortDirection,
  updateNativeTableGroup,
  updateNativeTableSummary,
  resizeNativeTable,
  fitNativeTableToPrintableWidth,
  alignNativeElements,
  fitNativeBandToContent,
  fitNativeContainerToContent,
  reparentNativeElement,
  moveBand,
  moveNativeBand,
  reorderNativeElement,
  width,
}: {
  formulaTools?: ReactNode;
  onConditionalCommand?: (targetKey: string, action: ConditionalRuleAction) => void;
  deleteSelection: () => void;
  duplicateSelection: () => void;
  orientation: DesignerPageOrientation;
  pageSize: DesignerPageSize;
  payload: FormattedInvoicePayload;
  selection: DesignerSelection;
  selectedBand: DesignerBand | null;
  setOrientation: (value: DesignerPageOrientation) => void;
  setPageSize: (value: DesignerPageSize) => void;
  setSelection: (selection: DesignerSelection) => void;
  styleNames: string[];
  updateColumn: (selection: Extract<DesignerSelection, { type: "column" }>, key: keyof Pick<DesignerColumn, "label" | "key" | "format" | "horizontalAlign" | "width">, value: unknown) => void;
  updateComponent: (selection: Extract<DesignerSelection, { type: "component" }>, key: "datasetId" | "styleId" | "outputs", value: unknown) => void;
  updateDefinitionField: (key: string, value: unknown) => void;
  updateField: (selection: Extract<DesignerSelection, { type: "field" }>, key: keyof Pick<DesignerField, "label" | "key" | "format" | "horizontalAlign" | "styleId" | "column" | "row" | "marginTop">, value: unknown) => void;
  updateMetadataField: (key: string, value: unknown) => void;
  updateBand: (bandId: string, key: "id" | "datasetId" | "styleId" | "outputs", value: unknown) => void;
  updateNativeBand: (bandId: string, key: "id" | "height" | "sizing", value: unknown) => void;
  updateNativeElement: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: Parameters<typeof commands.updateNativeElement>[2]) => void;
  updateNativeTable: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: Parameters<typeof commands.updateNativeTable>[2]) => void;
  addNativeCalculatedTableColumn: (bandId: string, tableId: string, options?: Parameters<typeof commands.addNativeCalculatedTableColumn>[3]) => void;
  updateNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, patch: Parameters<typeof commands.updateNativeTableColumn>[2]) => void;
  reorderNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, direction: -1 | 1) => void;
  addNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, field: string) => void;
  updateNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, patch: Parameters<typeof commands.updateNativeTableSort>[3]) => void;
  removeNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number) => void;
  moveNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, direction: -1 | 1) => void;
  setNativeTableSortDirection: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>, field: string, direction: "asc" | "desc", type?: "auto" | "date" | "number" | "string") => void;
  updateNativeTableGroup: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableGroup>[2]) => void;
  updateNativeTableSummary: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableSummary>[2]) => void;
  resizeNativeTable: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number) => void;
  fitNativeTableToPrintableWidth: (selection: Extract<DesignerSelection, { type: "nativeElement" }>) => void;
  alignNativeElements: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, alignment: Parameters<typeof commands.alignNativeElements>[2]) => void;
  fitNativeBandToContent: (bandId: string, height: number) => void;
  fitNativeContainerToContent: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Parameters<typeof commands.resizeNativeElement>[2]) => void;
  reparentNativeElement: (selection: Extract<DesignerSelection, { type: "nativeElement" }>, targetParentId: string | null, bounds: { x: number; y: number; width: number; height: number }) => void;
  moveBand: (bandId: string, direction: -1 | 1) => void;
  moveNativeBand: (bandId: string, direction: -1 | 1) => void;
  reorderNativeElement: (bandId: string, elementId: string, direction: -1 | 1) => void;
  width: number;
}) {
  const selectedComponent = selectedBand && selection.type !== "report" ? primaryComponent(selectedBand) : undefined;
  const selectedField = selection.type === "field" && selectedComponent?.kind === "keyValueGrid"
    ? selectedComponent.fields.find((field) => field.id === selection.fieldId)
    : undefined;
  const selectedColumn = selection.type === "column" && selectedComponent?.kind === "table"
    ? selectedComponent.columns.find((column) => column.id === selection.columnId)
    : undefined;
  const nativeBand = selection.type === "band" || selection.type === "nativeElement" || selection.type === "nativeTableColumn" || selection.type === "nativeTableGroup"
    ? nativeBandsFromPayload(payload).find((band) => String(band.id) === selection.bandId)
    : undefined;
  const nativeElement = selection.type === "nativeElement" && nativeBand
    ? findNativeElementInPayload(nativeBand, selection.elementIds[0])
    : selection.type === "nativeTableColumn" && nativeBand
      ? findNativeElementInPayload(nativeBand, selection.tableId)
    : selection.type === "nativeTableGroup" && nativeBand
      ? findNativeElementInPayload(nativeBand, selection.tableId)
    : undefined;
  const nativeTableColumn = selection.type === "nativeTableColumn" && nativeElement?.type === "table"
    ? asArray(nativeElement.columns).map(asRecord).find((column) => String(column.id) === selection.columnId)
    : undefined;
  const nativeTableColumnExpression = nativeTableColumn ? asRecord(nativeTableColumn.expr || nativeTableColumn.calculation) : {};
  const nativeTableColumnIsCalculated = Object.keys(nativeTableColumnExpression).length > 0;
  const datasetDescriptors = discoverReportDatasets(payload);
  const selectedTableDataset = nativeElement?.type === "table" ? datasetDescriptors.find((dataset) => dataset.id === String(nativeElement.dataset || "")) : undefined;
  const selectedTableDatasetBinding = nativeElement?.type === "table" ? String(nativeElement.dataset || "") : "";
  const selectedTableFields = selectedTableDataset?.fields || [];
  const nativeTableAuthoringSelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }> | null = nativeElement?.type === "table"
    ? selection.type === "nativeTableGroup"
      ? selection
      : selection.type === "nativeElement"
        ? selection
        : null
    : null;
  const nativeTableSummarySelection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }> | null = nativeElement?.type === "table"
    ? selection.type === "nativeTableGroup"
      ? selection
      : selection.type === "nativeElement"
        ? selection
        : null
    : null;
  const selectedColumnBinding = nativeTableColumn ? String(nativeTableColumn.binding || "") : "";
  const nativeTableColumns = nativeElement?.type === "table" ? asArray(nativeElement.columns).map(asRecord) : [];
  const nativeTableSortOptions = nativeSortFieldOptions(selectedTableFields, nativeTableColumns, "");
  const selectedColumnSortField = nativeTableColumn ? nativeColumnSortField(nativeTableColumn) : "";
  const selectedColumnSortMeta = nativeTableSortOptions.find((option) => option.value === selectedColumnSortField);
  const selectedColumnSortIndex = nativeElement?.type === "table" && selectedColumnSortField
    ? asArray(nativeElement.sort).map(asRecord).findIndex((descriptor) => String(descriptor.field || "") === selectedColumnSortField)
    : -1;
  const selectedColumnSortDescriptor = selectedColumnSortIndex >= 0 ? asRecord(asArray(nativeElement?.sort).map(asRecord)[selectedColumnSortIndex]) : null;
  const selectedColumnBindingValid = !nativeTableColumn || !selectedColumnBinding || selectedTableFields.some((field) => field.path === selectedColumnBinding);
  const nativePlan = isNativeReportsV31Payload(payload) ? resolveNativeLayoutPlan(payload) : null;
  const nativeParentInfo = selection.type === "nativeElement" && nativeBand
    ? findNativeParentInfo(nativeBand, selection.elementIds[0])
    : null;
  const nativeParentOptions = selection.type === "nativeElement" && nativeBand && nativePlan
    ? nativeReparentOptions(nativeBand, nativePlan, selection.bandId, selection.elementIds[0])
    : [];
  const assetOptions = Object.keys(asRecord(payload.assets));
  return (
    <aside className={styles["designer-inspector"]} aria-label="Properties inspector" style={{ width }}>
      <div className={styles["designer-inspector-header"]}>
        <strong>{inspectorTitle(selection)}</strong>
        {selection.type !== "report" && <button type="button" onClick={() => setSelection({ type: "report" })}>Document</button>}
      </div>
      <div className={styles["designer-inspector-body"]}>
        {formulaTools}
        {onConditionalCommand && <ConditionalRulesInspector payload={payload} selection={selection} datasets={datasetDescriptors} onCommand={onConditionalCommand} />}
        {selection.type === "report" && (
          <>
            <PropertySection title="Document">
              <TextInput label="Definition ID" value={String(payload.definitionId || "")} onChange={(value) => updateDefinitionField("definitionId", value)} />
              <TextInput label="Report name" value={String(asRecord(payload.metadata).reportName || "")} onChange={(value) => updateMetadataField("reportName", value)} />
            </PropertySection>
            <PropertySection title="Page setup">
              <SelectInput label="Page size" value={pageSize} options={["letter", "legal"]} optionLabels={["Letter", "Legal"]} onChange={(value) => setPageSize(value as DesignerPageSize)} />
              <SelectInput label="Orientation" value={orientation} options={["portrait", "landscape"]} optionLabels={["Portrait", "Landscape"]} onChange={(value) => setOrientation(value as DesignerPageOrientation)} />
            </PropertySection>
          </>
        )}
        {selection.type === "band" && nativeBand && (
          <>
            <PropertySection title="Native band">
              <TextInput label="Band ID" value={String(nativeBand.id || "")} onChange={(value) => updateNativeBand(selection.bandId, "id", value)} />
              <TextInput label="Role" value={nativeBandLabel(String(nativeBand.role || ""))} onChange={() => undefined} />
              <NumberInput label="Height (pt)" value={numberOrBlank(nativeBand.height)} onChange={(value) => updateNativeBand(selection.bandId, "height", value)} />
              <TextInput label="Sizing Mode" value={String(nativeBand.sizing || "fixed") === "flow" ? "Flow" : "Fixed"} onChange={() => undefined} />
            </PropertySection>
            <PropertySection title="Native authoring">
              <p className={styles["designer-empty-note"]}>Elements use point coordinates relative to the band content origin. Zoom and grid settings are editor-only.</p>
            </PropertySection>
            <PropertySection title="Sizing">
              <div className={styles["designer-action-row"]}>
                <button type="button" onClick={() => {
                  const height = nativePlan ? requiredBandHeight(nativePlan, selection.bandId) : null;
                  if (height !== null) fitNativeBandToContent(selection.bandId, height);
                }}>Fit to content</button>
              </div>
              <p className={styles["designer-empty-note"]}>Bands control report placement and pagination. Fixed height is preserved until you explicitly resize or fit content.</p>
            </PropertySection>
            <PropertySection title="Order">
              <div className={styles["designer-action-row"]}>
                <button type="button" onClick={() => moveNativeBand(selection.bandId, -1)}>Move up</button>
                <button type="button" onClick={() => moveNativeBand(selection.bandId, 1)}>Move down</button>
              </div>
            </PropertySection>
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "band" && !nativeBand && selectedBand && selectedComponent && (
          <>
            <PropertySection title="Band">
              <TextInput label="Section ID" value={selectedBand.legacyId || selectedBand.id} onChange={(value) => updateBand(selection.bandId, "id", value)} />
            </PropertySection>
            <PropertySection title="Order">
              <div className={styles["designer-action-row"]}>
                <button type="button" onClick={() => moveBand(selection.bandId, -1)}>Move up</button>
                <button type="button" onClick={() => moveBand(selection.bandId, 1)}>Move down</button>
              </div>
            </PropertySection>
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "component" && selectedComponent && (
          <>
            <PropertySection title="Data">
              {"datasetId" in selectedComponent && <TextInput label="Dataset" value={selectedComponent.datasetId || ""} onChange={(value) => updateComponent(selection, "datasetId", value)} />}
            </PropertySection>
            <PropertySection title="Appearance">
              {"styleId" in selectedComponent && <SelectInput label="Style" value={selectedComponent.styleId || ""} options={["", ...styleNames]} onChange={(value) => updateComponent(selection, "styleId", value || undefined)} />}
            </PropertySection>
            <PropertySection title="Behavior">
              <TextInput label="Outputs" value={selectedComponent.outputs.join(", ")} onChange={(value) => updateComponent(selection, "outputs", value.split(",").map((item) => item.trim()).filter(Boolean))} />
            </PropertySection>
          </>
        )}
        {selection.type === "field" && selectedField && (
          <>
            <PropertySection title="Content">
              <TextInput label="Label" value={selectedField.label || ""} onChange={(value) => updateField(selection, "label", value)} />
              <TextInput label="Key" value={selectedField.key || ""} onChange={(value) => updateField(selection, "key", value)} />
            </PropertySection>
            <PropertySection title="Appearance">
              <SelectInput label="Format" value={selectedField.format || ""} options={["", "date", "number", "money"]} onChange={(value) => updateField(selection, "format", value || undefined)} />
              <SelectInput label="Alignment" value={selectedField.horizontalAlign || ""} options={["", "left", "center", "right"]} onChange={(value) => updateField(selection, "horizontalAlign", value || undefined)} />
              <TextInput label="Style" value={selectedField.styleId || ""} onChange={(value) => updateField(selection, "styleId", value || undefined)} />
            </PropertySection>
            <PropertySection title="Placement">
              <NumberInput label="Column" value={numberOrBlank(selectedField.column)} onChange={(value) => updateField(selection, "column", value)} />
              <NumberInput label="Row" value={numberOrBlank(selectedField.row)} onChange={(value) => updateField(selection, "row", value)} />
              <NumberInput label="Margin top" value={numberOrBlank(selectedField.marginTop)} onChange={(value) => updateField(selection, "marginTop", value)} />
            </PropertySection>
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "column" && selectedColumn && (
          <>
            <PropertySection title="Content">
              <TextInput label="Label" value={selectedColumn.label || ""} onChange={(value) => updateColumn(selection, "label", value)} />
              <TextInput label="Key" value={selectedColumn.key || ""} onChange={(value) => updateColumn(selection, "key", value)} />
            </PropertySection>
            <PropertySection title="Appearance">
              <SelectInput label="Format" value={selectedColumn.format || ""} options={["", "date", "number", "money"]} onChange={(value) => updateColumn(selection, "format", value || undefined)} />
              <SelectInput label="Alignment" value={selectedColumn.horizontalAlign || ""} options={["", "left", "center", "right"]} onChange={(value) => updateColumn(selection, "horizontalAlign", value || undefined)} />
              <NumberInput label="Width" value={numberOrBlank(selectedColumn.width)} onChange={(value) => updateColumn(selection, "width", value)} />
            </PropertySection>
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "nativeTableColumn" && nativeTableColumn && (
          <>
            <PropertySection title="Table column">
              <TextInput label="Heading" value={String(nativeTableColumn.label || "")} onChange={(value) => updateNativeTableColumn(selection, { label: value })} />
              <SelectInput
                label="Column type"
                value={nativeTableColumnIsCalculated ? "calculated" : "bound"}
                options={["bound", "calculated"]}
                optionLabels={["Bound field", "Calculated field"]}
                onChange={(value) => {
                  if (value === "calculated") updateNativeTableColumn(selection, { expr: nativeTableColumnIsCalculated ? nativeTableColumnExpression : defaultCalculatedExpression(selectedTableFields), format: String(nativeTableColumn.format || "number"), horizontalAlign: "right" as const });
                  else updateNativeTableColumn(selection, { binding: selectedColumnBinding || selectedTableFields[0]?.path || "" });
                }}
              />
              {!nativeTableColumnIsCalculated ? (
                <SelectWithDescription
                  description={selectedColumnBindingValid ? "Choose a field from the table dataset." : `Binding "${selectedColumnBinding}" is not present in the selected dataset.`}
                  label="Binding"
                  onChange={(value) => updateNativeTableColumn(selection, { binding: value })}
                  options={fieldSelectOptions(selectedTableFields, selectedColumnBinding)}
                  value={selectedColumnBinding}
                />
              ) : nativeTableColumnExpression.typed !== undefined ? (
                <p className={styles["designer-empty-note"]}>Typed formula (internal). Use Formula Builder above. The legacy arithmetic editor and live calculated preview are not available for this expression.</p>
              ) : (
                <CalculatedExpressionBuilder
                  column={nativeTableColumn}
                  fields={selectedTableFields}
                  payload={payload}
                  selection={selection}
                  table={nativeElement || {}}
                  updateNativeTableColumn={updateNativeTableColumn}
                />
              )}
              <NumberInput label="Width (pt)" value={numberOrBlank(nativeTableColumn.width)} onChange={(value) => updateNativeTableColumn(selection, { width: value })} />
              <CheckboxInput label="Show column" checked={nativeTableColumn.visible !== false && nativeTableColumn.hidden !== true} onChange={(checked) => updateNativeTableColumn(selection, { visible: checked })} />
            </PropertySection>
            <PropertySection title="Column sorting">
              {selectedColumnSortField ? (
                <>
                  <p className={styles["designer-empty-note"]}>
                    {selectedColumnSortIndex >= 0
                      ? `Current table sort priority ${selectedColumnSortIndex + 1}: ${String(selectedColumnSortDescriptor?.direction || "asc") === "desc" ? "Descending" : "Ascending"}.`
                      : "Add this column to the parent table sort rules."}
                  </p>
                  <div className={styles["designer-action-row"]}>
                    <button type="button" onClick={() => setNativeTableSortDirection(selection, selectedColumnSortField, "asc", sortTypeForColumn(nativeTableColumn) as "auto" | "date" | "number" | "string")}>Sort Ascending</button>
                    <button type="button" onClick={() => setNativeTableSortDirection(selection, selectedColumnSortField, "desc", sortTypeForColumn(nativeTableColumn) as "auto" | "date" | "number" | "string")}>Sort Descending</button>
                    <button type="button" onClick={() => setSelection({ type: "nativeElement", bandId: selection.bandId, elementIds: [selection.tableId] })}>Manage Table Sorting</button>
                  </div>
                  <p className={styles["designer-empty-note"]}>Uses {selectedColumnSortMeta?.label || selectedColumnSortField} ({selectedColumnSortField}), not the display heading.</p>
                </>
              ) : (
                <>
                  <p className={styles["designer-empty-note"]}>Bind this column to a dataset field or supported calculated column before sorting.</p>
                  <div className={styles["designer-action-row"]}>
                    <button type="button" onClick={() => setSelection({ type: "nativeElement", bandId: selection.bandId, elementIds: [selection.tableId] })}>Manage Table Sorting</button>
                  </div>
                </>
              )}
            </PropertySection>
            <PropertySection title="Formatting">
              <SelectInput label="Format" value={String(nativeTableColumn.format || "")} options={["", "text", "date", "number", "money"]} onChange={(value) => updateNativeTableColumn(selection, { format: value || undefined })} />
              <SelectInput label="Alignment" value={String(nativeTableColumn.horizontalAlign || "left")} options={["left", "center", "right"]} onChange={(value) => updateNativeTableColumn(selection, { horizontalAlign: value as "left" | "center" | "right" })} />
              <TextInput label="Text color" value={String(asRecord(nativeTableColumn.style).color || "")} onChange={(value) => updateNativeTableColumn(selection, { color: value || undefined })} />
              <TextInput label="Background color" value={String(asRecord(nativeTableColumn.style).backgroundColor || "")} onChange={(value) => updateNativeTableColumn(selection, { backgroundColor: value || undefined })} />
            </PropertySection>
            <PropertySection title="Column order">
              <div className={styles["designer-action-row"]}>
                <button type="button" onClick={() => reorderNativeTableColumn(selection, -1)}>Move left</button>
                <button type="button" onClick={() => reorderNativeTableColumn(selection, 1)}>Move right</button>
              </div>
            </PropertySection>
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "nativeElement" && nativeElement && (
          <>
            <PropertySection title="Parent">
              <TextInput label="Current parent" value={nativeParentInfo?.label || nativeBandLabel(String(nativeBand?.role || ""))} onChange={() => undefined} />
              {nativeParentOptions.length > 1 && (
                <SelectInput
                  label="Move to parent"
                  value={nativeParentInfo?.parentId || ""}
                  options={nativeParentOptions.map((option) => option.value)}
                  optionLabels={nativeParentOptions.map((option) => option.label)}
                  onChange={(value) => {
                    if (!nativePlan || selection.type !== "nativeElement") return;
                    const next = reparentBoundsForTarget(nativePlan, selection.bandId, selection.elementIds[0], value || null);
                    if (next) reparentNativeElement(selection, value || null, next);
                  }}
                />
              )}
              <p className={styles["designer-empty-note"]}>Parentage is explicit. Visual overlap does not move an element into another band or container.</p>
            </PropertySection>
            {String(nativeElement.type) === "table" && (
              <>
                <PropertySection title="Table data">
                  <SelectWithDescription
                    description={datasetDescription(selectedTableDatasetBinding, selectedTableDataset, datasetDescriptors)}
                    label="Dataset"
                    onChange={(value) => updateNativeTable(selection, { dataset: value })}
                    options={datasetSelectOptions(datasetDescriptors, selectedTableDatasetBinding)}
                    value={selectedTableDatasetBinding}
                  />
                  <SelectInput label="Repeat headers" value={asRecord(nativeElement.header).repeat === false ? "false" : "true"} options={["true", "false"]} optionLabels={["Yes", "No"]} onChange={(value) => updateNativeTable(selection, { repeatHeader: value === "true" })} />
                  <SelectInput label="Footer totals" value={asRecord(nativeElement.footer).enabled ? "true" : "false"} options={["false", "true"]} optionLabels={["Disabled", "Enabled"]} onChange={(value) => updateNativeTable(selection, { footerEnabled: value === "true" })} />
                </PropertySection>
                <PropertySection title="Add column">
                  <p className={styles["designer-empty-note"]}>Create calculated fields from this table's dataset without editing JSON.</p>
                  <div className={styles["designer-action-row"]}>
                    <button
                      type="button"
                      onClick={() => addNativeCalculatedTableColumn(selection.bandId, selection.elementIds[0], {
                        label: "Calculated Amount",
                        expr: defaultCalculatedExpression(selectedTableFields),
                        format: "money",
                      })}
                    >
                      Add Column → Calculated Field
                    </button>
                  </div>
                </PropertySection>
                {nativeTableAuthoringSelection && !isNativeSummaryRegionSelection(nativeTableAuthoringSelection) && (
                  <NativeTableSortingGroupingInspector
                    addNativeTableSort={addNativeTableSort}
                    fields={nativeTableSortOptions}
                    groupFields={fieldSelectOptions(selectedTableFields, String(asRecord(asArray(nativeElement.groups).map(asRecord)[0]).field || ""))}
                    moveNativeTableSort={moveNativeTableSort}
                    removeNativeTableSort={removeNativeTableSort}
                    selection={nativeTableAuthoringSelection}
                    table={nativeElement}
                    updateNativeTableGroup={updateNativeTableGroup}
                    updateNativeTableSort={updateNativeTableSort}
                  />
                )}
                {nativeTableSummarySelection && (
                  <NativeTableSummaryInspector
                    fields={nativeSummaryFieldDescriptors(selectedTableFields, nativeTableColumns)}
                    selection={nativeTableSummarySelection}
                    table={nativeElement}
                    updateNativeTableSummary={updateNativeTableSummary}
                  />
                )}
                <PropertySection title="Table appearance">
                  <NumberInput label="Row padding (pt)" value={numberOrBlank(asRecord(nativeElement.row).padding)} onChange={(value) => updateNativeTable(selection, { rowPadding: value })} />
                  <NumberInput label="Font size" value={numberOrBlank(asRecord(nativeElement.style).fontSize)} onChange={(value) => updateNativeTable(selection, { fontSize: value })} />
                  <TextInput label="Header background" value={String(asRecord(nativeElement.style).headerBackgroundColor || "")} onChange={(value) => updateNativeTable(selection, { headerBackgroundColor: value || undefined })} />
                  <TextInput label="Footer background" value={String(asRecord(nativeElement.style).footerBackgroundColor || "")} onChange={(value) => updateNativeTable(selection, { footerBackgroundColor: value || undefined })} />
                  <TextInput label="Border color" value={String(asRecord(nativeElement.style).borderColor || "")} onChange={(value) => updateNativeTable(selection, { borderColor: value || undefined })} />
                </PropertySection>
              </>
            )}
            <PropertySection title="Content">
              {String(nativeElement.type) === "text" && (asRecord(nativeElement.expr).typed !== undefined
                ? <p>Typed presentation value. Use Formula Builder to edit or remove it; layout and formatting remain editable.</p>
                : <TextInput label="Text" value={String(nativeElement.text || "")} onChange={(value) => updateNativeElement(selection, { text: value })} />)}
              {String(nativeElement.type) === "field" && <TextInput label="Binding" value={String(nativeElement.binding || "")} onChange={(value) => updateNativeElement(selection, { binding: value })} />}
              {String(nativeElement.type) === "image" && (
                <>
                  <SelectInput label="Asset" value={String(nativeElement.asset || "")} options={selectOptionsWithCurrent(assetOptions, String(nativeElement.asset || ""))} onChange={(value) => updateNativeElement(selection, { asset: value })} />
                  <SelectInput label="Fit" value={String(nativeElement.fit || "contain")} options={["contain", "cover", "stretch"]} optionLabels={["Contain", "Cover", "Stretch"]} onChange={(value) => updateNativeElement(selection, { fit: value as "contain" | "cover" | "stretch" })} />
                  <SelectInput label="Aspect ratio" value={nativeElement.lockAspectRatio === false ? "false" : "true"} options={["true", "false"]} optionLabels={["Locked", "Unlocked"]} onChange={(value) => updateNativeElement(selection, { lockAspectRatio: value === "true" })} />
                  {!assetOptions.length && <p className={styles["designer-empty-note"]}>No image assets are available in this report payload.</p>}
                </>
              )}
              {(String(nativeElement.type) === "container" || String(nativeElement.type) === "table") && <TextInput label="Element ID" value={String(nativeElement.id || "")} onChange={() => undefined} />}
              {String(nativeElement.type) !== "container" && String(nativeElement.type) !== "table" && String(nativeElement.type) !== "image" && <SelectInput label="Format" value={String(nativeElement.format || "")} options={["", "date", "number", "money"]} onChange={(value) => updateNativeElement(selection, { format: value || undefined })} />}
            </PropertySection>
            <PropertySection title="Layout">
              <NumberInput label="X (pt)" value={numberOrBlank(nativeElement.x)} onChange={(value) => updateNativeElement(selection, { x: value })} />
              <NumberInput label="Y (pt)" value={numberOrBlank(nativeElement.y)} onChange={(value) => updateNativeElement(selection, { y: value })} />
              <NumberInput label="Width (pt)" value={numberOrBlank(nativeElement.width)} onChange={(value) => String(nativeElement.type) === "table" ? resizeNativeTable(selection, Number(value) || 0) : updateNativeElement(selection, { width: value })} />
              <NumberInput label="Height (pt)" value={numberOrBlank(nativeElement.height)} onChange={(value) => updateNativeElement(selection, { height: value })} />
              {String(nativeElement.type) === "table" && (
                <div className={styles["designer-action-row"]}>
                  <button type="button" onClick={() => fitNativeTableToPrintableWidth(selection)}>Fit table to printable width</button>
                </div>
              )}
              {String(nativeElement.type) === "container" && (
                <>
                  <SelectInput label="Layout mode" value={String(asRecord(nativeElement.layout).mode || "absolute")} options={["absolute", "vertical", "columns"]} optionLabels={["Absolute", "Vertical stack", "Columns"]} onChange={(value) => updateNativeElement(selection, { layoutMode: value as "absolute" | "vertical" | "columns" })} />
                  <NumberInput label="Padding (pt)" value={numberOrBlank(typeof nativeElement.padding === "number" ? nativeElement.padding : asRecord(nativeElement.padding).top)} onChange={(value) => updateNativeElement(selection, { padding: value })} />
                  <NumberInput label="Gap (pt)" value={numberOrBlank(asRecord(nativeElement.layout).gap ?? nativeElement.gap)} onChange={(value) => updateNativeElement(selection, { gap: value })} />
                </>
              )}
            </PropertySection>
            <PropertySection title="Appearance">
              <NumberInput label="Font size" value={numberOrBlank(asRecord(nativeElement.style).fontSize)} onChange={(value) => updateNativeElement(selection, { fontSize: value })} />
              <SelectInput label="Horizontal align" value={String(asRecord(nativeElement.style).horizontalAlign || "left")} options={["left", "center", "right"]} onChange={(value) => updateNativeElement(selection, { horizontalAlign: value as "left" | "center" | "right" })} />
              <SelectInput label="Vertical align" value={String(asRecord(nativeElement.style).verticalAlign || "top")} options={["top", "middle", "bottom"]} onChange={(value) => updateNativeElement(selection, { verticalAlign: value as "top" | "middle" | "bottom" })} />
              <TextInput label="Text color" value={String(asRecord(nativeElement.style).color || "")} onChange={(value) => updateNativeElement(selection, { color: value || undefined })} />
              <TextInput label="Background color" value={String(asRecord(nativeElement.style).backgroundColor || "")} onChange={(value) => updateNativeElement(selection, { backgroundColor: value || undefined })} />
              {String(nativeElement.type) === "container" && (
                <>
                  <TextInput label="Border color" value={String(asRecord(nativeElement.style).borderColor || "")} onChange={(value) => updateNativeElement(selection, { borderColor: value || undefined })} />
                  <NumberInput label="Border width" value={numberOrBlank(asRecord(nativeElement.style).borderWidth)} onChange={(value) => updateNativeElement(selection, { borderWidth: value })} />
                </>
              )}
              <NumberInput label="Line height" value={numberOrBlank(asRecord(nativeElement.style).lineHeight)} onChange={(value) => updateNativeElement(selection, { lineHeight: value })} />
              <div className={styles["designer-action-row"]}>
                <button type="button" onClick={() => updateNativeElement(selection, { bold: !Boolean(asRecord(nativeElement.style).bold) })}>Bold</button>
                <button type="button" onClick={() => updateNativeElement(selection, { italic: !Boolean(asRecord(nativeElement.style).italic) })}>Italic</button>
              </div>
            </PropertySection>
            {selection.elementIds.length > 1 && (
              <PropertySection title="Align and distribute">
                <div className={styles["designer-action-row"]}>
                  {(["left", "hCenter", "right", "top", "vCenter", "bottom", "distributeH", "distributeV"] as const).map((alignment) => (
                    <button key={alignment} type="button" onClick={() => alignNativeElements(selection, alignment)}>{titleize(alignment)}</button>
                  ))}
                </div>
              </PropertySection>
            )}
            {String(nativeElement.type) === "container" && (
              <PropertySection title="Container children">
                <div className={styles["designer-action-row"]}>
                  <button type="button" onClick={() => {
                    if (!nativePlan || selection.type !== "nativeElement") return;
                    const bounds = requiredContainerBounds(nativePlan, selection.bandId, selection.elementIds[0]);
                    if (bounds) fitNativeContainerToContent(selection, bounds);
                  }}>Fit to content</button>
                  <button type="button" onClick={() => reorderNativeElement(selection.bandId, selection.elementIds[0], -1)}>Move up</button>
                  <button type="button" onClick={() => reorderNativeElement(selection.bandId, selection.elementIds[0], 1)}>Move down</button>
                </div>
                <p className={styles["designer-empty-note"]}>Structured containers support absolute, vertical stack, and deterministic columns. Nesting is supported up to 6 levels.</p>
              </PropertySection>
            )}
            <SelectionActions deleteSelection={deleteSelection} duplicateSelection={duplicateSelection} />
          </>
        )}
        {selection.type === "nativeTableGroup" && nativeElement?.type === "table" && nativeTableAuthoringSelection && (
          <>
            {!isNativeSummaryRegionSelection(nativeTableAuthoringSelection) && (
              <NativeTableSortingGroupingInspector
                addNativeTableSort={addNativeTableSort}
                fields={nativeTableSortOptions}
                groupFields={fieldSelectOptions(selectedTableFields, String(asRecord(asArray(nativeElement.groups).map(asRecord)[0]).field || ""))}
                moveNativeTableSort={moveNativeTableSort}
                removeNativeTableSort={removeNativeTableSort}
                selection={nativeTableAuthoringSelection}
                table={nativeElement}
                updateNativeTableGroup={updateNativeTableGroup}
                updateNativeTableSort={updateNativeTableSort}
              />
            )}
            <NativeTableSummaryInspector
              fields={nativeSummaryFieldDescriptors(selectedTableFields, nativeTableColumns)}
              selection={nativeTableAuthoringSelection}
              table={nativeElement}
              updateNativeTableSummary={updateNativeTableSummary}
            />
          </>
        )}
      </div>
    </aside>
  );
}

function NativeTableSortingGroupingInspector({
  addNativeTableSort,
  fields,
  groupFields,
  moveNativeTableSort,
  removeNativeTableSort,
  selection,
  table,
  updateNativeTableGroup,
  updateNativeTableSort,
}: {
  addNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, field: string) => void;
  fields: Array<{ value: string; label: string; detail?: string; invalid?: boolean; type?: string }>;
  groupFields: Array<{ value: string; label: string; detail?: string; invalid?: boolean }>;
  moveNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, direction: -1 | 1) => void;
  removeNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number) => void;
  selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>;
  table: JsonRecord;
  updateNativeTableGroup: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableGroup>[2]) => void;
  updateNativeTableSort: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, patch: Parameters<typeof commands.updateNativeTableSort>[3]) => void;
}) {
  const sort = asArray(table.sort).map(asRecord);
  const group = asRecord(asArray(table.groups).map(asRecord)[0]);
  const groupEnabled = Object.keys(group).length > 0;
  const groupPagination = asRecord(group.pagination);
  const usedSortFields = new Set(sort.map((descriptor) => String(descriptor.field || "")).filter(Boolean));
  const firstField = fields.find((field) => !field.invalid && !usedSortFields.has(field.value))?.value
    || fields.find((field) => !field.invalid)?.value
    || fields[0]?.value
    || "";
  const groupField = String(group.field || asRecord(group.key).field || "");
  const groupedFieldColumn = asArray(table.columns).map(asRecord).find((column) => nativeColumnSortField(column) === groupField || String(column.key || column.id || "") === groupField);
  const footer = asRecord(group.footer);
  const header = asRecord(group.header);
  return (
    <>
      <PropertySection title="Sorting">
        <p className={styles["designer-empty-note"]}>Rules run in priority order. With grouping, they sort records inside each group.</p>
        <div className={styles["designer-rule-list"]}>
          {sort.map((descriptor, index) => {
            const field = String(descriptor.field || "");
            const fieldOptions = withCurrentOption(fields, field);
            const fieldMeta = fieldOptions.find((option) => option.value === field);
            return (
              <div className={styles["designer-rule-card"]} data-testid={`native-sort-rule-${index}`} key={`${field}-${index}`}>
                <div className={styles["designer-rule-heading"]}>
                  <strong>{index + 1}. {fieldMeta?.label || field || "Unassigned field"}</strong>
                  <span>{fieldMeta?.detail || "sorting rule"}</span>
                </div>
                <SelectWithDescription label="Field" value={field} options={fieldOptions} onChange={(value) => updateNativeTableSort(selection, index, { field: value })} />
                <div className={styles["designer-two-column"]}>
                  <SelectInput label="Direction" value={String(descriptor.direction || "asc")} options={["asc", "desc"]} optionLabels={sortDirectionLabels(fieldMeta?.type)} onChange={(value) => updateNativeTableSort(selection, index, { direction: value as "asc" | "desc" })} />
                  <SelectInput label="Null handling" value={String(descriptor.nulls || "last")} options={["last", "first"]} optionLabels={["Nulls Last", "Nulls First"]} onChange={(value) => updateNativeTableSort(selection, index, { nulls: value as "first" | "last" })} />
                </div>
                <SelectInput label="Sort type" value={String(descriptor.type || "auto")} options={["auto", "date", "number", "string"]} optionLabels={["Auto", "Date", "Number", "String"]} onChange={(value) => updateNativeTableSort(selection, index, { type: value as "auto" | "date" | "number" | "string" })} />
                <div className={styles["designer-action-row"]}>
                  <button aria-label={`Move sorting rule ${index + 1} up`} disabled={index === 0} type="button" onClick={() => moveNativeTableSort(selection, index, -1)}>Move up</button>
                  <button aria-label={`Move sorting rule ${index + 1} down`} disabled={index === sort.length - 1} type="button" onClick={() => moveNativeTableSort(selection, index, 1)}>Move down</button>
                  <button aria-label={`Remove sorting rule ${index + 1}`} className={styles["designer-danger-btn"]} type="button" onClick={() => removeNativeTableSort(selection, index)}>Remove</button>
                </div>
              </div>
            );
          })}
          {!sort.length && <p className={styles["designer-empty-note"]}>No sorting rules. Records use source order unless grouped by group key.</p>}
        </div>
        <div className={styles["designer-action-row"]}>
          <button disabled={!firstField} type="button" onClick={() => firstField && addNativeTableSort(selection, firstField)}>Add sorting rule</button>
        </div>
      </PropertySection>
      <PropertySection title="Grouping">
        <p className={styles["designer-empty-note"]}>Group order controls group sequence. Sorting controls detail row order within each group.</p>
        <CheckboxInput label="Enable grouping" checked={groupEnabled} onChange={(checked) => updateNativeTableGroup(selection, { enabled: checked, field: groupField || groupFields[0]?.value || firstField })} />
        {groupEnabled && (
          <>
            <div className={styles["designer-property-subsection"]}>
              <strong>Grouping</strong>
              <SelectWithDescription label="Group field" value={groupField} options={withCurrentOption(groupFields, groupField)} onChange={(value) => updateNativeTableGroup(selection, { field: value })} />
              <SelectInput label="Group order" value={String(group.sort || "asc")} options={["asc", "desc"]} optionLabels={["Ascending", "Descending"]} onChange={(value) => updateNativeTableGroup(selection, { sort: value as "asc" | "desc" })} />
              <CheckboxInput label="Show grouped field in detail table" checked={groupedFieldColumn?.visible !== false} onChange={(checked) => updateNativeTableGroup(selection, { showGroupedFieldInDetail: checked })} />
            </div>
            <div className={styles["designer-property-subsection"]}>
              <strong>Group header and footer</strong>
              <CheckboxInput label="Show group header" checked={header.visible !== false} onChange={(checked) => updateNativeTableGroup(selection, { headerVisible: checked })} />
              <TextInput label="Group header label" value={String(header.label || group.label || "Group")} onChange={(value) => updateNativeTableGroup(selection, { headerLabel: value })} />
              <CheckboxInput label="Show group footer" checked={footer.visible !== false} onChange={(checked) => updateNativeTableGroup(selection, { footerVisible: checked })} />
            </div>
            <div className={styles["designer-property-subsection"]}>
              <strong>Group layout and pagination</strong>
              <NumberInput label="Space between groups (pt)" value={numberOrBlank(groupPagination.spacingAfter ?? group.spacingAfter)} onChange={(value) => updateNativeTableGroup(selection, { spacingAfter: Number(value) || 0 })} />
              <div className={styles["designer-checkbox-grid"]}>
                <CheckboxInput label="Start each group on a new page" checked={groupPagination.pageBreakBefore === true} onChange={(checked) => updateNativeTableGroup(selection, { pageBreakBefore: checked })} />
                <CheckboxInput label="Repeat group header on continuation pages" checked={groupPagination.repeatHeaderOnContinuation === true || group.repeatHeaderOnContinuation === true} onChange={(checked) => updateNativeTableGroup(selection, { repeatHeaderOnContinuation: checked })} />
                <CheckboxInput label="Keep header with first detail row" checked={groupPagination.keepHeaderWithFirstRow !== false} onChange={(checked) => updateNativeTableGroup(selection, { keepHeaderWithFirstRow: checked })} />
                <CheckboxInput label="Keep footer with final detail row" checked={groupPagination.keepFooterWithLastRow !== false} onChange={(checked) => updateNativeTableGroup(selection, { keepFooterWithLastRow: checked })} />
              </div>
            </div>
          </>
        )}
      </PropertySection>
    </>
  );
}

function NativeTableSummaryInspector({
  fields,
  selection,
  table,
  updateNativeTableSummary,
}: {
  fields: FieldDescriptor[];
  selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>;
  table: JsonRecord;
  updateNativeTableSummary: (selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>, patch: Parameters<typeof commands.updateNativeTableSummary>[2]) => void;
}) {
  const region = selection.type === "nativeTableGroup" ? selection.region : undefined;
  const target = region === "footer" ? "groupFooter" : region === "tableFooter" ? "tableFooter" : null;
  const group = asRecord(asArray(table.groups).map(asRecord)[0]);
  const footer = target === "groupFooter" ? asRecord(group.footer) : target === "tableFooter" ? asRecord(table.footer) : null;
  const allColumns = asArray(table.columns).map(asRecord);
  const visibleColumns = allColumns.filter((column) => column.visible !== false);
  const columnsForSelect = visibleColumns.length ? visibleColumns : allColumns;
  const footerCells = footer ? asArray(footer.cells).map(asRecord) : [];
  const labelCell = footerCells.find((cell) => !cell.aggregate) || footerCells[0] || {};
  const aggregateCells = footerCells.filter((cell) => cell.aggregate);
  const defaultScope = target === "groupFooter" ? "group" : "report";
  if (!target || !footer) {
    return (
      <PropertySection title="Summary rows">
        <p className={styles["designer-empty-note"]}>Select Group Footer or Table Footer to configure subtotal and grand-total rows.</p>
      </PropertySection>
    );
  }
  return (
    <PropertySection title={target === "groupFooter" ? "Group Footer — Subtotal" : "Table Footer — Grand Total"}>
      <p className={styles["designer-empty-note"]}>{target === "groupFooter" ? "Group scope totals include all records in the current group, not just the page fragment." : "Report scope totals include every applicable record in the table."}</p>
      <CheckboxInput label={target === "groupFooter" ? "Enable group footer" : "Enable table footer"} checked={target === "groupFooter" ? footer.visible !== false : footer.enabled !== false} onChange={(checked) => updateNativeTableSummary(selection, { target, enabled: checked })} />
      <TextInput label="Label text" value={String(labelCell.value || (target === "groupFooter" ? "Subtotal" : "Grand Total"))} onChange={(value) => updateNativeTableSummary(selection, { target, label: value })} />
      <NumberInput label="Label span (visible columns)" value={numberOrBlank(visibleSpanForNativeCell(allColumns, labelCell) || Math.max(1, columnsForSelect.length - 1))} onChange={(value) => updateNativeTableSummary(selection, { target, labelColSpan: Number(value) || 1 })} />
      <NumberInput label="Row height (pt)" value={numberOrBlank(footer.height || footer.minHeight)} onChange={(value) => updateNativeTableSummary(selection, { target, rowHeight: Number(value) || 0 })} />
      <div className={styles["designer-property-subsection"]}>
        <strong>Appearance</strong>
        <NumberInput label="Font size" value={numberOrBlank(asRecord(labelCell.style).fontSize)} onChange={(value) => updateNativeTableSummary(selection, { target, fontSize: value })} />
        <TextInput label="Text color" value={String(asRecord(labelCell.style).color || "")} onChange={(value) => updateNativeTableSummary(selection, { target, color: value })} />
        <TextInput label="Background" value={String(asRecord(labelCell.style).backgroundColor || "")} onChange={(value) => updateNativeTableSummary(selection, { target, backgroundColor: value })} />
        <CheckboxInput label="Bold summary row" checked={asRecord(labelCell.style).bold !== false} onChange={(checked) => updateNativeTableSummary(selection, { target, bold: checked })} />
      </div>
      <div className={styles["designer-rule-list"]}>
        {aggregateCells.map((cell, index) => {
          const aggregate = asRecord(cell.aggregate);
          return (
            <div className={styles["designer-rule-card"]} data-testid={`native-summary-aggregate-${target}-${index}`} key={`${String(cell.columnId)}-${index}`}>
              <div className={styles["designer-rule-heading"]}>
                <strong>{index + 1}. {String(aggregate.fn || "sum").toUpperCase()} {String(aggregate.field || "field")}</strong>
                <span>{String(aggregate.scope || defaultScope)} scope · output {String(cell.columnId || "column")}</span>
              </div>
              <SelectInput label="Function" value={String(aggregate.fn || "sum")} options={["sum", "count", "min", "max", "avg"]} optionLabels={["Sum", "Count", "Minimum", "Maximum", "Average"]} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { fn: value as "sum" | "count" | "min" | "max" | "avg" } })} />
              <SelectWithDescription label="Source field" value={String(aggregate.field || "")} options={fieldSelectOptions(fields, String(aggregate.field || ""))} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { field: value } })} />
              <SelectInput label="Scope" value={String(aggregate.scope || defaultScope)} options={target === "groupFooter" ? ["group", "report"] : ["report"]} optionLabels={target === "groupFooter" ? ["Group", "Report"] : ["Report"]} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { scope: value as "group" | "report" } })} />
              <SelectInput label="Output column" value={String(cell.columnId || "")} options={columnsForSelect.map((column) => String(column.id || ""))} optionLabels={columnsForSelect.map((column) => String(column.label || column.id || ""))} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { columnId: value } })} />
              <SelectInput label="Format" value={String(cell.format || "")} options={["", "money", "number", "date", "text"]} optionLabels={["Default", "Money", "Number", "Date", "Text"]} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { format: value } })} />
              <SelectInput label="Alignment" value={String(cell.horizontalAlign || asRecord(cell.style).horizontalAlign || "right")} options={["left", "center", "right"]} optionLabels={["Left", "Center", "Right"]} onChange={(value) => updateNativeTableSummary(selection, { target, aggregateIndex: index, aggregate: { horizontalAlign: value as "left" | "center" | "right" } })} />
              <div className={styles["designer-action-row"]}>
                <button className={styles["designer-danger-btn"]} type="button" onClick={() => updateNativeTableSummary(selection, { target, removeAggregateIndex: index })}>Remove aggregate</button>
              </div>
            </div>
          );
        })}
        {!aggregateCells.length && <p className={styles["designer-empty-note"]}>No aggregate cells configured yet.</p>}
      </div>
      <div className={styles["designer-action-row"]}>
        <button type="button" onClick={() => updateNativeTableSummary(selection, { target, addAggregate: true })}>Add aggregate</button>
      </div>
    </PropertySection>
  );
}

function visibleSpanForNativeCell(columns: JsonRecord[], cell: JsonRecord): number {
  const startId = String(cell.columnId || "");
  const startIndex = Math.max(0, columns.findIndex((column) => String(column.id || "") === startId));
  const span = Math.max(1, Number(cell.colSpan || 1) || 1);
  return columns.slice(startIndex, startIndex + span).filter((column) => column.visible !== false && column.hidden !== true).length;
}

function isNativeSummaryRegionSelection(selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>): boolean {
  return selection.type === "nativeTableGroup" && (selection.region === "footer" || selection.region === "tableFooter");
}

function CalculatedExpressionBuilder({
  column,
  fields,
  payload,
  selection,
  table,
  updateNativeTableColumn,
}: {
  column: JsonRecord;
  fields: FieldDescriptor[];
  payload: FormattedInvoicePayload;
  selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>;
  table: JsonRecord;
  updateNativeTableColumn: (selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, patch: Parameters<typeof commands.updateNativeTableColumn>[2]) => void;
}) {
  const expression = asRecord(column.expr || column.calculation);
  const fieldOptions = expressionFieldOptions(fields, table, selection.columnId);
  const summary = expressionSummary(expression, fieldOptions);
  const sample = calculatedColumnSample(payload, selection.tableId, selection.columnId);
  return (
    <div className={styles["calculated-expression-builder"]} data-testid="native-calculated-expression-builder">
      <div className={styles["calculated-expression-summary"]}>
        <strong>Expression</strong>
        <span>{summary}</span>
      </div>
      <ExpressionNodeEditor
        expression={expression}
        fieldOptions={fieldOptions}
        onChange={(next) => updateNativeTableColumn(selection, { expr: next })}
      />
      <div className={styles["calculated-expression-preview"]}>
        <strong>Preview result</strong>
        <span>{sample.status === "ok" ? sample.value : sample.message}</span>
      </div>
      <div className={styles["designer-action-row"]}>
        <button type="button" onClick={() => updateNativeTableColumn(selection, { expr: { op: "add", left: expression, right: { const: 0 } } })}>Add operation</button>
        <button type="button" onClick={() => updateNativeTableColumn(selection, { expr: defaultCalculatedExpression(fields) })}>Reset expression</button>
      </div>
    </div>
  );
}

function ExpressionNodeEditor({
  expression,
  fieldOptions,
  onChange,
}: {
  expression: JsonRecord;
  fieldOptions: Array<{ value: string; label: string; detail?: string; invalid?: boolean }>;
  onChange: (expression: JsonRecord) => void;
}) {
  const kind = expressionKind(expression);
  if (kind === "operation") {
    const op = expressionOperator(expression);
    const operands = expressionOperands(expression);
    return (
      <div className={styles["calculated-expression-node"]}>
        <SelectInput
          label="Operator"
          value={op}
          options={["add", "sub", "mul", "div"]}
          optionLabels={["Add (+)", "Subtract (-)", "Multiply (×)", "Divide (÷)"]}
          onChange={(value) => onChange({ op: value, left: operands[0] || defaultFieldOperand(fieldOptions), right: operands[1] || { const: 0 } })}
        />
        <div className={styles["calculated-expression-operand"]}>
          <strong>Left operand</strong>
          <ExpressionOperandEditor
            expression={asRecord(operands[0])}
            fieldOptions={fieldOptions}
            onChange={(next) => onChange({ op, left: next, right: operands[1] || { const: 0 } })}
          />
        </div>
        <div className={styles["calculated-expression-operand"]}>
          <strong>Right operand</strong>
          <ExpressionOperandEditor
            expression={asRecord(operands[1])}
            fieldOptions={fieldOptions}
            onChange={(next) => onChange({ op, left: operands[0] || defaultFieldOperand(fieldOptions), right: next })}
          />
        </div>
      </div>
    );
  }
  return (
    <ExpressionOperandEditor
      expression={expression}
      fieldOptions={fieldOptions}
      onChange={onChange}
    />
  );
}

function ExpressionOperandEditor({
  expression,
  fieldOptions,
  onChange,
}: {
  expression: JsonRecord;
  fieldOptions: Array<{ value: string; label: string; detail?: string; invalid?: boolean }>;
  onChange: (expression: JsonRecord) => void;
}) {
  const kind = expressionKind(expression);
  if (kind === "operation") {
    return (
      <div className={styles["calculated-expression-nested"]}>
        <ExpressionNodeEditor expression={expression} fieldOptions={fieldOptions} onChange={onChange} />
        <div className={styles["designer-action-row"]}>
          <button type="button" onClick={() => onChange(defaultFieldOperand(fieldOptions))}>Replace with field</button>
          <button type="button" onClick={() => onChange({ const: 0 })}>Replace with constant</button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles["calculated-expression-leaf"]}>
      <SelectInput
        label="Operand type"
        value={kind}
        options={["field", "constant", "operation"]}
        optionLabels={["Field", "Constant", "Nested operation"]}
        onChange={(value) => {
          if (value === "constant") onChange({ const: numericConstant(expression) });
          else if (value === "operation") onChange({ op: "add", left: expressionKind(expression) === "field" ? expression : defaultFieldOperand(fieldOptions), right: { const: 0 } });
          else onChange(defaultFieldOperand(fieldOptions));
        }}
      />
      {kind === "field" ? (
        <SelectWithDescription
          label="Field"
          value={String(expression.field || fieldOptions[0]?.value || "")}
          options={withCurrentOption(fieldOptions, String(expression.field || ""))}
          onChange={(value) => onChange({ field: value })}
        />
      ) : (
        <NumberInput label="Constant" value={numberOrBlank(expression.const)} onChange={(value) => onChange({ const: Number(value || 0) })} />
      )}
    </div>
  );
}

function defaultCalculatedExpression(fields: FieldDescriptor[]): JsonRecord {
  const quantity = fields.find((field) => /quantity|qty|hours|days/i.test(field.path)) || fields.find(isNumericField);
  const rate = fields.find((field) => /rate|price|fee/i.test(field.path) && field.path !== quantity?.path) || fields.find((field) => isNumericField(field) && field.path !== quantity?.path);
  if (quantity && rate) return { op: "mul", left: { field: quantity.path }, right: { field: rate.path } };
  if (quantity) return { op: "mul", left: { field: quantity.path }, right: { const: 1 } };
  return { op: "add", left: { const: 0 }, right: { const: 0 } };
}

function expressionFieldOptions(fields: FieldDescriptor[], table: JsonRecord, currentColumnId: string): Array<{ value: string; label: string; detail?: string; invalid?: boolean }> {
  const options = fieldSelectOptions([...fields].sort((left, right) => Number(isNumericField(right)) - Number(isNumericField(left)) || left.label.localeCompare(right.label)), "");
  const seen = new Set(options.map((option) => option.value));
  for (const column of asArray(table.columns).map(asRecord)) {
    const id = String(column.id || "");
    if (!id || id === currentColumnId) break;
    const expression = asRecord(column.expr || column.calculation);
    if (!Object.keys(expression).length || seen.has(id)) continue;
    options.push({
      value: id,
      label: String(column.label || humanizeIdentifier(id)),
      detail: `${id} · calculated field`,
    });
    seen.add(id);
  }
  return options;
}

function isNumericField(field: FieldDescriptor): boolean {
  const type = field.type.toLowerCase();
  const path = field.path.toLowerCase();
  return type.includes("number") || type.includes("money") || /amount|total|rate|quantity|qty|days|hours|miles|cost|price|fee|discount|adjustment|tax/.test(path);
}

function expressionKind(expression: JsonRecord): "field" | "constant" | "operation" {
  if (Object.prototype.hasOwnProperty.call(expression, "const")) return "constant";
  if (typeof expression.field === "string") return "field";
  return "operation";
}

function expressionOperator(expression: JsonRecord): string {
  const op = String(expression.op || "add");
  return ["add", "sub", "mul", "div"].includes(op) ? op : "add";
}

function expressionOperands(expression: JsonRecord): JsonRecord[] {
  const args = Array.isArray(expression.args) ? expression.args : [expression.left, expression.right].filter(Boolean);
  return args.map(asRecord);
}

function defaultFieldOperand(fieldOptions: Array<{ value: string }>): JsonRecord {
  return fieldOptions[0]?.value ? { field: fieldOptions[0].value } : { const: 0 };
}

function numericConstant(expression: JsonRecord): number {
  const value = Object.prototype.hasOwnProperty.call(expression, "const") ? Number(expression.const) : 0;
  return Number.isFinite(value) ? value : 0;
}

function expressionSummary(expression: JsonRecord, fieldOptions: Array<{ value: string; label: string }>): string {
  if (Object.prototype.hasOwnProperty.call(expression, "const")) return String(expression.const ?? 0);
  if (typeof expression.field === "string") return fieldOptions.find((field) => field.value === expression.field)?.label || humanizeIdentifier(expression.field);
  const op = expressionOperator(expression);
  const operands = expressionOperands(expression);
  const symbol = operatorSymbol(op);
  const rendered = operands.map((operand) => expressionSummary(operand, fieldOptions));
  if (!rendered.length) return "Incomplete calculation";
  return `(${rendered.join(` ${symbol} `)})`;
}

function calculatedColumnSample(payload: FormattedInvoicePayload, tableId: string, columnId: string): { status: "ok"; value: string } | { status: "empty"; message: string } {
  try {
    const plan = resolveNativeLayoutPlan(payload);
    for (const page of plan.pages) {
      for (const band of page.bands) {
        for (const element of band.elements) {
          const match = findResolvedTableCell(element, tableId, columnId);
          if (match) return { status: "ok", value: match.value || "—" };
        }
      }
    }
    return { status: "empty", message: "No sample row is available for this calculation." };
  } catch (error) {
    return { status: "empty", message: error instanceof Error ? error.message : "Unable to calculate a sample result." };
  }
}

function findResolvedTableCell(element: NativeResolvedElement, tableId: string, columnId: string): { value: string } | null {
  if (element.id === tableId && element.table) {
    const row = element.table.rows.find((candidate) => candidate.kind === "detailRow" || candidate.kind === "detail");
    const cell = row?.cells.find((candidate) => candidate.columnId === columnId);
    return cell ? { value: cell.value } : null;
  }
  for (const child of element.elements || []) {
    const match = findResolvedTableCell(child, tableId, columnId);
    if (match) return match;
  }
  return null;
}

function nativeTableColumnDependencies(payload: FormattedInvoicePayload, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>): string[] {
  const band = nativeBandsFromPayload(payload).find((candidate) => String(candidate.id) === selection.bandId);
  const table = band ? findNativeElementInPayload(band, selection.tableId) : null;
  if (!table || table.type !== "table") return [];
  const columnId = selection.columnId;
  const dependencies: string[] = [];
  for (const column of asArray(table.columns).map(asRecord)) {
    const id = String(column.id || "");
    if (id && id !== columnId && expressionReferencesField(asRecord(column.expr || column.calculation), columnId)) {
      dependencies.push(`Calculated field "${String(column.label || humanizeIdentifier(id))}"`);
    }
  }
  asArray(table.sort).map(asRecord).forEach((descriptor, index) => {
    if (String(descriptor.field || "") === columnId) dependencies.push(`Sort rule ${index + 1}`);
  });
  asArray(asRecord(table.footer).cells).map(asRecord).forEach((cell, index) => {
    const aggregate = asRecord(cell.aggregate);
    if (String(cell.columnId || "") === columnId || String(aggregate.field || "") === columnId || expressionReferencesField(asRecord(aggregate.expr || aggregate.expression), columnId)) {
      dependencies.push(`Table footer cell ${index + 1}`);
    }
  });
  asArray(table.groups).map(asRecord).forEach((group, groupIndex) => {
    const key = asRecord(group.key);
    if (String(group.field || key.field || "") === columnId || expressionReferencesField(asRecord(group.expr || key.expr || group.expression || key.expression), columnId)) {
      dependencies.push(`Group ${groupIndex + 1} key`);
    }
    asArray(asRecord(group.footer).cells).map(asRecord).forEach((cell, cellIndex) => {
      const aggregate = asRecord(cell.aggregate);
      if (String(cell.columnId || "") === columnId || String(aggregate.field || "") === columnId || expressionReferencesField(asRecord(aggregate.expr || aggregate.expression), columnId)) {
        dependencies.push(`Group ${groupIndex + 1} footer cell ${cellIndex + 1}`);
      }
    });
  });
  return dependencies;
}

function expressionReferencesField(expression: JsonRecord, field: string): boolean {
  if (!Object.keys(expression).length) return false;
  if (String(expression.field || "") === field) return true;
  const aggregate = asRecord(expression.aggregate);
  if (String(aggregate.field || "") === field || expressionReferencesField(asRecord(aggregate.expr || aggregate.expression), field)) return true;
  return expressionOperands(expression).some((operand) => expressionReferencesField(operand, field));
}

function SelectionActions({ deleteSelection, duplicateSelection }: { deleteSelection: () => void; duplicateSelection: () => void }) {
  return (
    <PropertySection title="Actions">
      <div className={styles["designer-action-row"]}>
        <button type="button" onClick={duplicateSelection}>Duplicate</button>
        <button className={styles["designer-danger-btn"]} type="button" onClick={deleteSelection}>Delete</button>
      </div>
    </PropertySection>
  );
}

function PropertySection({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <details className={styles["designer-property-section"]} open>
      <summary>{title}</summary>
      <div className={styles["designer-property-body"]}>{children}</div>
    </details>
  );
}

function ValidationPanel({ messages }: { messages: DesignerValidationMessage[] }) {
  return (
    <div className={styles["designer-validation"]}>
      {messages.map((message, index) => (
        <div className={`${styles["designer-validation-item"]} ${styles[`designer-validation-${message.level}`]}`} key={`${message.path}-${index}`}>
          <strong>{message.level.toUpperCase()}</strong>
          <span>{message.path}</span>
          <p>{message.message}</p>
        </div>
      ))}
    </div>
  );
}

function TextInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label>
      <span className={styles["designer-label"]}>{label}</span>
      <input className={styles["designer-input"]} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberInput({ label, value, onChange }: { label: string; value: string; onChange: (value: number | undefined) => void }) {
  return (
    <label>
      <span className={styles["designer-label"]}>{label}</span>
      <input className={styles["designer-input"]} type="number" value={value} onChange={(event) => onChange(event.target.value === "" ? undefined : Number(event.target.value))} />
    </label>
  );
}

function SelectInput({ label, onChange, optionLabels, options, value }: { label: string; onChange: (value: string) => void; optionLabels?: string[]; options: string[]; value: string }) {
  return (
    <label>
      <span className={styles["designer-label"]}>{label}</span>
      <select aria-label={label} className={styles["designer-input"]} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option, index) => <option key={option} value={option}>{optionLabels?.[index] || option || "None"}</option>)}
      </select>
    </label>
  );
}

function CheckboxInput({ checked, label, onChange }: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return (
    <label className={styles["designer-checkbox"]}>
      <input checked={checked} onChange={(event) => onChange(event.target.checked)} type="checkbox" />
      <span>{label}</span>
    </label>
  );
}

function SelectWithDescription({
  description,
  label,
  onChange,
  options,
  value,
}: {
  description?: string;
  label: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; detail?: string; invalid?: boolean }>;
  value: string;
}) {
  return (
    <div>
      <SelectInput
        label={label}
        onChange={onChange}
        optionLabels={options.map((option) => `${option.label}${option.detail ? ` — ${option.detail}` : ""}${option.invalid ? " (unresolved)" : ""}`)}
        options={options.map((option) => option.value)}
        value={value}
      />
      {description && <p className={styles["designer-empty-note"]}>{description}</p>}
    </div>
  );
}

function getDataset(datasets: JsonRecord, name: string): unknown {
  if (!name) return undefined;
  return name.split(".").reduce<unknown>((current, key) => asRecord(current)[key], datasets);
}

function getPath(value: unknown, path: string): unknown {
  if (!path) return undefined;
  return path.split(".").reduce<unknown>((current, key) => asRecord(current)[key], value);
}

function getRows(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value.map(asRecord);
  const record = asRecord(value);
  for (const key of ["rows", "items", "entries", "details"]) {
    const rows = record[key];
    if (Array.isArray(rows)) return rows.map(asRecord);
  }
  return [];
}

function discoverReportDatasets(payload: FormattedInvoicePayload): DatasetDescriptor[] {
  const samples = asRecord(payload.datasets);
  const metadataRows = asArray(asRecord(asRecord(payload as unknown as JsonRecord).data).datasets).map(asRecord);
  const generatedSamples = asRecord(asRecord(payload.metadata).designSampleDatasets);
  const byId = new Map<string, DatasetDescriptor>();
  for (const metadata of metadataRows) {
    const id = String(metadata.id || metadata.key || metadata.name || metadata.source || "").trim();
    if (!id) continue;
    byId.set(id, describeDataset(id, samples[id], { ...metadata, designSample: generatedSamples[id] }));
  }
  for (const id of Object.keys(samples)) {
    if (!byId.has(id)) byId.set(id, describeDataset(id, samples[id], { designSample: generatedSamples[id] }));
  }
  return [...byId.values()].sort((a, b) => Number(b.kind === "collection") - Number(a.kind === "collection") || a.label.localeCompare(b.label));
}

function describeDataset(id: string, sample: unknown, metadata: JsonRecord = {}): DatasetDescriptor {
  const collection = collectionRows(sample);
  const sampleRecord = collection.isCollection ? collection.record : asRecord(sample);
  const knownSource = id === "invoiceHeader" || id === "invoiceLines" ? INVOICE_SOURCES[id].source : "invoice";
  const knownFields = metadata.source === knownSource || (id === "invoiceTotals" && metadata.source === "invoiceTimeEntries") ? INVOICE_FIELDS[id] || [] : [];
  const metadataFields = mergeFieldDescriptors(metadataFieldDescriptors(metadata), metadataFieldDescriptors({ fields: knownFields }));
  const sampleFields = scalarFieldDescriptors(sampleRecord);
  const fields = mergeFieldDescriptors(metadataFields, sampleFields).map(field => {
    const lifecycle = id === "invoiceHeader" && ["invoiceState", "invoiceNumber"].includes(field.path)
      ? knownFields.find(known => known.path === field.path) : undefined;
    return lifecycle ? { ...field, type: lifecycle.type, metadata: { ...field.metadata, ...lifecycle, enum: lifecycle.enum } } : field;
  });
  const metadataKind = String(metadata.kind || metadata.type || metadata.cardinality || "").toLowerCase();
  const metadataCollection = Boolean(metadata.isCollection || metadata.repeating || metadataKind.includes("array") || metadataKind.includes("collection") || metadataKind.includes("many"));
  const hasSampleData = sample !== undefined && sample !== null && (Array.isArray(sample) ? sample.length > 0 : Object.keys(asRecord(sample)).length > 0);
  const declaredSingle = metadataKind === "single" || (["invoiceHeader", "invoiceTotals"].includes(id) && knownFields.length > 0);
  const kind: DatasetKind = collection.isCollection || metadataCollection ? "collection" : sample === undefined && !declaredSingle ? "unknown" : "single";
  const designSample = asRecord(metadata.designSample);
  const generatedRecordCount = typeof designSample.generatedRecordCount === "number" ? designSample.generatedRecordCount : null;
  const embeddedRecordCount = typeof designSample.embeddedRecordCount === "number"
    ? designSample.embeddedRecordCount
    : collection.isCollection ? collection.count : null;
  return {
    id,
    label: String(metadata.label || metadata.displayName || metadata.title || metadata.name || titleize(id)),
    kind,
    recordCount: collection.isCollection ? collection.count : null,
    embeddedRecordCount,
    generatedRecordCount,
    rowContainerKey: collection.containerKey,
    hasSampleData,
    noSampleData: sample === undefined || sample === null,
    fields,
    metadata,
  };
}

function collectionRows(value: unknown): { isCollection: boolean; count: number; record: JsonRecord; containerKey?: string } {
  if (Array.isArray(value)) return { isCollection: true, count: value.length, record: asRecord(value.find((item) => Object.keys(asRecord(item)).length > 0)) };
  const record = asRecord(value);
  for (const key of ["rows", "Rows", "items", "Items", "entries", "Entries", "details", "Details"]) {
    const rows = record[key];
    if (Array.isArray(rows)) return { isCollection: true, count: rows.length, record: asRecord(rows.find((item) => Object.keys(asRecord(item)).length > 0)), containerKey: key };
  }
  return { isCollection: false, count: 0, record: {} };
}

function metadataFieldDescriptors(metadata: JsonRecord): FieldDescriptor[] {
  return asArray(metadata.fields || metadata.columns || metadata.properties).map(asRecord).map((field) => {
    const path = String(field.path || field.key || field.id || field.name || "").trim();
    if (!path) return null;
    return {
      path,
      label: String(field.label || field.displayName || field.title || titleize(path)),
      type: String(field.type || field.dataType || field.valueType || "unknown"),
      sampleAvailable: false,
      metadata: field,
    } satisfies FieldDescriptor;
  }).filter(Boolean) as FieldDescriptor[];
}

function scalarFieldDescriptors(record: JsonRecord): FieldDescriptor[] {
  return Object.keys(record).filter((key) => {
    const sampleValue = record[key];
    return sampleValue === null || ["string", "number", "boolean", "undefined"].includes(typeof sampleValue);
  }).map((key) => ({
    path: key,
    label: titleize(key),
    type: record[key] === null || record[key] === undefined ? "unknown" : typeof record[key],
    sampleAvailable: true,
  }));
}

function mergeFieldDescriptors(primary: FieldDescriptor[], secondary: FieldDescriptor[]): FieldDescriptor[] {
  const byPath = new Map<string, FieldDescriptor>();
  for (const field of [...secondary, ...primary]) byPath.set(field.path, { ...byPath.get(field.path), ...field, sampleAvailable: byPath.get(field.path)?.sampleAvailable || field.sampleAvailable });
  return [...byPath.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function preferredTableDataset(datasets: DatasetDescriptor[]): DatasetDescriptor | undefined {
  return datasets.find((dataset) => dataset.kind === "collection" && dataset.fields.length) || datasets.find((dataset) => dataset.kind === "collection") || datasets[0];
}

function datasetKindLabel(dataset: DatasetDescriptor): string {
  if (dataset.kind === "collection") {
    const effectiveLabel = dataset.recordCount === null ? "repeating collection" : `${dataset.recordCount} active row${dataset.recordCount === 1 ? "" : "s"}`;
    if (dataset.generatedRecordCount !== null) {
      const embedded = dataset.embeddedRecordCount !== null ? ` · ${dataset.embeddedRecordCount} embedded` : "";
      return `${dataset.generatedRecordCount} generated row${dataset.generatedRecordCount === 1 ? "" : "s"}${embedded}`;
    }
    return effectiveLabel;
  }
  if (dataset.kind === "single") return "single record";
  return "metadata only";
}

function datasetSelectOptions(datasets: DatasetDescriptor[], currentValue: string): Array<{ value: string; label: string; detail?: string; invalid?: boolean }> {
  const options: Array<{ value: string; label: string; detail?: string; invalid?: boolean }> = datasets.map((dataset) => ({ value: dataset.id, label: dataset.label, detail: `${dataset.id} · ${datasetKindLabel(dataset)}` }));
  if (currentValue && !datasets.some((dataset) => dataset.id === currentValue)) options.unshift({ value: currentValue, label: currentValue, detail: "Unresolved manual binding", invalid: true });
  return options;
}

function fieldSelectOptions(fields: FieldDescriptor[], currentValue: string): Array<{ value: string; label: string; detail?: string; invalid?: boolean }> {
  const options: Array<{ value: string; label: string; detail?: string; invalid?: boolean }> = fields.map((field) => ({ value: field.path, label: field.label, detail: `${field.path} · ${field.type}${field.metadata?.nullable === true ? " (nullable)" : ""}${Array.isArray(field.metadata?.enum) ? ` [${field.metadata.enum.join(" | ")}]` : ""}` }));
  if (currentValue && !fields.some((field) => field.path === currentValue)) options.unshift({ value: currentValue, label: currentValue, detail: "Unresolved binding", invalid: true });
  return options;
}

function nativeSortFieldOptions(fields: FieldDescriptor[], columns: JsonRecord[], currentValue: string): Array<{ value: string; label: string; detail?: string; invalid?: boolean; type?: string }> {
  const options = fieldSelectOptions(fields, currentValue).map((option) => ({ ...option, type: fields.find((field) => field.path === option.value)?.type || "auto" }));
  const seen = new Set(options.map((option) => option.value));
  for (const column of columns) {
    const id = String(column.id || "");
    const expr = asRecord(column.expr || column.calculation);
    if (!id || !Object.keys(expr).length || seen.has(id)) continue;
    options.push({
      value: id,
      label: String(column.label || humanizeIdentifier(id)),
      detail: `${id} · calculated column`,
      type: sortTypeForColumn(column),
    });
    seen.add(id);
  }
  return withCurrentOption(options, currentValue);
}

function nativeSummaryFieldDescriptors(fields: FieldDescriptor[], columns: JsonRecord[]): FieldDescriptor[] {
  const byPath = new Map(fields.map((field) => [field.path, field]));
  for (const column of columns) {
    const id = String(column.id || "");
    const expression = asRecord(column.expr || column.calculation);
    if (!id || !Object.keys(expression).length || byPath.has(id)) continue;
    byPath.set(id, {
      path: id,
      label: String(column.label || humanizeIdentifier(id)),
      type: sortTypeForColumn(column) === "number" ? "number" : "calculated",
      sampleAvailable: true,
      metadata: { calculated: true },
    });
  }
  return [...byPath.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function nativeColumnSortField(column: JsonRecord): string {
  const binding = String(column.binding || "");
  if (binding) return binding;
  const id = String(column.id || "");
  const expr = asRecord(column.expr || column.calculation);
  return id && Object.keys(expr).length ? id : "";
}

function withCurrentOption<T extends { value: string; label: string; detail?: string; invalid?: boolean }>(options: T[], currentValue: string): T[] {
  if (!currentValue || options.some((option) => option.value === currentValue)) return options;
  return [{ value: currentValue, label: currentValue, detail: "Unresolved binding", invalid: true } as T, ...options];
}

function sortTypeForColumn(column: JsonRecord): string {
  const format = String(column.format || "");
  if (format === "date") return "date";
  if (format === "number" || format === "money") return "number";
  return "auto";
}

function sortDirectionLabels(type: string | undefined): string[] {
  if (type === "date") return ["Ascending (Oldest First)", "Descending (Newest First)"];
  if (type === "number") return ["Ascending (Low to High)", "Descending (High to Low)"];
  return ["Ascending", "Descending"];
}

function selectOptionsWithCurrent(options: string[], currentValue: string): string[] {
  return currentValue && !options.includes(currentValue) ? [currentValue, ...options] : options;
}

function designTimeSampleOptions(payload: FormattedInvoicePayload): DesignSampleOption[] {
  if (!isNativeReportsV31Payload(payload)) {
    return [
      { id: "standard", label: "Embedded report data", description: "Legacy designer samples use their embedded report payload data.", compatible: true },
      { id: "edge", label: "Edge-case sample data", description: "Native reports-v3.1 only.", compatible: false },
      { id: "empty", label: "Empty collections", description: "Native reports-v3.1 only.", compatible: false },
      { id: "stress", label: "Multipage stress data", description: "Native reports-v3.1 only.", compatible: false },
    ];
  }
  const descriptors = discoverReportDatasets(payload);
  const compatible = descriptors.some((dataset) => dataset.fields.length > 0);
  return [
    { id: "standard", label: "Generated sample data", description: "Deterministic values matching this report's dataset contracts.", compatible },
    { id: "edge", label: "Edge-case sample data", description: "Long text, nulls, large values, and varied dates for the same dataset contracts.", compatible },
    { id: "empty", label: "Empty collections", description: "Single-record datasets stay populated while repeating collections return zero rows.", compatible },
    { id: "stress", label: "Multipage stress data", description: "At least 100 repeating rows for pagination and repeated-header verification.", compatible },
  ];
}

function applyDesignTimeSampleData(payload: FormattedInvoicePayload, fixtureId: DesignSampleFixtureId, recordCount: number): FormattedInvoicePayload {
  assertReportAdmission(payload, "sample");
  if (!isNativeReportsV31Payload(payload)) return payload;
  const descriptors = discoverReportDatasets(payload).filter((dataset) => dataset.fields.length > 0);
  if (!descriptors.length) return payload;
  const next = clonePayload(payload);
  const datasets = { ...asRecord(next.datasets) };
  const count = fixtureId === "stress" ? Math.max(100, recordCount) : Math.max(1, recordCount);
  const sampleMetadata: JsonRecord = {};
  for (const descriptor of descriptors) {
    if (descriptor.kind === "collection") {
      const rows = fixtureId === "empty"
        ? []
        : Array.from({ length: count }, (_, index) => generateDesignSampleRecord(descriptor, index, fixtureId));
      datasets[descriptor.id] = withGeneratedCollectionRows(datasets[descriptor.id], rows, descriptor.rowContainerKey);
      sampleMetadata[descriptor.id] = {
        generatedRecordCount: rows.length,
        embeddedRecordCount: descriptor.embeddedRecordCount,
      };
    } else {
      datasets[descriptor.id] = generateDesignSampleRecord(descriptor, 0, fixtureId);
      sampleMetadata[descriptor.id] = {
        generatedRecordCount: null,
        embeddedRecordCount: null,
      };
    }
  }
  next.datasets = datasets;
  next.metadata = {
    ...asRecord(next.metadata),
    designSampleDatasets: sampleMetadata,
    sampleDataNotice: designTimeSampleNotice(next, fixtureId, recordCount),
  };
  return next;
}

function withGeneratedCollectionRows(originalDataset: unknown, rows: JsonRecord[], rowContainerKey?: string): unknown {
  if (Array.isArray(originalDataset) || !rowContainerKey) return rows;
  return {
    ...asRecord(originalDataset),
    [rowContainerKey]: rows,
  };
}

function designTimeSampleNotice(payload: FormattedInvoicePayload, fixtureId: DesignSampleFixtureId, recordCount: number): string {
  const descriptors = discoverReportDatasets(payload);
  const collectionCount = descriptors.filter((dataset) => dataset.kind === "collection").length;
  const effectiveCount = fixtureId === "empty" ? 0 : fixtureId === "stress" ? Math.max(100, recordCount) : recordCount;
  const fixtureLabel = designTimeSampleOptions(payload).find((option) => option.id === fixtureId)?.label || "Generated sample data";
  return `${fixtureLabel}: ${collectionCount ? `${effectiveCount} row${effectiveCount === 1 ? "" : "s"} per repeating dataset` : "single-record datasets only"}.`;
}

function generateDesignSampleRecord(dataset: DatasetDescriptor, index: number, fixtureId: DesignSampleFixtureId): JsonRecord {
  return dataset.fields.reduce<JsonRecord>((record, field) => {
    record[field.path] = generateDesignSampleValue(dataset, field, index, fixtureId);
    return record;
  }, {});
}

function generateDesignSampleValue(dataset: DatasetDescriptor, field: FieldDescriptor, index: number, fixtureId: DesignSampleFixtureId): unknown {
  const path = field.path.toLowerCase();
  const type = field.type.toLowerCase();
  const row = index + 1;
  if (fixtureId === "edge" && row % 11 === 0 && /contact|address|attention|notes?|description|legal|project|prospect/.test(path)) return null;
  if (type.includes("date") || /date/.test(path)) {
    const day = ((index * 3) % 28) + 1;
    return `2026-10-${String(day).padStart(2, "0")}`;
  }
  if (type.includes("boolean") || /^is[A-Z_]/.test(field.path)) return row % 2 === 0;
  if (type.includes("number") || type.includes("money") || /amount|total|rate|quantity|qty|days|hours|miles|cost|price|fee/.test(path)) {
    if (/quantity|qty/.test(path)) return fixtureId === "edge" && row % 9 === 0 ? 0 : (row % 5) + 1;
    if (/days|hours/.test(path)) return Number((((row % 8) + 1) * 0.5).toFixed(2));
    if (/rate|price|fee/.test(path)) return fixtureId === "edge" && row % 10 === 0 ? 2500 : [125, 150, 175, 200][row % 4];
    if (/miles/.test(path)) return fixtureId === "edge" && row % 8 === 0 ? 1234 : row * 7;
    const quantity = (row % 5) + 1;
    const rate = [125, 150, 175, 200][row % 4];
    return fixtureId === "edge" && row % 10 === 0 ? 98765.43 : quantity * rate;
  }
  if (/company.*name|broker.*name/.test(path)) return "Land Agility Sample Services";
  if (/client.*name|customer|billto|bill_to/.test(path)) return "Sample Exploration Company";
  if (/address.*1|addressline1/.test(path)) return `${100 + row} Sample Field Road`;
  if (/address.*2|addressline2/.test(path)) return row % 2 === 0 ? "Midland, TX 79701" : "Houston, TX 77002";
  if (/contact|email|phone/.test(path)) return "billing@example.test · (555) 010-0100";
  if (/attention|attn/.test(path)) return "Avery Approver";
  if (/invoice.*number|invoice_no/.test(path)) return `INV-SAMPLE-${String(row).padStart(4, "0")}`;
  if (/terms/.test(path)) return "Net 30";
  if (/period/.test(path)) return "10/01/2026 - 10/31/2026";
  if (/landman|payee/.test(path)) return ["Taylor Sample", "Jordan Example", "Morgan Fixture"][index % 3];
  if (/prospect|project/.test(path)) return ["North Basin", "Glasscock", "Midland"][index % 3] + " Prospect";
  if (/legal/.test(path)) return fixtureId === "edge" || row % 7 === 0
    ? `Section ${row}, Block ${row % 4}, Township ${row % 8} South, Range ${row % 6} East, Abstract ${1000 + row}, with an intentionally long legal description for wrapping verification`
    : `Sec ${row} Blk ${row % 4} Abs ${1000 + row}`;
  if (/description|desc|notes?/.test(path)) return fixtureId === "edge" || row % 9 === 0
    ? `Extended sample work description ${row} with varied text length, nullable companion fields, and wrapping behavior across designer preview and PDF rendering.`
    : `Sample work description ${row}`;
  if (/status/.test(path)) return ["pending", "approved", "invoiced", "not_invoiced"][index % 4];
  return `${field.label || titleize(field.path)} ${row}`;
}

function datasetDescription(binding: string, dataset: DatasetDescriptor | undefined, datasets: DatasetDescriptor[]): string {
  if (!binding) return "Select a repeating collection for dynamic table rows.";
  if (!dataset) return `Binding "${binding}" is not in dataset metadata or loaded sample data. It is preserved until you remap it.`;
  if (dataset.kind !== "collection") return `${dataset.label} is a single-record dataset. Repeating tables need a collection.`;
  if (dataset.noSampleData) return `${dataset.label} exists in metadata, but no sample data is loaded.`;
  if (dataset.recordCount === 0) return `${dataset.label} is a repeating dataset, but the loaded sample returned zero records.`;
  return `${dataset.label} is a repeating dataset with ${dataset.recordCount ?? "unknown"} sample record${dataset.recordCount === 1 ? "" : "s"}.`;
}

function titleize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function numberOrBlank(value: unknown): string {
  return Number.isFinite(Number(value)) ? String(value) : "";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function workspaceGridTemplate(focusMode: boolean, collapsedPanels: Record<PanelSide, boolean>): string {
  if (focusMode) return "minmax(0, 1fr)";
  const columns: string[] = [];
  if (!collapsedPanels.left) columns.push("auto", "6px");
  columns.push("minmax(0, 1fr)");
  if (!collapsedPanels.right) columns.push("6px", "auto");
  return columns.join(" ");
}

function loadDesignerPreference(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  const stored = Number(window.localStorage.getItem(`reportsV3Designer.${key}`));
  if (!Number.isFinite(stored)) return fallback;
  return key === "rightPanelWidth" ? clamp(stored, 280, 460) : clamp(stored, 220, 430);
}

function saveDesignerPreference(key: string, value: number) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(`reportsV3Designer.${key}`, String(value));
}

function currentPagePixelSize(pageSize: DesignerPageSize, orientation: DesignerPageOrientation, payload: FormattedInvoicePayload): { width: number; height: number } {
  if (isNativeReportsV31Payload(payload)) {
    const page = resolveNativeLayoutPlan(payload).page;
    return { width: page.width, height: page.height };
  }
  const legal = pageSize === "legal";
  const portrait = orientation === "portrait";
  const width = legal ? 816 : 816;
  const height = legal ? 1344 : 1056;
  return portrait ? { width, height } : { width: height, height: width };
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable;
}

function handleTreeKeyDown(event: React.KeyboardEvent<HTMLElement>) {
  if (isEditableTarget(event.target) || !["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
  if (!(event.target instanceof HTMLElement) || !event.target.closest("[data-structure-item]")) return;
  event.preventDefault();
  event.stopPropagation();
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-structure-item]"))
    .filter((item) => !item.hasAttribute("disabled") && !item.closest("[hidden]") && item.offsetParent !== null);
  const index = items.indexOf(document.activeElement as HTMLElement);
  if (event.key === "ArrowDown") {
    items[Math.min(items.length - 1, Math.max(0, index + 1))]?.focus();
  }
  if (event.key === "ArrowUp") {
    items[Math.max(0, index - 1)]?.focus();
  }
  if (event.key === "Home") items[0]?.focus();
  if (event.key === "End") items[items.length - 1]?.focus();
  if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
    const branch = (document.activeElement as HTMLElement | null)?.closest("[data-structure-branch]");
    const toggle = branch?.querySelector<HTMLButtonElement>("[data-structure-toggle]");
    if (!toggle) return;
    if ((toggle.getAttribute("aria-expanded") === "true") !== (event.key === "ArrowRight")) toggle.click();
    if (event.key === "ArrowLeft") branch?.querySelector<HTMLElement>("[data-structure-item]")?.focus();
  }
}

function formatCanvasValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "—";
  return String(value);
}

function formatDesignerValue(value: unknown, binding: string | undefined, mode: CanvasValueMode): string {
  const bindingText = binding || "Unbound";
  const placeholder = bindingText.startsWith("{") || bindingText === "Unbound" ? bindingText : `{${bindingText}}`;
  if (mode === "binding") return placeholder;
  const sampleText = formatCanvasValue(value);
  if (mode === "both") return `${sampleText} · ${placeholder}`;
  return sampleText;
}

function startColumnResize(startX: number, startWidth: number, commitResize: (width: number) => void) {
  const handleMouseUp = (event: MouseEvent) => {
    const delta = event.clientX - startX;
    const nextWidth = Math.round((startWidth + delta / 10) * 10) / 10;
    commitResize(clamp(nextWidth, 4, 80));
    window.removeEventListener("mouseup", handleMouseUp);
  };
  window.addEventListener("mouseup", handleMouseUp);
}

function startNativeTableColumnResize({
  adjacentStartWidth,
  commitResize,
  startWidth,
  startX,
  zoom,
}: {
  adjacentStartWidth: number;
  commitResize: (width: number, adjacentWidth: number) => void;
  startWidth: number;
  startX: number;
  zoom: number;
}) {
  const minWidth = 24;
  const total = startWidth + adjacentStartWidth;
  const overlay = document.createElement("div");
  overlay.className = styles["native-table-column-resize-overlay"];
  overlay.style.left = `${startX}px`;
  document.body.appendChild(overlay);
  function nextWidths(clientX: number) {
    const delta = (clientX - startX) / Math.max(0.01, zoom / 100);
    const nextWidth = clamp(startWidth + delta, minWidth, total - minWidth);
    return { width: Math.round(nextWidth), adjacentWidth: Math.round(total - nextWidth) };
  }
  function handlePointerMove(event: PointerEvent) {
    overlay.style.left = `${event.clientX}px`;
  }
  function handlePointerUp(event: PointerEvent) {
    const next = nextWidths(event.clientX);
    commitResize(next.width, next.adjacentWidth);
    overlay.remove();
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerCancel);
  }
  function handlePointerCancel() {
    overlay.remove();
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerCancel);
  }
  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerUp);
  window.addEventListener("pointercancel", handlePointerCancel);
}

function startNativeTableWidthResize({
  commitResize,
  startWidth,
  startX,
  zoom,
}: {
  commitResize: (width: number) => void;
  startWidth: number;
  startX: number;
  zoom: number;
}) {
  const overlay = document.createElement("div");
  overlay.className = styles["native-table-column-resize-overlay"];
  overlay.style.left = `${startX}px`;
  document.body.appendChild(overlay);
  function nextWidth(clientX: number) {
    return Math.max(24, Math.round(startWidth + (clientX - startX) / Math.max(0.01, zoom / 100)));
  }
  function handlePointerMove(event: PointerEvent) {
    overlay.style.left = `${event.clientX}px`;
  }
  function handlePointerUp(event: PointerEvent) {
    commitResize(nextWidth(event.clientX));
    overlay.remove();
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerCancel);
  }
  function handlePointerCancel() {
    overlay.remove();
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerCancel);
  }
  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerUp);
  window.addEventListener("pointercancel", handlePointerCancel);
}

function componentLabel(component: DesignerComponent | undefined): string {
  if (!component) return "section";
  if (component.kind === "keyValueGrid") return "Key-value grid";
  if (component.kind === "rendererSection") return humanizeIdentifier(component.legacyType);
  return humanizeIdentifier(component.kind);
}

function legacyBandLabel(band: DesignerBand): string {
  return String(band.title || humanizeIdentifier(band.legacyId || band.id));
}

function humanizeIdentifier(value: string): string {
  const lastSegment = value.split(".").filter(Boolean).pop() || value;
  return lastSegment
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());
}

function inspectorTitle(selection: DesignerSelection): string {
  if (selection.type === "nativeElement") return selection.elementIds.length > 1 ? "Elements properties" : "Element properties";
  if (selection.type === "nativeTableColumn") return "Table column properties";
  if (selection.type === "nativeTableGroup") return "Table grouping properties";
  if (selection.type === "field") return "Field properties";
  if (selection.type === "column") return "Column properties";
  if (selection.type === "component") return "Component properties";
  if (selection.type === "band") return "Band properties";
  return "Document properties";
}

function findSelectedNativeTable(nativeBands: JsonRecord[], selection: Extract<DesignerSelection, { type: "nativeElement" }>): { bandId: string; tableId: string } | null {
  if (selection.elementIds.length !== 1) return null;
  const band = nativeBands.find((candidate) => String(candidate.id) === selection.bandId);
  const element = band ? findNativeElementInPayload(band, selection.elementIds[0]) : undefined;
  return element && String(element.type) === "table" ? { bandId: selection.bandId, tableId: selection.elementIds[0] } : null;
}

function resolveTemplate(payload: FormattedInvoicePayload, template: string): string {
  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, path) => String(getPath(payload.datasets, path.trim()) ?? ""));
}

function nativeBandsFromPayload(payload: FormattedInvoicePayload): JsonRecord[] {
  const layout = asRecord(payload.layout);
  return asArray(layout.bands).map(asRecord);
}

function findNativeElementInPayload(root: JsonRecord, elementId: string): JsonRecord | undefined {
  for (const element of asArray(root.elements).map(asRecord)) {
    if (String(element.id) === elementId) return element;
    if (String(element.type) === "container") {
      const child = findNativeElementInPayload(element, elementId);
      if (child) return child;
    }
  }
  return undefined;
}

function resolveNativeDataInsertionTarget(nativeBands: JsonRecord[], selection: DesignerSelection): string | undefined {
  if (selection.type !== "nativeElement" || selection.elementIds.length !== 1) return undefined;
  const elementId = selection.elementIds[0];
  const band = nativeBands.find((candidate) => String(candidate.id) === selection.bandId);
  if (!band) return undefined;
  const selected = findNativeElementInPayload(band, elementId);
  if (selected && String(selected.type) === "container") return elementId;
  const parent = findNativeParentInfo(band, elementId);
  return parent?.parentId || undefined;
}

function findNativeParentInfo(root: JsonRecord, elementId: string, parentId = "", parentLabel = ""): { parentId: string; label: string } | null {
  for (const element of asArray(root.elements).map(asRecord)) {
    if (String(element.id) === elementId) {
      return { parentId, label: parentLabel || `${nativeBandLabel(String(root.role || ""))} band` };
    }
    if (String(element.type) === "container") {
      const child = findNativeParentInfo(element, elementId, String(element.id), `Container ${String(element.id)}`);
      if (child) return child;
    }
  }
  return null;
}

function nativeReparentOptions(nativeBand: JsonRecord, plan: ReturnType<typeof resolveNativeLayoutPlan>, bandId: string, elementId: string): Array<{ value: string; label: string }> {
  const band = findResolvedBand(plan, bandId);
  const selected = band ? findResolvedElement(band.elements, elementId) : null;
  const descendantIds = new Set(selected ? flattenResolvedNativeElements(selected.elements || []).map((element) => element.id) : []);
  const options = [{ value: "", label: `${nativeBandLabel(String(nativeBand.role || ""))} band` }];
  const visit = (elements: JsonRecord[]) => {
    elements.forEach((element) => {
      const id = String(element.id || "");
      if (String(element.type) === "container" && id && id !== elementId && !descendantIds.has(id) && String(asRecord(element.layout).mode || "absolute") === "absolute") {
        options.push({ value: id, label: `Container ${id}` });
      }
      if (String(element.type) === "container") visit(asArray(element.elements).map(asRecord));
    });
  };
  visit(asArray(nativeBand.elements).map(asRecord));
  return options;
}

function reparentBoundsForTarget(plan: ReturnType<typeof resolveNativeLayoutPlan>, bandId: string, elementId: string, targetParentId: string | null): { x: number; y: number; width: number; height: number } | null {
  const band = findResolvedBand(plan, bandId);
  if (!band) return null;
  const element = findResolvedElement(band.elements, elementId);
  if (!element) return null;
  const origin = targetParentId ? resolvedContainerContentOrigin(band.elements, targetParentId) : { x: band.bounds.x, y: band.bounds.y };
  if (!origin) return null;
  return {
    x: Math.max(0, element.absolute.x - origin.x),
    y: Math.max(0, element.absolute.y - origin.y),
    width: element.absolute.width,
    height: element.absolute.height,
  };
}

function requiredBandHeight(plan: ReturnType<typeof resolveNativeLayoutPlan>, bandId: string): number | null {
  const band = findResolvedBand(plan, bandId);
  if (!band) return null;
  const bottoms = flattenResolvedNativeElements(band.elements).map((element) => element.absolute.y + element.absolute.height - band.bounds.y);
  return Math.max(12, Math.ceil(Math.max(0, ...bottoms) + 12));
}

function requiredContainerBounds(plan: ReturnType<typeof resolveNativeLayoutPlan>, bandId: string, elementId: string): Partial<{ x: number; y: number; width: number; height: number }> | null {
  const band = findResolvedBand(plan, bandId);
  const element = band ? findResolvedElement(band.elements, elementId) : null;
  if (!element || element.type !== "container") return null;
  const children = element.elements || [];
  if (!children.length) return { width: element.sourceBounds?.width ?? element.width, height: element.sourceBounds?.height ?? element.height };
  const padding = normalizeNativePaddingForInspector((element as unknown as JsonRecord).padding);
  const right = Math.max(...flattenResolvedNativeElements(children).map((child) => child.absolute.x + child.absolute.width - element.absolute.x));
  const bottom = Math.max(...flattenResolvedNativeElements(children).map((child) => child.absolute.y + child.absolute.height - element.absolute.y));
  return {
    width: Math.max(6, Math.ceil(right + padding.right + 8)),
    height: Math.max(6, Math.ceil(bottom + padding.bottom + 8)),
  };
}

function resolvedContainerContentOrigin(elements: NativeResolvedElement[], containerId: string): { x: number; y: number } | null {
  const container = findResolvedElement(elements, containerId);
  if (!container || container.type !== "container") return null;
  const padding = normalizeNativePaddingForInspector((container as unknown as JsonRecord).padding);
  return { x: container.absolute.x + padding.left, y: container.absolute.y + padding.top };
}

function findResolvedElement(elements: NativeResolvedElement[], elementId: string): NativeResolvedElement | null {
  for (const element of elements) {
    if (element.id === elementId) return element;
    const child = findResolvedElement(element.elements || [], elementId);
    if (child) return child;
  }
  return null;
}

function normalizeNativePaddingForInspector(value: unknown): { top: number; right: number; bottom: number; left: number } {
  if (typeof value === "number") return { top: value, right: value, bottom: value, left: value };
  const record = asRecord(value);
  const fallback = Number(record.all || 0) || 0;
  return {
    top: Number(record.top ?? fallback) || 0,
    right: Number(record.right ?? fallback) || 0,
    bottom: Number(record.bottom ?? fallback) || 0,
    left: Number(record.left ?? fallback) || 0,
  };
}

function nativeBandLabel(role: string): string {
  const labels: Record<string, string> = {
    reportHeader: "Report Header",
    pageHeader: "Page Header",
    detail: "Detail",
    reportFooter: "Report Footer",
    pageFooter: "Page Footer",
  };
  return labels[role] || titleize(role || "band");
}

function nativeBandDescription(role: string): string {
  const descriptions: Record<string, string> = {
    reportHeader: "Once per report",
    pageHeader: "Repeats at the top of each page",
    detail: "Main repeating content",
    reportFooter: "Once after report content",
    pageFooter: "Repeats at the bottom of each page",
  };
  return descriptions[role] || "Report band";
}

function nativeResizeDiagnosticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const explicitlyAuthorized = process.env.REACT_APP_ENABLE_NATIVE_RESIZE_DIAGNOSTICS === "true";
  const devOrStaging = process.env.NODE_ENV !== "production"
    || process.env.REACT_APP_ENABLE_E2E_OVERRIDES === "true"
    || window.location.hostname === "localhost"
    || window.location.hostname.endsWith(".localhost")
    || window.location.hostname.includes("staging");
  if (!explicitlyAuthorized && !devOrStaging) return false;
  const params = new URLSearchParams(window.location.search);
  const requested = params.get("nativeResizeDiagnostics");
  if (requested === "1" || requested === "true") {
    window.sessionStorage.setItem("landagility.nativeResizeDiagnostics", "1");
    return true;
  }
  if (requested === "0" || requested === "false") {
    window.sessionStorage.removeItem("landagility.nativeResizeDiagnostics");
    return false;
  }
  return window.sessionStorage.getItem("landagility.nativeResizeDiagnostics") === "1";
}

function createResizeOverlay(draftElement: HTMLElement, startBounds: { x: number; y: number; width: number; height: number }): HTMLDivElement | undefined {
  const band = draftElement.closest(`.${styles["native-band"]}`);
  if (!band) return undefined;
  const overlay = document.createElement("div");
  overlay.className = styles["native-resize-overlay"];
  overlay.dataset.testid = "native-resize-overlay";
  overlay.setAttribute("aria-hidden", "true");
  for (const handle of ["nw", "n", "ne", "e", "se", "s", "sw", "w"]) {
    const marker = document.createElement("span");
    marker.className = `${styles["native-resize-overlay-handle"]} ${styles[`native-resize-overlay-handle-${handle}`]}`;
    overlay.appendChild(marker);
  }
  applyResizeBoundsToElement(overlay, startBounds, startBounds);
  band.appendChild(overlay);
  return overlay;
}

function removeResizeOverlay(interaction: NativeInteraction): void {
  if (interaction.kind === "resize" && interaction.overlayElement?.isConnected) {
    interaction.overlayElement.remove();
  }
}

function createBandResizeOverlay(bandElement: HTMLElement | null, startHeight: number): HTMLDivElement | undefined {
  if (!bandElement) return undefined;
  const overlay = document.createElement("div");
  overlay.className = styles["native-band-resize-overlay"];
  overlay.dataset.testid = "native-band-resize-overlay";
  overlay.setAttribute("aria-hidden", "true");
  applyBandResizeHeightToElement(overlay, startHeight);
  bandElement.appendChild(overlay);
  return overlay;
}

function applyBandResizeHeightToElement(element: HTMLElement, height: number): void {
  element.style.height = `${Math.max(12, height)}px`;
}

function removeBandResizeOverlay(interaction: NativeInteraction): void {
  if (interaction.kind === "bandResize" && interaction.overlayElement?.isConnected) {
    interaction.overlayElement.remove();
  }
}

function completeResizeHandoffIfReady(handoffRef: React.MutableRefObject<ResizeHandoff | null>): void {
  const handoff = handoffRef.current;
  if (!handoff) return;
  if (!elementMatchesResizeBounds(handoff.draftElement, handoff.bounds)) return;
  handoff.overlayElement.remove();
  delete handoff.draftElement.dataset.resizeHandoff;
  handoffRef.current = null;
}

function elementMatchesResizeBounds(element: HTMLElement, bounds: Partial<{ x: number; y: number; width: number; height: number }>): boolean {
  return (
    styleNumberEquals(element.style.left, bounds.x) &&
    styleNumberEquals(element.style.top, bounds.y) &&
    styleNumberEquals(element.style.width, bounds.width) &&
    styleNumberEquals(element.style.height, bounds.height)
  );
}

function styleNumberEquals(value: string, expected: number | undefined): boolean {
  if (expected === undefined) return true;
  return Math.abs(parseFloat(value || "0") - expected) < 0.001;
}

function applyResizeBoundsToElement(element: HTMLElement, bounds: Partial<{ x: number; y: number; width: number; height: number }>, fallback: { x: number; y: number; width: number; height: number }): void {
  element.style.left = `${Number(bounds.x ?? fallback.x)}px`;
  element.style.top = `${Number(bounds.y ?? fallback.y)}px`;
  element.style.width = `${Number(bounds.width ?? fallback.width)}px`;
  element.style.height = `${Number(bounds.height ?? fallback.height)}px`;
}

function resizeDraftDelta(delta: number, snapEnabled: boolean): number {
  return snapEnabled ? snapDistance(delta, true) : delta;
}

function normalizePointerEventTimestamp(timeStamp: number): number | null {
  const now = performance.now();
  if (!Number.isFinite(timeStamp)) return null;
  if (timeStamp >= 0 && timeStamp <= now + 1000) return timeStamp;
  if (typeof performance.timeOrigin === "number") {
    const relative = timeStamp - performance.timeOrigin;
    if (relative >= 0 && relative <= now + 1000) return relative;
  }
  return null;
}

function createNativeResizeDiagnostics(): NativeResizeDiagnostics {
  return {
    active: false,
    kind: null,
    resizeRenderMode: "overlay",
    startedAt: 0,
    endedAt: 0,
    zoom: 0,
    snapEnabled: false,
    nativeDesignCanvasRenderCount: 0,
    renderCountDuringInteraction: 0,
    renderDuringActiveResize: false,
    layoutPlanInvocationCount: 0,
    pointerMoveCount: 0,
    animationFramesRequested: 0,
    animationFramesExecuted: 0,
    draftGeometryUpdateCount: 0,
    documentCommandCount: 0,
    pointerLatencyCount: 0,
    pointerLatencyTotalMs: 0,
    pointerLatencyMaxMs: 0,
    pointerEventLatencyCount: 0,
    pointerEventLatencyTotalMs: 0,
    pointerEventLatencyMaxMs: 0,
    animationFrameIntervalCount: 0,
    animationFrameIntervalTotalMs: 0,
    animationFrameIntervalMaxMs: 0,
    longFrameCount: 0,
    frameWorkTotalMs: 0,
    frameWorkMaxMs: 0,
    snappingCalculationCount: 0,
    snappingCalculationTotalMs: 0,
    snappingCalculationMaxMs: 0,
    styleConflictCount: 0,
    latestStyleConflict: "",
    lastFrameTime: 0,
    lastSummary: "",
  };
}

function startNativeResizeDiagnostics(metrics: NativeResizeDiagnostics, enabled: boolean, interaction: NativeInteraction, zoom: number, snapEnabled: boolean): void {
  if (!enabled) return;
  const next = createNativeResizeDiagnostics();
  Object.assign(metrics, next, {
    active: true,
    kind: interaction.kind,
    resizeRenderMode: "overlay",
    startedAt: performance.now(),
    zoom,
    snapEnabled,
  });
  metrics.lastSummary = nativeResizeDiagnosticsSummary(metrics);
}

function finishNativeResizeDiagnostics(metrics: NativeResizeDiagnostics, enabled: boolean, reason = "pointerup"): void {
  if (!enabled || !metrics.active) return;
  metrics.active = false;
  metrics.endedAt = performance.now();
  metrics.lastSummary = nativeResizeDiagnosticsSummary(metrics, reason);
}

function recordNativeDesignCanvasRender(metrics: NativeResizeDiagnostics, enabled: boolean, interaction: NativeInteraction | null): void {
  if (!enabled) return;
  metrics.nativeDesignCanvasRenderCount += 1;
  if (!metrics.active) return;
  metrics.renderCountDuringInteraction += 1;
  if (interaction?.kind === "resize") metrics.renderDuringActiveResize = true;
}

function recordLayoutPlanInvocation(metrics: NativeResizeDiagnostics, enabled: boolean): void {
  if (enabled && metrics.active) metrics.layoutPlanInvocationCount += 1;
}

function recordPointerMove(metrics: NativeResizeDiagnostics, enabled: boolean): void {
  if (enabled && metrics.active) metrics.pointerMoveCount += 1;
}

function recordAnimationFrameRequested(metrics: NativeResizeDiagnostics, enabled: boolean): void {
  if (enabled && metrics.active) metrics.animationFramesRequested += 1;
}

function recordAnimationFrameExecuted(metrics: NativeResizeDiagnostics, enabled: boolean, frameTime: number): void {
  if (!enabled || !metrics.active) return;
  metrics.animationFramesExecuted += 1;
  if (metrics.lastFrameTime) {
    const interval = Math.max(0, frameTime - metrics.lastFrameTime);
    metrics.animationFrameIntervalCount += 1;
    metrics.animationFrameIntervalTotalMs += interval;
    metrics.animationFrameIntervalMaxMs = Math.max(metrics.animationFrameIntervalMaxMs, interval);
    if (interval > 50) metrics.longFrameCount += 1;
  }
  metrics.lastFrameTime = frameTime;
}

function recordPointerToDraftApplication(metrics: NativeResizeDiagnostics, enabled: boolean, eventTime: number, appliedTime: number): void {
  if (!enabled || !metrics.active) return;
  const latency = Math.max(0, appliedTime - eventTime);
  metrics.pointerLatencyCount += 1;
  metrics.pointerLatencyTotalMs += latency;
  metrics.pointerLatencyMaxMs = Math.max(metrics.pointerLatencyMaxMs, latency);
}

function recordPointerEventToDraftApplication(metrics: NativeResizeDiagnostics, enabled: boolean, eventTime: number | null, appliedTime: number): void {
  if (!enabled || !metrics.active || eventTime === null) return;
  const latency = Math.max(0, appliedTime - eventTime);
  metrics.pointerEventLatencyCount += 1;
  metrics.pointerEventLatencyTotalMs += latency;
  metrics.pointerEventLatencyMaxMs = Math.max(metrics.pointerEventLatencyMaxMs, latency);
}

function recordDraftGeometryUpdate(metrics: NativeResizeDiagnostics, enabled: boolean, workMs: number): void {
  if (!enabled || !metrics.active) return;
  metrics.draftGeometryUpdateCount += 1;
  metrics.frameWorkTotalMs += Math.max(0, workMs);
  metrics.frameWorkMaxMs = Math.max(metrics.frameWorkMaxMs, workMs);
  if (workMs > 16.7) metrics.longFrameCount += 1;
}

function recordAnimationFrameWork(metrics: NativeResizeDiagnostics, enabled: boolean, workMs: number): void {
  if (!enabled || !metrics.active || metrics.kind === "resize") return;
  metrics.frameWorkTotalMs += Math.max(0, workMs);
  metrics.frameWorkMaxMs = Math.max(metrics.frameWorkMaxMs, workMs);
}

function recordSnappingCalculation(metrics: NativeResizeDiagnostics, enabled: boolean, workMs: number): void {
  if (!enabled || !metrics.active) return;
  metrics.snappingCalculationCount += 1;
  metrics.snappingCalculationTotalMs += Math.max(0, workMs);
  metrics.snappingCalculationMaxMs = Math.max(metrics.snappingCalculationMaxMs, workMs);
}

function recordDocumentCommand(metrics: NativeResizeDiagnostics, enabled: boolean): void {
  if (enabled && metrics.active) metrics.documentCommandCount += 1;
}

function recordResizeStyleConflict(metrics: NativeResizeDiagnostics, enabled: boolean, interaction: NativeInteraction | null): void {
  if (!enabled || !metrics.active || interaction?.kind !== "resize") return;
  const bounds = nextResizeBounds(interaction.startBounds, interaction.handle, resizeDraftDelta(interaction.dx, metrics.snapEnabled), resizeDraftDelta(interaction.dy, metrics.snapEnabled));
  const target = interaction.overlayElement;
  const expected = {
    left: `${Number(bounds.x ?? interaction.startBounds.x)}px`,
    top: `${Number(bounds.y ?? interaction.startBounds.y)}px`,
    width: `${Number(bounds.width ?? interaction.startBounds.width)}px`,
    height: `${Number(bounds.height ?? interaction.startBounds.height)}px`,
  };
  const actual = {
    left: target.style.left,
    top: target.style.top,
    width: target.style.width,
    height: target.style.height,
  };
  if (expected.left === actual.left && expected.top === actual.top && expected.width === actual.width && expected.height === actual.height) return;
  metrics.styleConflictCount += 1;
  metrics.latestStyleConflict = `expected ${JSON.stringify(expected)} but found ${JSON.stringify(actual)}`;
}

function nativeResizeDiagnosticsSummary(metrics: NativeResizeDiagnostics, reason = metrics.active ? "active" : "idle"): string {
  const duration = metrics.endedAt && metrics.startedAt ? metrics.endedAt - metrics.startedAt : metrics.startedAt ? performance.now() - metrics.startedAt : 0;
  const summary = {
    reason,
    active: metrics.active,
    kind: metrics.kind,
    resizeRenderMode: metrics.resizeRenderMode,
    durationMs: roundMetric(duration),
    zoom: metrics.zoom,
    snapEnabled: metrics.snapEnabled,
    nativeDesignCanvasRenderCount: metrics.nativeDesignCanvasRenderCount,
    renderCountDuringInteraction: metrics.renderCountDuringInteraction,
    renderDuringActiveResize: metrics.renderDuringActiveResize,
    resolveNativeLayoutPlanInvocationCount: metrics.layoutPlanInvocationCount,
    pointerMoveCount: metrics.pointerMoveCount,
    animationFramesRequested: metrics.animationFramesRequested,
    animationFramesExecuted: metrics.animationFramesExecuted,
    draftGeometryUpdateCount: metrics.draftGeometryUpdateCount,
    documentCommandCount: metrics.documentCommandCount,
    avgPointerToDraftLatencyMs: roundMetric(metrics.pointerLatencyTotalMs / Math.max(1, metrics.pointerLatencyCount)),
    maxPointerToDraftLatencyMs: roundMetric(metrics.pointerLatencyMaxMs),
    avgPointerEventToDraftLatencyMs: roundMetric(metrics.pointerEventLatencyTotalMs / Math.max(1, metrics.pointerEventLatencyCount)),
    maxPointerEventToDraftLatencyMs: roundMetric(metrics.pointerEventLatencyMaxMs),
    avgAnimationFrameIntervalMs: roundMetric(metrics.animationFrameIntervalTotalMs / Math.max(1, metrics.animationFrameIntervalCount)),
    maxAnimationFrameIntervalMs: roundMetric(metrics.animationFrameIntervalMaxMs),
    actualFrameCallbackRate: roundMetric(metrics.animationFramesExecuted / Math.max(0.001, duration / 1000)),
    longFrameCount: metrics.longFrameCount,
    avgDraftUpdateWorkMs: roundMetric(metrics.frameWorkTotalMs / Math.max(1, metrics.draftGeometryUpdateCount || metrics.animationFramesExecuted)),
    maxDraftUpdateWorkMs: roundMetric(metrics.frameWorkMaxMs),
    snappingCalculationCount: metrics.snappingCalculationCount,
    avgSnappingCalculationMs: roundMetric(metrics.snappingCalculationTotalMs / Math.max(1, metrics.snappingCalculationCount)),
    maxSnappingCalculationMs: roundMetric(metrics.snappingCalculationMaxMs),
    styleConflictCount: metrics.styleConflictCount,
    latestStyleConflict: metrics.latestStyleConflict || null,
  };
  return JSON.stringify(summary, null, 2);
}

function publishNativeResizeDiagnostics(metrics: NativeResizeDiagnostics, target: HTMLPreElement | null, enabled: boolean): void {
  if (!enabled || !target) return;
  const summary = metrics.active ? nativeResizeDiagnosticsSummary(metrics) : metrics.lastSummary || nativeResizeDiagnosticsSummary(metrics);
  target.textContent = summary;
}

function copyNativeResizeDiagnostics(metrics: NativeResizeDiagnostics, target: HTMLPreElement | null): void {
  const summary = target?.textContent || metrics.lastSummary || nativeResizeDiagnosticsSummary(metrics);
  void navigator.clipboard?.writeText(summary);
}

function roundMetric(value: number): number {
  return Number.isFinite(value) ? Number(value.toFixed(3)) : 0;
}

function requestInteractionFrame(callback: FrameRequestCallback): number {
  return typeof window.requestAnimationFrame === "function"
    ? window.requestAnimationFrame(callback)
    : window.setTimeout(() => callback(performance.now()), 0);
}

function cancelInteractionFrame(handle: number): void {
  if (typeof window.cancelAnimationFrame === "function") {
    window.cancelAnimationFrame(handle);
    return;
  }
  window.clearTimeout(handle);
}

function snapDistance(value: number, enabled: boolean): number {
  if (!enabled) return value;
  return Math.round(value / 6) * 6;
}

function snapPoint(point: { x: number; y: number }, enabled: boolean): { x: number; y: number } {
  return enabled ? { x: snapDistance(point.x, true), y: snapDistance(point.y, true) } : point;
}

function snapNativeInteraction(
  interaction: NativeInteraction,
  plan: ReturnType<typeof resolveNativeLayoutPlan>,
  snapEnabled: boolean,
): { dx: number; dy: number; guides: AlignmentGuide[] } {
  if (!snapEnabled) return { dx: interaction.dx, dy: interaction.dy, guides: [] };
  const gridDx = snapDistance(interaction.dx, true);
  const gridDy = snapDistance(interaction.dy, true);
  if (interaction.kind !== "drag") return { dx: gridDx, dy: gridDy, guides: [] };

  const band = findResolvedBand(plan, interaction.bandId);
  if (!band) return { dx: gridDx, dy: gridDy, guides: [] };
  const bandElements = flattenResolvedNativeElements(band.elements);
  const selected = bandElements.filter((element) => interaction.elementIds.includes(element.id));
  if (!selected.length) return { dx: gridDx, dy: gridDy, guides: [] };

  const moving = boundsForElements(selected);
  const stationary = bandElements.filter((element) => !interaction.elementIds.includes(element.id));
  const verticalTargets = [
    { value: 0, start: 0, end: band.bounds.height },
    { value: band.bounds.width / 2, start: 0, end: band.bounds.height },
    { value: band.bounds.width, start: 0, end: band.bounds.height },
    ...stationary.flatMap((element) => edgeTargets(element, "x", "width", band.bounds.height)),
  ];
  const horizontalTargets = [
    { value: 0, start: 0, end: band.bounds.width },
    { value: band.bounds.height / 2, start: 0, end: band.bounds.width },
    { value: band.bounds.height, start: 0, end: band.bounds.width },
    ...stationary.flatMap((element) => edgeTargets(element, "y", "height", band.bounds.width)),
  ];
  const movingX = [moving.x + gridDx, moving.x + moving.width / 2 + gridDx, moving.x + moving.width + gridDx];
  const movingY = [moving.y + gridDy, moving.y + moving.height / 2 + gridDy, moving.y + moving.height + gridDy];
  const snapX = nearestGuideDelta(movingX, verticalTargets, gridDx, "vertical");
  const snapY = nearestGuideDelta(movingY, horizontalTargets, gridDy, "horizontal");

  return {
    dx: snapX.delta,
    dy: snapY.delta,
    guides: [...snapX.guides, ...snapY.guides],
  };
}

function flattenResolvedNativeElements(elements: NativeResolvedElement[]): NativeResolvedElement[] {
  return elements.flatMap((element) => [element, ...flattenResolvedNativeElements(element.elements || [])]);
}

function findResolvedBand(plan: ReturnType<typeof resolveNativeLayoutPlan>, bandId: string): NativeResolvedBand | undefined {
  for (const page of plan.pages) {
    const band = page.bands.find((item) => item.id === bandId);
    if (band) return band;
  }
  return undefined;
}

function boundsForElements(elements: NativeResolvedElement[]): { x: number; y: number; width: number; height: number } {
  const left = Math.min(...elements.map((element) => Number(element.x || 0)));
  const top = Math.min(...elements.map((element) => Number(element.y || 0)));
  const right = Math.max(...elements.map((element) => Number(element.x || 0) + Number(element.width || 0)));
  const bottom = Math.max(...elements.map((element) => Number(element.y || 0) + Number(element.height || 0)));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function edgeTargets(element: NativeResolvedElement, positionKey: "x" | "y", sizeKey: "width" | "height", extent: number): Array<{ value: number; start: number; end: number }> {
  const position = Number(element[positionKey] || 0);
  const size = Number(element[sizeKey] || 0);
  return [
    { value: position, start: 0, end: extent },
    { value: position + size / 2, start: 0, end: extent },
    { value: position + size, start: 0, end: extent },
  ];
}

function nearestGuideDelta(
  movingEdges: number[],
  targets: Array<{ value: number; start: number; end: number }>,
  fallbackDelta: number,
  orientation: AlignmentGuide["orientation"],
): { delta: number; guides: AlignmentGuide[] } {
  const tolerance = 6;
  let best: { distance: number; delta: number; target: { value: number; start: number; end: number } } | null = null;
  for (const edge of movingEdges) {
    for (const target of targets) {
      const adjustment = target.value - edge;
      const distance = Math.abs(adjustment);
      if (distance <= tolerance && (!best || distance < best.distance)) {
        best = { distance, delta: fallbackDelta + adjustment, target };
      }
    }
  }
  return best
    ? { delta: best.delta, guides: [{ orientation, position: best.target.value, start: best.target.start, end: best.target.end }] }
    : { delta: fallbackDelta, guides: [] };
}

function eventPointInBand(event: { clientX: number; clientY: number; target: EventTarget | null }, zoom: number): { x: number; y: number } {
  const element = event.target instanceof Element ? event.target.closest(`.${styles["native-band"]}`) : null;
  if (!element) return { x: 24, y: 12 };
  const bounds = element.getBoundingClientRect();
  const scale = zoom / 100 || 1;
  return {
    x: Math.max(0, (event.clientX - bounds.left) / scale),
    y: Math.max(0, (event.clientY - bounds.top) / scale),
  };
}

function eventPointInElement(event: { clientX: number; clientY: number; currentTarget?: EventTarget | null }, zoom: number): { x: number; y: number } {
  const element = event.currentTarget instanceof Element ? event.currentTarget : null;
  if (!element) return { x: 8, y: 8 };
  const bounds = element.getBoundingClientRect();
  const scale = zoom / 100 || 1;
  return {
    x: Math.max(0, (event.clientX - bounds.left) / scale),
    y: Math.max(0, (event.clientY - bounds.top) / scale),
  };
}

function nextResizeBounds(bounds: { x: number; y: number; width: number; height: number }, handle: string, dx: number, dy: number): Partial<{ x: number; y: number; width: number; height: number }> {
  const next = { ...bounds };
  if (handle.includes("e")) next.width = bounds.width + dx;
  if (handle.includes("s")) next.height = bounds.height + dy;
  if (handle.includes("w")) {
    next.x = bounds.x + dx;
    next.width = bounds.width - dx;
  }
  if (handle.includes("n")) {
    next.y = bounds.y + dy;
    next.height = bounds.height - dy;
  }
  next.width = Math.max(6, next.width);
  next.height = Math.max(6, next.height);
  next.x = Math.max(0, next.x);
  next.y = Math.max(0, next.y);
  return next;
}
